import { sortVehiclesByStatus } from "@/lib/vehicle-status-sort";
import { AssistanceNewRequestForm } from "@/components/assistance-new-request-dialog";
import { AssistanceDispatchPanel } from "@/components/assistance-dispatch-panel";
import { passwordPolicyError } from "@/lib/password-policy";
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import {
  Plus,
  Minus,
  Zap,
  X,
  MapPin,
  Layers,
  ChevronRight,
  Gauge,
  Lock,
  Unlock,
  Anchor,
  Route as RouteIcon,
  Bell,
  Battery,
  BatteryLow,
  Radio,
  CheckCircle2,
  AlertTriangle,
  QrCode,
  Copy,
  Eye,
  EyeOff,
  Sun,
  Moon,
  Activity,
  CirclePause,
  Signal,
  CircleDot,
  SignalZero,
  LandPlot,
  Combine,
  Tag,
  Loader2,
  Cpu,
  Pencil,
  FileText,
  Wrench,
  Receipt,
  Megaphone,
  Search,
  ChevronLeft,
  Send,
  Map as MapIcon,
  Camera,
  Repeat,
  Compass,
  CloudRain,
  CloudLightning,
  Share2,
  History as HistoryIcon,
  Clock as ClockIcon,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useSidebarNavItems } from "@/components/app-sidebar";
import { rememberAppTheme } from "@/lib/agenda-theme";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logActivity } from "@/services/gpsdrive-activity-log.service";
import { useUserLocation } from "@/hooks/use-user-location";
import {
  useWeather,
  useWeatherCoords,
  describeWeatherCode,
  weatherBucket,
  isRainCode,
  type WeatherBucket,
} from "@/hooks/use-weather";
import { WazeAlertsLayer, WazeIcon } from "@/components/waze-alerts-layer";
import { LocationShareSheet } from "@/components/location-share-sheet";
import { useWazeAlertsToggle } from "@/lib/waze-alerts";
import { isValidCoordinate } from "@/lib/position-validation";
import { useDeviceHeading } from "@/hooks/use-device-heading";
import { useMovingVehicles } from "@/hooks/use-moving-vehicles";
// Ícone do "personagem" que representa a localização do usuário no PWA —
// pré-renderizado a partir do .glb enviado (models-3d/user-avatar/model.glb,
// Sketchfab "Av. 1", matheuszorn, CC-BY-4.0) pelo MESMO pipeline three.js
// que gera os ícones de veículo (scripts/render-vehicle-icons/). Ver
// MobileUserLocationLayer mais abaixo pra por quê não é um modelo 3D ao vivo.
import userLocationIconUrl from "@/assets/vehicle-icons/generated/user-avatar-flat/f97316.png";
import { haversineDistanceMeters, formatDistance } from "@/lib/geo-distance";
import { format, formatDistanceToNow, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { VehicleInspectionScreen } from "@/components/mobile-vehicle-inspection";
import { useOfflineInspectionSync } from "@/lib/offline-inspections";
import { ClientMaintenanceScreen } from "@/components/mobile-client-maintenance";
import { DriverHomePanel } from "@/components/mobile-driver-home";
import { ReferralsCenterScreen } from "@/components/mobile-referrals";
import { MobileAiAssistantPanel, useAiAssistantAccess } from "@/components/ai-assistant/mobile-ai-assistant";
import { useKeyboardInset } from "@/hooks/use-keyboard-inset";
import logoAsset from "@/assets/gps-drive-logo-v3.png.asset.json";
import {
  useVehicles,
  useInvoices,
  useMyBillingStatus,
  usePixQrCode,
  useNotificationTypes,
  useDeviceNotifications,
  useToggleDeviceNotification,
  useQuickToggleRules,
  useQuickToggleRule,
  useUserAvatar,
  useUser,
  useUpdateUser,
  useRecentEvents,
  useVehicleAnchor,
  useGeofenceAlertRules,
  ANCHOR_NAME_PREFIX,
  useGeofences,
  useVehicleMetadata,
  useVehicleMetadataSummaries,
  useVehicleImages,
  useVehicleUsers,
  usePlans,
  useLinkableUsers,
  useDevicePermissions,
  useServiceTypes,
  useServiceGroups,
  useAssistanceClientSummary,
  useServiceUsage,
  useCreateAssistanceRequest,
  useMyAssistanceRequests,
  useAdminBranding,
  useVehicleSummary,
  useVehicleStops,
  useVehicleTrips,
  useBlockCommandSmsFallback,
  queryKeys,
} from "@/hooks/queries";
import { sendBlockCommandWithSmsFallback } from "@/lib/send-block-command-with-sms-fallback";
import { usePendingBlockCommand } from "@/lib/pending-block-commands";
import { vehicleMetadataService } from "@/services/vehicle-metadata.service";
import { compressImageFile } from "@/lib/compress-image";
import { usePermission } from "@/hooks/use-permission";
import { useAppMenuAccess } from "@/hooks/use-app-menu-access";
import { parseWkt } from "@/lib/wkt";
import { DECK_ON_TOP, resolveGoogleMapId } from "@/lib/google-maps-style";
import { useAuth } from "@/contexts/auth-context";
import { authService } from "@/services/auth.service";
import { resolveGpsDriveUrl, GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  resolveAutoColor,
  resolveVehicleColor,
  resolveVehicleModel,
  vehicleIconDataUri,
} from "@/lib/vehicle-icons";
import {
  ignitionState,
  ignitionLabel,
  isVehicleBlocked,
  isVehiclePowered,
} from "@/lib/vehicle-state";
import { ALERT_TYPE_META, ALERT_TONE_CLASSES, ALERT_CATALOG } from "@/lib/alert-types";
import { AlertLimitsEditor, isLimitEditable, limitSummary } from "@/components/alert-limits-editor";
import type { AlertRuleConfig } from "@/services/alert-rules.service";
import { useReverseGeocode } from "@/hooks/use-reverse-geocode";
import {
  useVehicleClusters,
  clusterBubbleRadiusPx,
  type MapBoundsBox,
} from "@/hooks/use-vehicle-clusters";
import { RouteHistoryCenter } from "@/components/route-history-center";
import { VehicleEditDialog, describeImageUploadError } from "@/routes/index";
import { StreetViewPanorama } from "@/components/street-view-panorama";
import { VehicleAnchorDialog } from "@/components/vehicle-anchor-dialog";
import { VehicleSendCommandDialog } from "@/components/vehicle-send-command-dialog";
import { VehicleHistoryDeleteDialog } from "@/components/vehicle-history-delete-dialog";
import { SubUsersManagerDialog } from "@/components/sub-users-manager-dialog";
import { GpsInlineLoader } from "@/components/gps-loader";
import { AvatarEditor } from "@/components/user-avatar-editor";
import { VehicleBlockConfirmDialog } from "@/components/vehicle-block-confirm-dialog";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import type { Vehicle, VehicleStatus, VehicleType, Geofence, Trip } from "@/types";
import { photoIconProps, usePhotoMarkers } from "@/hooks/use-photo-markers";
import type { VehicleImageUrls } from "@/types/vehicle-metadata";
import { SatelliteZoomGuard } from "@/components/satellite-zoom-guard";
import { MapTileRecovery } from "@/components/map-tile-recovery";
import type { BillingStatus, Invoice } from "@/services/gpsdrive-billing.service";
import type { LinkableUser } from "@/services/gpsdrive-users.service";
import type {
  AssistanceRequest,
  ServiceType,
  ServiceUsage,
} from "@/services/gpsdrive-assistance.service";
import { isRestrictedAccountType } from "@/lib/account-type";
import { timeWithAgo, useNowTick } from "@/lib/time-ago";
import { LicensePlate } from "@/components/license-plate";

// Preferências pessoais do PWA (mapa padrão / tema claro-escuro) — só deste
// navegador/aparelho, por isso localStorage e não um campo de backend.
// Mesma convenção de nomes já usada por outras chaves do projeto (ver
// BRANDING_COLOR_CACHE_KEY em use-branding-theme.ts: "gpsdrive:<recurso>").
const MOBILE_THEME_KEY = "gpsdrive:mobile-theme-mode";
const MOBILE_MAP_LAYER_KEY = "gpsdrive:mobile-map-layer";
// Mesma camada do botão de "olho" do mapa principal (routes/index.tsx —
// Geocercas), chave própria pro PWA (pedido 2026-08-30: "no mapa do app pwa
// tem que ter o ícone do olho também"). Toggle de cluster removido na
// migração pra deck.gl 2026-09-03 (não precisava mais agrupar pra manter
// DOM baixo) e trazido de volta em 2026-09-03 — não pra performance dessa
// vez, e sim porque zoom bem aberto sem agrupar virava mancha preta de
// ícones sobrepostos (ver use-vehicle-clusters.ts). Chave NOVA
// (MOBILE_SHOW_CLUSTERS_KEY) — nunca reaproveitar a antiga descontinuada
// (clientes velhos podem ter "1"/"0" salvo nela com semântica diferente).
const MOBILE_SHOW_GEOFENCES_KEY = "gpsdrive:mobile-map-show-geofences";
const MOBILE_SHOW_CLUSTERS_KEY = "gpsdrive:mobile-map-cluster-vehicles";
const MOBILE_SHOW_TAGS_KEY = "gpsdrive:mobile-map-show-tags";
// Acima disso, desligar o agrupamento vira erro em vez de opção — GPU de
// celular não desenha esse tanto de ícone de 1024x1024 individual sem
// travar/virar mancha preta (achado real 2026-09-03, print com >5000
// veículos). Não existe um "número certo" sem testar em aparelho de
// verdade; 200 é uma margem generosa (bem abaixo de onde o problema
// aparece) escolhida até termos dado real de perfil em device físico.
const CLUSTER_MANDATORY_THRESHOLD = 200;
// Carrossel de avatares da Início: quantos veículos entram por lote (ver InicioPanel).
const CAROUSEL_BATCH = 30;
// Ordem do carrossel do Início: online (em movimento) → parado → offline.
const CAROUSEL_STATUS_RANK: Record<VehicleStatus, number> = { online: 0, idle: 1, offline: 2 };


function readStoredTheme(): boolean {
  try {
    return localStorage.getItem(MOBILE_THEME_KEY) === "dark";
  } catch {
    return false;
  }
}

function readStoredLayer(): LayerKey {
  try {
    const stored = localStorage.getItem(MOBILE_MAP_LAYER_KEY);
    return stored === "satellite" || stored === "hybrid" || stored === "streets"
      ? stored
      : "streets";
  } catch {
    return "streets";
  }
}

function readStoredShowGeofences(): boolean {
  try {
    return localStorage.getItem(MOBILE_SHOW_GEOFENCES_KEY) === "1";
  } catch {
    return false;
  }
}

// Default "ligado" (agrupar) — sem isso a frota inteira em zoom aberto
// vira mancha preta (ver comentário em MOBILE_SHOW_CLUSTERS_KEY). Só
// desligado se o usuário mexer explicitamente.
function readStoredShowClusters(): boolean {
  try {
    const raw = localStorage.getItem(MOBILE_SHOW_CLUSTERS_KEY);
    return raw === null ? true : raw === "1";
  } catch {
    return true;
  }
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

// "Bom dia"/"Boa tarde"/"Boa noite" real (hora local do aparelho) + emoji —
// usado no topo da aba Perfil (pedido: "Bom dia, Igor Feijó 👋").
export function greetingFor(name: string | undefined): { text: string; emoji: string } {
  const hour = new Date().getHours();
  const period = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";
  const emoji = hour < 12 ? "👋" : hour < 18 ? "🌤️" : "🌙";
  return { text: name ? `${period}, ${name}` : period, emoji };
}

// ============================================================================
// PWA mobile app
// ============================================================================

type TabKey = "inicio" | "mapa" | "veiculos" | "eventos" | "assistente" | "voce";
type LayerKey = "streets" | "satellite" | "hybrid";

const LAYERS: Record<LayerKey, { label: string; emoji: string; url: string; sub: string }> = {
  streets: {
    label: "Mapa",
    emoji: "🗺️",
    url: "https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=pt-BR&scale=2",
    sub: "0123",
  },
  satellite: {
    label: "Satélite HD",
    emoji: "🛰️",
    url: "https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}&scale=2",
    sub: "0123",
  },
  hybrid: {
    label: "Híbrido HD",
    emoji: "🌐",
    url: "https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&hl=pt-BR&scale=2",
    sub: "0123",
  },
};

function detectType(v: Vehicle): VehicleType {
  const name = `${v.name} ${v.plate}`.toLowerCase();
  if (/moto|scooter/.test(name)) return "moto";
  if (/caminh|truck|carreta|onibus|ônibus/.test(name)) return "truck";
  if (/van|kombi/.test(name)) return "van";
  if (/escav|máquina|maquina/.test(name)) return "machine";
  return "car";
}
// Cor centralizada em src/lib/vehicle-icons/colors.ts — mesma lógica do mapa
// principal e do mapa expandido (fixa se configurada, senão por status). Usar
// SÓ pra colorir o ÍCONE do veículo (imagem do carro) — nunca pra texto/dot
// de status, senão um veículo com cor fixa (ex.: verde) faz o rótulo
// "Offline" aparecer verde também (bug relatado 2026-08-31, veículo J17).
const tintFor = (v: Vehicle): string => resolveVehicleColor(v);
// Cor de status "de verdade" — ignora a cor fixa do ícone de propósito.
// Usar em todo dot/texto que informa o status (online/parado/offline), pra
// nunca ficar errado só porque o dono escolheu uma cor fixa pro carro.
export const statusColorFor = (v: Vehicle): string => resolveAutoColor(v);
// Ícone (modelo + cor) do veículo, igual ao mapa principal — com fallback de
// categoria por nome quando o dispositivo não tem "type" definido.
export const iconUrlFor = (v: Vehicle): string =>
  vehicleIconDataUri(resolveVehicleModel(v.iconKey, v.type ?? detectType(v)), tintFor(v));
export function statusText(v: Vehicle) {
  return v.moving
    ? "Em movimento"
    : v.status === "online"
      ? "Ligado"
      : v.status === "idle"
        ? "Parado"
        : "Offline";
}

// Centro usado só até a primeira posição real chegar (map.setView() recentra
// para a frota assim que useVehicles() responde, ver efeito de sync abaixo).
const DEFAULT_CENTER: [number, number] = [-23.5505, -46.6333];

// Camada de veículos — deck.gl IconLayer sobre GoogleMapsOverlay, mesmo
// padrão do mapa principal desktop (ver VehicleLayer em routes/index.tsx).
// Sem popup aqui (diferente do desktop): o clique só seleciona o veículo —
// o "pill" no topo da tela e a bottom sheet já mostram os detalhes, não
// precisa de InfoWindow.
//
// Agrupamento (clustering) reintroduzido 2026-09-03: sem isso, com zoom bem
// aberto (frota inteira, ou um polo só com centenas de veículos a poucos km
// um do outro) os ícones de 44px caíam todos no mesmo pixel e viravam uma
// mancha preta sólida (bug real relatado por print) — ver
// use-vehicle-clusters.ts. Bolha = ScatterplotLayer (círculo) + TextLayer
// (contagem); clicar expande o zoom até separar os pontos.

// memo — evita reconstruir os layers do deck.gl (IconLayer/ScatterplotLayer/
// TextLayer, custoso pra frota grande) toda vez que o MobileApp re-renderiza
// por causa de um clique em algo sem relação nenhuma com o mapa (ex.: abrir
// um dialog no Perfil) — pedido 2026-09-08 "app lento, demora pra abrir
// qualquer coisa". Só refaz quando vehicles/bounds/zoom de fato mudam.
const MobileVehicleLayer = memo(function MobileVehicleLayer({
  Mod,
  vehicles,
  bounds,
  zoom,
  focusId,
  onSelect,
  visible,
  interleaved,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  vehicles: Vehicle[];
  bounds: MapBoundsBox | null;
  zoom: number;
  focusId?: string | null;
  onSelect: (id: string) => void;
  // Componente agora fica SEMPRE montado (ver comentário na chamada, mais
  // abaixo) — `visible` (== aba Mapa ativa) só congela a lista `vehicles`
  // usada pro clustering enquanto escondido, pra não pagar o rebuild do
  // índice Supercluster (caro pra frotas grandes) a cada poll de 15s em
  // segundo plano. Assim que volta a ficar visível, resincroniza com o
  // dado mais recente numa passada só.
  visible: boolean;
  // Ver comentário completo no efeito de criação do overlay logo abaixo —
  // `true` (padrão vetorial, layer "streets") evita a distorção de zoom
  // fracionário no ícone/cluster; `false` só é necessário no raster
  // (Satélite/Híbrido).
  interleaved: boolean;
}) {
  const map = Mod.useMap();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlayRef = useRef<any>(null);
  // Congela `vehicles` enquanto a aba Mapa não está visível (ver `visible`
  // acima) — só resincroniza (efeito abaixo) quando `visible` vira true ou
  // quando já visível e a frota muda. Isso é o que permite manter este
  // componente sempre montado (overlay/contexto WebGL do deck.gl criado
  // uma única vez, ver efeito `[map, Mod]` abaixo) sem reintroduzir o custo
  // de recomputo em segundo plano que motivou desmontar/remontar por aba
  // (ver comentário no ponto de montagem, 2026-09-08, frota de 5000
  // dispositivos da conta de teste).
  const [liveVehicles, setLiveVehicles] = useState(vehicles);
  useEffect(() => {
    if (visible) setLiveVehicles(vehicles);
  }, [visible, vehicles]);
  // focusId (veículo selecionado/em foco) nunca some por bounds/cluster
  // ainda desatualizados enquanto a câmera termina de animar — ver
  // comentário completo em use-vehicle-clusters.ts. Bug relatado
  // 2026-09-09: "Acessar rastreamento" no Início levava pro mapa mas o
  // veículo só aparecia depois de alguns segundos.
  const { items, expansionZoomFor } = useVehicleClusters(liveVehicles, bounds, zoom, focusId, true);
  const rawIndividualVehicles = useMemo(
    () => items.filter((i) => i.type === "vehicle").map((i) => i.vehicle),
    [items],
  );
  // Deslize entre dois fixes reais (mesmo hook do mapa desktop) — sem isso o
  // ícone saltava a cada posição nova. Aplicado só nos ícones individuais já
  // visíveis (o clustering continua usando a posição bruta), então o custo
  // do tick de animação fica limitado ao que está desenhado na tela.
  const individualVehicles = useMovingVehicles(rawIndividualVehicles);
  const clusters = useMemo(() => items.filter((i) => i.type === "cluster"), [items]);
  const { circleFor, headingLayer, photoSize } = usePhotoMarkers(individualVehicles, true, iconUrlFor, zoom, map);

  useEffect(() => {
    if (!map) return;
    // `interleaved` agora segue o layer ativo (pedido 2026-09-10, prints
    // reais mostrando o ícone do veículo virando um retângulo preto sólido
    // com a frota de 5000 dispositivos da conta de teste, durante zoom
    // manual normal — não só a animação inicial). Motivo original de usar
    // sempre `false` (comentário histórico abaixo): evitar que Satélite/
    // Híbrido bloqueiem arrastar/pinça-zoom. Mas `interleaved: false` tem
    // uma limitação documentada pelo próprio deck.gl: não suporta zoom
    // fracionário — QUALQUER zoom fracionário (inclusive pinch/scroll manual
    // do usuário, não só fitBounds/panTo/moveCamera programáticos) deixa a
    // camada do deck.gl dessincronizada da projeção real do mapa; no
    // IconLayer (textura, não só geometria de círculo) isso aparece como o
    // ícone do carro virando um retângulo preto sólido em vez da imagem.
    // `interleaved: true` é o modo recomendado pra basemap vetorial (não
    // sofre esse problema) — só troca pra `false` quando o layer é raster de
    // verdade (Satélite/Híbrido), que é onde o bug de arrastar/zoom de
    // 2026-09-03 realmente vivia.
    //
    // Comentário histórico (bug 2026-09-03): sem `pointer-events: none` no
    // <div> que o canvas do deck.gl ocupa, Satélite/Híbrido (que sempre
    // renderizam raster mesmo com Map ID vetorial) deixavam um <div>
    // transparente cobrindo o mapa inteiro que intercepta todo toque —
    // clique pra selecionar veículo ainda funcionava (vem do listener nativo
    // do Maps, não do DOM), mas arrastar/pinça pra dar zoom travava.
    // Mapa vetorial (interleaved): ícones sempre por cima, sem disputar
    // profundidade com ruas/nomes — sem isso os nomes "piscavam" (print
    // 2026-10-21 com o Map ID próprio). Ver DECK_ON_TOP.
    const overlay = new Mod.GoogleMapsOverlay({ interleaved, parameters: DECK_ON_TOP });
    overlay.setMap(map);
    overlayRef.current = overlay;
    return () => {
      overlay.finalize?.();
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, Mod, interleaved]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.setProps({
      layers: [
        ...headingLayer(Mod, "mobile-vehicle-heading", individualVehicles),
        new Mod.IconLayer({
          id: "mobile-vehicles",
          data: individualVehicles,
          pickable: true,
          sizeUnits: "pixels",
          getPosition: (v: Vehicle) => [v.lng, v.lat],
          // Moto é estreita: mapScale (models.ts) aumenta só o marcador dela.
          getSize: (v: Vehicle) => (circleFor(v) ? photoSize : 44 * (resolveVehicleModel(v.iconKey, v.type).mapScale ?? 1)),
          getAngle: (v: Vehicle) => (circleFor(v) ? 0 : 360 - (v.bearing ?? 0)),
          // width/height reduzidos de 1024 pra 128 — ver comentário completo
          // no mesmo ponto em routes/index.tsx#VehicleLayer (causa real do
          // "mapa cheio de retângulos pretos" no Android com a conta de
          // 5000 veículos: atlas de textura do deck.gl estourando o limite
          // da GPU do aparelho ao reservar 1024×1024 por ícone).
          getIcon: (v: Vehicle) =>
            photoIconProps(circleFor(v)) ?? { url: iconUrlFor(v), width: 128, height: 128, anchorY: 64 },
          updateTriggers: { getIcon: [circleFor], getSize: [circleFor, photoSize], getAngle: [circleFor] },
          onClick: (info: { object?: Vehicle }) => {
            if (info.object) onSelect(info.object.id);
          },
        }),
        new Mod.ScatterplotLayer({
          id: "mobile-vehicle-clusters",
          data: clusters,
          pickable: true,
          stroked: true,
          filled: true,
          radiusUnits: "pixels",
          getPosition: (c: { lng: number; lat: number }) => [c.lng, c.lat],
          getRadius: (c: { count: number }) => clusterBubbleRadiusPx(c.count),
          getFillColor: [14, 165, 233, 220], // sky-500, mesma cor da marca no resto do PWA
          getLineColor: [255, 255, 255, 255],
          lineWidthUnits: "pixels",
          getLineWidth: 2,
          onClick: (info: { object?: { clusterId: number; lat: number; lng: number } }) => {
            if (!info.object || !map) return;
            const nextZoom = expansionZoomFor(info.object.clusterId);
            map.panTo({ lat: info.object.lat, lng: info.object.lng });
            map.setZoom(nextZoom);
          },
        }),
        new Mod.TextLayer({
          id: "mobile-vehicle-cluster-labels",
          data: clusters,
          getPosition: (c: { lng: number; lat: number }) => [c.lng, c.lat],
          getText: (c: { count: number }) => String(c.count),
          getSize: 11,
          getColor: [255, 255, 255, 255],
          fontFamily: "system-ui, sans-serif",
          fontWeight: 700,
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          pickable: false,
        }),
      ],
    });
  }, [individualVehicles, clusters, Mod, onSelect, map, expansionZoomFor, circleFor, photoSize]);

  return null;
});

// Rastro ao vivo do PWA — um google.maps.Polyline por veículo com rastro ativo
// (poucos elementos, nunca a frota inteira; não precisa de deck.gl).
function MobileTrailsLayer({
  Mod,
  trails,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  trails: Record<string, { lat: number; lng: number }[]>;
}) {
  const map = Mod.useMap();
  const linesRef = useRef<Map<string, google.maps.Polyline>>(new Map());
  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    const current = linesRef.current;
    const seen = new Set<string>();
    for (const [id, pts] of Object.entries(trails)) {
      if (pts.length < 2) continue;
      seen.add(id);
      const path = pts.map((p) => ({ lat: p.lat, lng: p.lng }));
      const line = current.get(id);
      if (line) line.setPath(path);
      else
        current.set(
          id,
          new google.maps.Polyline({
            map,
            path,
            strokeColor: "#2563eb",
            strokeWeight: 5,
            strokeOpacity: 0.85,
          }),
        );
    }
    for (const [id, line] of current) {
      if (!seen.has(id)) {
        line.setMap(null);
        current.delete(id);
      }
    }
  }, [map, trails]);
  useEffect(
    () => () => {
      for (const line of linesRef.current.values()) line.setMap(null);
      linesRef.current.clear();
    },
    [],
  );
  return null;
}

// Desliga a feature de localização do usuário no mapa (pedido 2026-09-14:
// "vamos tirar isso do mapa por enquanto") sem apagar o trabalho — nenhuma
// permissão de geolocalização/bússola é pedida e nenhum layer é montado
// enquanto for `false`. Reativar é só trocar pra `true`.
const USER_LOCATION_FEATURE_ENABLED = false;

const USER_LOCATION_ICON_SIZE_PX = 56; // um pouco maior que o getSize:44 dos veículos, pra se destacar como "você"
const USER_LOCATION_CONE_SIZE_PX = 130;

// Cone de direção (estilo Google Maps/Uber) — SVG gerado em código (sem
// asset extra), um leque translúcido que aponta "pra cima" na imagem-fonte
// (mesma convenção north-up dos ícones de veículo, ver getAngle abaixo).
// Decisão 2026-09-14: testei girar o PERSONAGEM em si (ScenegraphLayer 3D
// ao vivo E o ícone 2D rotacionado) — o 3D ao vivo fica em pé e gira certo,
// mas o mapa olha reto de cima (sem tilt), então só dá pra ver o topo da
// cabeça/ombros, não o corpo (verificado offline, comparando com a câmera
// real do deck.gl); girar o ícone 2D (renderizado com a câmera inclinada
// pra mostrar o corpo todo) faz ele parecer "deitado" em certos ângulos,
// porque é uma imagem meio de perfil, não um top-down puro. Usuário
// escolheu manter o personagem sempre de frente (fixo) e mostrar a direção
// só com esse cone — padrão consolidado (blue dot + direction cone).
const USER_LOCATION_CONE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128"><defs><linearGradient id="g" x1="64" y1="64" x2="64" y2="10" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#2563eb" stop-opacity="0.6"/><stop offset="1" stop-color="#2563eb" stop-opacity="0"/></linearGradient></defs><path d="M64,64 L37.51,21.60 A50,50 0 0 1 90.49,21.60 Z" fill="url(#g)"/></svg>`;
const USER_LOCATION_CONE_ICON_URL = `data:image/svg+xml,${encodeURIComponent(USER_LOCATION_CONE_SVG)}`;

// Camada da localização do usuário no dispositivo (PWA) — halo de precisão
// do GPS (ScatterplotLayer, raio real em metros = accuracy do fix) +
// personagem (IconLayer, ícone 2D pré-renderizado, mesma técnica dos
// veículos — GPU-friendly, tamanho fixo em pixels independente do zoom).
// Overlay próprio (GoogleMapsOverlay), independente do overlay de
// MobileVehicleLayer — mesmo padrão de "cada camada cuida do seu
// WebGLOverlayView" já usado nesse arquivo. NUNCA lê/escreve posição de
// veículo — só a posição local do hook use-user-location.
//
// Posição do usuário é 100% local ao navegador (Geolocation API) — nunca é
// enviada ao backend; essa camada só a lê pra desenhar no mapa.
const MobileUserLocationLayer = memo(function MobileUserLocationLayer({
  Mod,
  position,
  visible,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  position: { lat: number; lng: number; heading: number | null; accuracy: number } | null;
  visible: boolean;
}) {
  const map = Mod.useMap();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlayRef = useRef<any>(null);

  useEffect(() => {
    if (!map) return;
    const overlay = new Mod.GoogleMapsOverlay({});
    overlay.setMap(map);
    overlayRef.current = overlay;
    return () => {
      overlay.finalize?.();
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, Mod]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const data = visible && position ? [position] : [];
    // Cone só aparece quando existe heading de verdade (bússola OU GPS em
    // movimento) — sem nenhum dos dois, nunca inventa uma direção (mesmo
    // raciocínio de use-user-location.ts/use-device-heading.ts, só que aqui
    // decide se a FEATURE aparece, não qual valor ela usa).
    const coneData = visible && position && position.heading != null ? [position] : [];
    overlay.setProps({
      layers: [
        new Mod.ScatterplotLayer({
          id: "mobile-user-location-accuracy",
          data,
          getPosition: (d: { lng: number; lat: number }) => [d.lng, d.lat],
          radiusUnits: "meters",
          getRadius: (d: { accuracy: number }) => d.accuracy,
          radiusMinPixels: 12,
          stroked: true,
          filled: true,
          getFillColor: [37, 99, 235, 40], // sky/blue-600, bem translúcido — halo de precisão do GPS
          getLineColor: [37, 99, 235, 90],
          lineWidthUnits: "pixels",
          getLineWidth: 1,
          pickable: false,
        }),
        new Mod.IconLayer({
          id: "mobile-user-direction-cone",
          data: coneData,
          pickable: false,
          sizeUnits: "pixels",
          getPosition: (d: { lng: number; lat: number }) => [d.lng, d.lat],
          getSize: USER_LOCATION_CONE_SIZE_PX,
          // `heading` já vem mesclado de cima (MobileApp): prioriza a
          // bússola do aparelho (DeviceOrientation — gira junto com o
          // celular, mesmo parado) e cai pro heading de GPS (direção do
          // deslocamento) só quando a bússola não está disponível/permitida.
          getAngle: (d: { heading: number | null }) => 360 - (d.heading ?? 0),
          getIcon: () => ({
            url: USER_LOCATION_CONE_ICON_URL,
            width: 128,
            height: 128,
            anchorY: 64,
          }),
        }),
        new Mod.IconLayer({
          id: "mobile-user-location",
          data,
          pickable: false,
          sizeUnits: "pixels",
          getPosition: (d: { lng: number; lat: number }) => [d.lng, d.lat],
          getSize: USER_LOCATION_ICON_SIZE_PX,
          // Personagem SEMPRE de frente (sem rotação) — ver decisão
          // 2026-09-14 no comentário de USER_LOCATION_CONE_SVG acima: girar
          // esse ícone (renderizado com câmera inclinada pra mostrar o corpo
          // todo) ficava parecendo deitado em certos ângulos. A direção é
          // mostrada só pelo cone acima.
          getIcon: () => ({ url: userLocationIconUrl, width: 128, height: 128, anchorY: 64 }),
        }),
      ],
    });
  }, [position, visible, Mod]);

  return null;
});

// Geocercas nativas do Google Maps (Circle/Polygon) — mesmo padrão do
// desktop, popup em HTML puro (sem React dentro) já que o conteúdo aqui é
// só nome+tipo, igual ao antigo gLayer.bindPopup() do Leaflet. memo pelo
// mesmo motivo de MobileVehicleLayer acima.
const MobileGeofencesLayer = memo(function MobileGeofencesLayer({
  Mod,
  geofences,
  visible,
  strokeColor,
  fillColor,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  geofences: Geofence[];
  visible: boolean;
  // Pedido 2026-09-04: âncora com cor própria (verde fraco/borda vermelha)
  // — ver mesma prop no GeofencesLayer do mapa desktop.
  strokeColor?: string;
  fillColor?: string;
}) {
  // useMap() (não um ref recebido por prop) — reage à instância real assim
  // que o mapa monta, mesmo motivo do GeofencesLayer no mapa desktop.
  const map = Mod.useMap();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlaysRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const infoWindowRef = useRef<any>(null);

  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    for (const ov of overlaysRef.current) ov.setMap(null);
    overlaysRef.current = [];
    if (!visible) return;

    try {
      for (const g of geofences) {
        const parsed = parseWkt(g.area);
        if (!parsed) continue;
        const attrColor = g.attributes?.color;
        const color = typeof attrColor === "string" && attrColor ? attrColor : "#0ea5e9";
        const typeLabel = parsed.type === "circle" ? "Círculo" : "Polígono";
        const baseOptions = {
          map,
          strokeColor: strokeColor ?? color,
          strokeWeight: 2,
          fillColor: fillColor ?? color,
          fillOpacity: fillColor ? 0.15 : 0.2,
          clickable: true,
        };
        const overlay =
          parsed.type === "circle"
            ? new google.maps.Circle({
                ...baseOptions,
                center: { lat: parsed.data.lat, lng: parsed.data.lng },
                radius: parsed.data.radius,
              })
            : new google.maps.Polygon({
                ...baseOptions,
                paths: parsed.data.points.map((p) => ({ lat: p.lat, lng: p.lng })),
              });
        overlay.addListener("click", (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          if (!infoWindowRef.current) infoWindowRef.current = new google.maps.InfoWindow();
          infoWindowRef.current.setContent(
            `<strong>${escapeHtml(g.name)}</strong><br/>Tipo: ${typeLabel}`,
          );
          infoWindowRef.current.setPosition(e.latLng);
          infoWindowRef.current.open(map);
        });
        overlaysRef.current.push(overlay);
      }
    } catch (err) {
      console.error("Falha ao desenhar geocercas no mapa — mapa/veículos seguem normalmente.", err);
    }
  }, [map, geofences, visible, strokeColor, fillColor]);

  useEffect(
    () => () => {
      for (const ov of overlaysRef.current) ov.setMap(null);
      overlaysRef.current = [];
      infoWindowRef.current?.close();
    },
    [],
  );

  return null;
});

// Câmera + clique de fundo (desseleciona, fecha menus) + captura da
// instância do mapa pra fora (googleMapRef, usado pelos botões de zoom) —
// mesmo padrão do CameraController do mapa principal desktop, simplificado
// (sem "seguir veículo", que o PWA não tem).
function MobileMapController({
  Mod,
  vehicles,
  selectedId,
  followNonce,
  hasCenteredRef,
  googleMapRef,
  onDeselect,
  onBoundsChange,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  vehicles: Vehicle[];
  selectedId: string | null;
  // Incrementado a cada toque no veículo — tocar de novo no mesmo veículo
  // volta a seguir depois de o usuário ter arrastado o mapa.
  followNonce: number;
  hasCenteredRef: MutableRefObject<boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  googleMapRef: MutableRefObject<any>;
  onDeselect: () => void;
  onBoundsChange: (bounds: MapBoundsBox, zoom: number) => void;
}) {
  const map = Mod.useMap();
  const onDeselectRef = useRef(onDeselect);
  onDeselectRef.current = onDeselect;
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;

  useEffect(() => {
    googleMapRef.current = map;
  }, [map, googleMapRef]);

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", () => onDeselectRef.current());
    return () => listener.remove();
  }, [map]);

  // Bug relatado 2026-09-09 "entro no mapa e o veículo fica sumindo e
  // reaparecendo" — mesma causa raiz do mapa desktop (ver comentário
  // análogo em routes/index.tsx#CameraController): antes de `vehicles`
  // carregar, o efeito de centralização abaixo ainda não rodou, então o
  // primeiro "idle" reflete o framing padrão do mapa (não a frota de
  // verdade). Como o clustering (use-vehicle-clusters.ts) usa esse bounds
  // pra excluir quem está fora dele, um veículo fora dessa área padrão
  // sumia até o segundo idle (já centralizado na frota) chegar. Ignora
  // esse primeiro idle só quando a centralização ainda não aconteceu nesta
  // sessão (hasCenteredRef ainda false no momento em que este listener é
  // montado) — o idle seguinte, disparado pela centralização real, chega
  // poucos instantes depois com o bounds correto.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      // Só ignora idles enquanto a centralização inicial ainda não aconteceu.
      // Antes era "ignora o 1º idle sempre": com a frota já em cache (abre
      // instantâneo) a centralização roda ANTES do 1º idle, então esse idle
      // — o único útil — era descartado e o bounds ficava null até o usuário
      // mexer no mapa, deixando o Cluster inativo (bug 2026-09-20).
      if (!hasCenteredRef.current) return;
      const b = map.getBounds();
      const z = map.getZoom();
      if (!b || z == null) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      onBoundsChangeRef.current(
        { west: sw.lng(), south: sw.lat(), east: ne.lng(), north: ne.lat() },
        z,
      );
    });
    return () => listener.remove();
  }, [map, hasCenteredRef]);

  // Pedido 2026-09-07: "o zoom fica errado, não aparece os veículos já de
  // cara" — centralizar na MÉDIA de lat/lng com zoom fixo 12 não tem
  // relação nenhuma com o quão espalhada a frota está (uma frota numa só
  // cidade some perto demais do chão, uma frota no estado inteiro fica
  // maioria fora da tela); mesmo bug já resolvido no mapa desktop
  // (routes/index.tsx#CameraController) com fitBounds — replicado aqui.
  // Bug relatado 2026-09-09, confirmado por print real (bolhas de cluster
  // esticadas/deformadas ao abrir o mapa): mesma causa raiz documentada em
  // routes/index.tsx#CameraController — `interleaved: false` (usado por
  // este overlay também, ver comentário completo em MobileVehicleLayer)
  // não suporta zoom fracionário, e é exatamente isso que o Google Maps
  // percorre durante fitBounds/setZoom/panTo animados. moveCamera() pula
  // direto pro estado final sem passar por zoom fracionário.
  useEffect(() => {
    if (!map || hasCenteredRef.current || !vehicles.length || typeof google === "undefined") return;
    hasCenteredRef.current = true;
    if (vehicles.length === 1) {
      map.moveCamera({ center: { lat: vehicles[0].lat, lng: vehicles[0].lng }, zoom: 17 });
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const v of vehicles) bounds.extend({ lat: v.lat, lng: v.lng });
    map.fitBounds(bounds, 60);
    const c = map.getCenter();
    const z = map.getZoom();
    if (c && z != null) map.moveCamera({ center: { lat: c.lat(), lng: c.lng() }, zoom: z });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vehicles.length, hasCenteredRef]);

  const selectedVehicle = selectedId ? vehicles.find((v) => v.id === selectedId) : undefined;
  // Seguir veículo (pedido 2026-10-17: "não segue o veículo no mapa, tenho
  // que ficar arrastando"): o veículo selecionado fica centralizado a cada
  // posição nova, até o usuário arrastar o mapa (aí para de seguir, igual
  // ao CameraController do desktop). Selecionar de novo volta a seguir.
  const followingRef = useRef(false);
  useEffect(() => {
    if (!map || !selectedVehicle) return;
    followingRef.current = true;
    map.moveCamera({
      center: { lat: selectedVehicle.lat, lng: selectedVehicle.lng },
      zoom: Math.max(map.getZoom() ?? 0, 16),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedVehicle?.id, followNonce]);

  useEffect(() => {
    if (!map || !selectedVehicle || !followingRef.current) return;
    // panTo sem mudar zoom — não passa por zoom fracionário (ver acima).
    map.panTo({ lat: selectedVehicle.lat, lng: selectedVehicle.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedVehicle?.lat, selectedVehicle?.lng]);

  useEffect(() => {
    if (!map || !selectedId) return;
    const listener = map.addListener("dragstart", () => {
      followingRef.current = false;
    });
    return () => listener.remove();
  }, [map, selectedId]);

  return null;
}

export function MobileApp() {
  const { data: vehicles = [], isLoading: vehiclesLoading } = useVehicles();
  // Motor de mapa migrado de Leaflet puro pra Google Maps real + deck.gl
  // 2026-09-03 (mesma migração do mapa principal desktop, ver
  // gpsdrive_deckgl_google_maps_pilot_2026-09-03 na memória) — nenhum
  // elemento DOM por veículo, um único <canvas> pra frota inteira.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [Mod, setMod] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const googleMapRef = useRef<any>(null);
  const { user, gpsDriveUser } = useAuth();
  // Vistorias feitas sem internet sobem sozinhas quando o sinal volta
  // (2026-10-16, lib/offline-inspections.ts).
  const inspectionQueryClient = useQueryClient();
  useOfflineInspectionSync(gpsDriveUser?.id, (n) => {
    toast.success(n === 1 ? "Vistoria feita sem internet foi enviada" : `${n} vistorias feitas sem internet foram enviadas`);
    void inspectionQueryClient.invalidateQueries({ queryKey: ["vehicle-inspections"] });
  });
  // Pedido 2026-09-08: o app sempre abre na tela Início, nunca no mapa.
  const [activeTab, setActiveTab] = useState<TabKey>("inicio");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [followNonce, setFollowNonce] = useState(0);
  const handleMapVehicleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setFollowNonce((n) => n + 1);
  }, []);
  // Lápis de editar na lista de Veículos — só pra quem é Administrador
  // (pedido 2026-08-31: "tem que ter um lápis para quem é admin, para ele
  // poder editar alguma informação"). Reaproveita o MESMO formulário
  // completo já usado no desktop (VehicleEditDialog/VehicleFormPanel, ver
  // routes/index.tsx) — nunca uma segunda implementação divergente, ver
  // VEHICLE_FORM_ARCHITECTURE.md §0.
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  const [mapReady, setMapReady] = useState(false);
  // Bounds+zoom atuais do mapa, só pro clustering de veículos (ver
  // use-vehicle-clusters.ts) — atualizado no "idle" do Google Maps dentro
  // de MobileMapController.
  const [mapBounds, setMapBounds] = useState<MapBoundsBox | null>(null);
  const [mapZoom, setMapZoom] = useState(12);
  // Mapa padrão e tema — carregados uma vez do localStorage (ver
  // readStoredLayer/readStoredTheme acima) e persistidos a cada mudança,
  // pra abrir sempre do jeito que o cliente escolheu em Preferências (ou no
  // seletor de camadas do mapa, mesma preferência) — pedido: "sempre que
  // abrir o app tem que aparecer esse mapa".
  const [layer, setLayerState] = useState<LayerKey>(readStoredLayer);
  const [darkMode, setDarkModeState] = useState<boolean>(readStoredTheme);
  const setLayer = (next: LayerKey) => {
    setLayerState(next);
    try {
      localStorage.setItem(MOBILE_MAP_LAYER_KEY, next);
    } catch {
      // localStorage indisponível — preferência não persiste, mas não quebra o app
    }
  };
  // useCallback — passado como prop pra YouPanel (memoizado, ver abaixo),
  // senão uma nova função a cada render do MobileApp anularia o memo.
  const setDarkMode = useCallback((next: boolean) => {
    setDarkModeState(next);
    try {
      localStorage.setItem(MOBILE_THEME_KEY, next ? "dark" : "light");
    } catch {
      // idem acima
    }
  }, []);
  const [showLayers, setShowLayers] = useState(false);
  const [showShare, setShowShare] = useState(false);
  // Botão de "olho" (pedido 2026-08-30) — geocercas e agrupamento de
  // veículos, mesmas duas camadas do mapa principal desktop.
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  // "Alertas do Waze" (menu do olho) — ícones no próprio mapa, ver
  // components/waze-alerts-layer.tsx.
  const [showWaze, setShowWaze] = useWazeAlertsToggle();
  const [showGeofences, setShowGeofences] = useState<boolean>(readStoredShowGeofences);
  const toggleShowGeofences = (next: boolean) => {
    setShowGeofences(next);
    try {
      localStorage.setItem(MOBILE_SHOW_GEOFENCES_KEY, next ? "1" : "0");
    } catch {
      // localStorage indisponível — preferência não persiste, mas não quebra o app
    }
  };
  // Tags no mapa (pedido 2026-10-01, igual ao olho do mapa desktop) —
  // ligado por padrão; a selecionada continua visível mesmo desligado.
  const [showTags, setShowTags] = useState<boolean>(() => {
    try {
      return localStorage.getItem(MOBILE_SHOW_TAGS_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const toggleShowTags = (next: boolean) => {
    setShowTags(next);
    try {
      localStorage.setItem(MOBILE_SHOW_TAGS_KEY, next ? "1" : "0");
    } catch {
      // idem acima
    }
  };
  // Mesma regra do mapa web (routes/index.tsx#allMapVehicles, pedido
  // 2026-10-01): só entra no mapa quem já mandou posição GPS real — o
  // dispositivo que nunca comunicou vinha com 0,0 e aparecia no meio do
  // oceano (e puxava o enquadramento inicial pra lá). Continua na lista.
  const mapVehicles = useMemo(
    () =>
      vehicles.filter(
        (v) =>
          v.hasPosition !== false &&
          (v.lat !== 0 || v.lng !== 0) &&
          (showTags || !v.isTag || v.id === selectedId),
      ),
    [vehicles, showTags, selectedId],
  );
  const [showClusters, setShowClusters] = useState<boolean>(readStoredShowClusters);
  // Quantos veículos caem na área visível AGORA, independente de estar
  // agrupando ou não — é essa contagem (não o total da frota) que decide
  // se dá pra desligar o agrupamento, porque o problema é quanto tem que
  // desenhar na tela, não quanto o cliente tem cadastrado. Recalcula a
  // cada pan/zoom (mapBounds muda no "idle"), então dar zoom numa região
  // com poucos veículos libera o toggle de novo.
  const visibleVehicleCount = useMemo(() => {
    if (!mapBounds) return mapVehicles.length;
    return mapVehicles.filter(
      (v) =>
        v.lng >= mapBounds.west &&
        v.lng <= mapBounds.east &&
        v.lat >= mapBounds.south &&
        v.lat <= mapBounds.north,
    ).length;
  }, [mapVehicles, mapBounds]);
  // Acima do limite, agrupar deixa de ser opcional — ver
  // CLUSTER_MANDATORY_THRESHOLD. `clusteringActive` (não `showClusters`
  // puro) é o valor real que vale pro mapa: ignora a preferência salva
  // enquanto tiver veículo demais em vista pra desenhar sem agrupar.
  const clusterMandatory = visibleVehicleCount > CLUSTER_MANDATORY_THRESHOLD;
  const clusteringActive = showClusters || clusterMandatory;
  const toggleShowClusters = (next: boolean) => {
    if (!next && clusterMandatory) {
      toast.error("Agrupamento obrigatório nessa área", {
        description: `Tem ${visibleVehicleCount} veículos na área visível (limite: ${CLUSTER_MANDATORY_THRESHOLD}) — o celular não consegue desenhar todos individualmente sem travar. Dê mais zoom numa região com menos veículos pra poder desagrupar.`,
      });
      return;
    }
    setShowClusters(next);
    try {
      localStorage.setItem(MOBILE_SHOW_CLUSTERS_KEY, next ? "1" : "0");
    } catch {
      // idem acima
    }
  };
  // Rastro em tempo real (pedido 2026-09-20) — igual ao do mapa desktop: ligar
  // não busca histórico, só acumula os fixes que chegarem a partir de agora
  // (poll da frota) do veículo selecionado. Só em memória; some ao recarregar.
  const [trails, setTrails] = useState<
    Record<string, { lat: number; lng: number; ts: string | null }[]>
  >({});
  const trailActive = !!selectedId && !!trails[selectedId];
  const toggleTrail = (next: boolean) => {
    if (!selectedId) {
      toast.info("Selecione um veículo no mapa para ver o rastro.");
      return;
    }
    const v = vehicles.find((x) => x.id === selectedId);
    setTrails((prev) => {
      const copy = { ...prev };
      if (next && v)
        copy[selectedId] = [{ lat: v.lat, lng: v.lng, ts: v.positionTimestamp ?? null }];
      else delete copy[selectedId];
      return copy;
    });
  };
  useEffect(() => {
    setTrails((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const v of vehicles) {
        const pts = prev[v.id];
        if (!pts) continue;
        const last = pts[pts.length - 1];
        const ts = v.positionTimestamp ?? null;
        if (last && last.lat === v.lat && last.lng === v.lng && last.ts === ts) continue;
        next[v.id] = [...pts, { lat: v.lat, lng: v.lng, ts }].slice(-480);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [vehicles]);
  const { data: geofences = [] } = useGeofences(showGeofences);
  const [showDetails, setShowDetails] = useState(false);
  const [showMeuPerfil, setShowMeuPerfil] = useState(false);
  const [showRegistrarAssistencia, setShowRegistrarAssistencia] = useState(false);
  const [showMinhasAssistencias, setShowMinhasAssistencias] = useState(false);
  // "Apagar histórico" (aba Perfil, pedido 2026-09-08) — ver
  // vehicle-history-delete-dialog.tsx.
  const [showApagarHistorico, setShowApagarHistorico] = useState(false);
  // "Criar usuário" (aba Perfil, pedido 2026-09-10/11) — abre um popup de
  // gestão (listar/editar/excluir os que já criou + criar novo), ver
  // sub-users-manager-dialog.tsx.
  const [showCriarUsuario, setShowCriarUsuario] = useState(false);
  // Central de Indicações (aba Perfil, pedido 2026-09-27, só cliente) — ver
  // mobile-referrals.tsx.
  const [showIndicacoes, setShowIndicacoes] = useState(false);
  // Vistoria do veículo (Perfil, pedido 2026-10-08) — ver
  // mobile-vehicle-inspection.tsx.
  const [showVistoria, setShowVistoria] = useState(false);
  // Manutenção do veículo (Perfil, só cliente, pedido 2026-10-14) — ver
  // mobile-client-maintenance.tsx.
  const [showManutencao, setShowManutencao] = useState(false);
  // Assistente IA (BETA, pedido 2026-10-20) — aba própria ao lado de
  // Eventos desde 2026-10-21, só pra quem o backend libera (testadores +
  // equipe), ver ai-assistant/. Monta na primeira visita e fica montada
  // (volta na mesma conversa ao trocar de aba).
  const aiAccess = useAiAssistantAccess();
  const [aiVisited, setAiVisited] = useState(false);
  const aiKeyboard = useKeyboardInset(activeTab === "assistente");
  useEffect(() => {
    if (activeTab === "assistente") setAiVisited(true);
  }, [activeTab]);
  // Faturas deixou de ser uma aba própria (pedido 2026-09-01: tela Início
  // nova substituiu a aba) — vira uma tela cheia acessível por um atalho
  // dentro do Início, igual Meu perfil/Registrar Assistência acima.
  const [showFaturas, setShowFaturas] = useState(false);
  // "Meus relatórios" do Início abre o mesmo RouteHistoryCenter compacto já
  // usado dentro de VehicleDetails, só que direto pelo atalho da home, sem
  // precisar abrir os Detalhes do veículo primeiro.
  const [reportsVehicleId, setReportsVehicleId] = useState<string | null>(null);
  const hasCenteredRef = useRef(false);

  // Acesso suspenso (pedido 2026-08-28) — a ÚNICA fonte de verdade é o
  // backend (ver useMyBillingStatus/BillingService#getMyStatus), nunca uma
  // suposição do front. Enquanto suspended for true, TODA navegação some e
  // volta pra Início sozinha (que passa a renderizar o FinanceiroPanel
  // direto, ver abaixo) — goToTab() abaixo é o único jeito de trocar de aba
  // no app inteiro (ver TabItem/handleVehiclePick), então basta esse único
  // ponto pra bloquear tudo de uma vez.
  const { data: billingStatus } = useMyBillingStatus();
  const suspended = billingStatus?.suspended === true;
  // Motorista (pedido 2026-10-08): só as abas que o admin liberou em
  // Permissões → Menus do app. goToTab é o único jeito de trocar de aba,
  // então o bloqueio vale também pros atalhos (ex.: "Ver no mapa").
  const menuAccess = useAppMenuAccess();
  // Agenda no app (pedido 2026-10-15: "quem tiver permissão de ver agenda
  // deve aparecer o menu igual o da Isabel") — mesma regra da sidebar
  // (módulo ligado + não cliente + permissão).
  const canSeeAgenda = useSidebarNavItems().some((item) => item.url === "/agenda");
  const navigateToRoute = useNavigate();
  // useCallback — várias telas memoizadas (InicioPanel/YouPanel) dependem
  // dele indiretamente pra abrir abas; identidade estável evita re-render
  // desses painéis à toa.
  const goToTab = useCallback(
    (tab: TabKey) => {
      if (suspended && tab !== "inicio") {
        toast.warning("Acesso suspenso por pendência financeira", {
          description: "Regularize o pagamento para liberar o restante do aplicativo.",
        });
        setActiveTab("inicio");
        return;
      }
      if (
        (tab === "mapa" && !menuAccess.mapa) ||
        (tab === "veiculos" && !menuAccess.veiculos) ||
        (tab === "eventos" && !menuAccess.eventos)
      ) {
        toast.warning("Você não tem acesso a este menu");
        return;
      }
      setActiveTab(tab);
    },
    [suspended, menuAccess.mapa, menuAccess.veiculos, menuAccess.eventos],
  );
  // Redundância proposital ao goToTab acima: cobre o instante entre a
  // suspensão ser confirmada pelo poll (useMyBillingStatus#refetchInterval)
  // e qualquer clique do usuário — sem isso, quem já estava em "Mapa"
  // quando a suspensão bateu ficava lá até clicar em algo.
  useEffect(() => {
    if (suspended && activeTab !== "inicio") setActiveTab("inicio");
  }, [suspended, activeTab]);

  const selected = useMemo(
    () => (selectedId ? (vehicles.find((v) => v.id === selectedId) ?? null) : null),
    [selectedId, vehicles],
  );

  // Localização do usuário no dispositivo (Geolocation API do navegador,
  // 100% local — nunca sai do PWA). Só liga o watch com a aba Mapa aberta:
  // não pede permissão assim que o app abre, nem mantém o GPS ligado em
  // segundo plano nas outras abas. Ver use-user-location.ts.
  //
  // Desativada temporariamente (pedido 2026-09-14) — código todo mantido
  // (hooks/layer/UI), só a flag desliga: nenhuma permissão é pedida, nenhum
  // layer é montado. Reativar é só trocar pra `true`.
  const { status: userLocationStatus, position: userLocation } = useUserLocation(
    USER_LOCATION_FEATURE_ENABLED && activeTab === "mapa",
  );
  const deniedToastShownRef = useRef(false);
  useEffect(() => {
    if (userLocationStatus === "denied" && !deniedToastShownRef.current) {
      deniedToastShownRef.current = true;
      toast.info("Localização não autorizada", {
        description:
          "Pra mostrar sua posição no mapa e a distância até o veículo, é preciso permitir o acesso à localização nas configurações do navegador/app.",
      });
    }
  }, [userLocationStatus]);

  // Bússola do aparelho (DeviceOrientation API) — gira o personagem junto
  // com o celular, mesmo parado (diferente do heading de GPS acima, que só
  // existe andando). iOS 13+ exige um toque explícito pra liberar
  // (`requestPermission`, ver botão "Ativar bússola" nos controles do mapa
  // mais abaixo); Android/demais navegadores ligam sozinhos. Ver
  // use-device-heading.ts.
  const {
    status: deviceHeadingStatus,
    heading: deviceHeading,
    requestPermission: requestHeadingPermission,
  } = useDeviceHeading(USER_LOCATION_FEATURE_ENABLED && activeTab === "mapa");
  // Prioriza a bússola (responde a girar o celular parado); GPS moving-
  // heading como fallback; nunca inventa uma direção do zero.
  const effectiveHeading = deviceHeading ?? userLocation?.heading ?? null;
  const userLocationWithHeading = useMemo(
    () => (userLocation ? { ...userLocation, heading: effectiveHeading } : null),
    [userLocation, effectiveHeading],
  );

  // Distância até o veículo selecionado — Haversine local, recalcula sozinho
  // sempre que a posição do usuário OU a do veículo mudar (useMemo comum,
  // nada de polling extra). Nunca é enviada a lugar nenhum, só exibida.
  const distanceToSelectedMeters = useMemo(() => {
    if (!selected || !userLocation) return null;
    return haversineDistanceMeters(userLocation, { lat: selected.lat, lng: selected.lng });
  }, [selected, userLocation]);

  // Perímetro da âncora do veículo aberto no mapa (pedido 2026-09-04) —
  // mesmo padrão do mapa desktop (routes/index.tsx#MapPage): busca só do
  // veículo selecionado, nunca da frota inteira (evitaria N+1 query num
  // celular com milhares de veículos).
  const { anchor: selectedAnchor } = useVehicleAnchor(selected ? Number(selected.id) : undefined);
  const anchorGeofences = useMemo(
    () => (selectedAnchor ? [selectedAnchor.geofence] : []),
    [selectedAnchor],
  );

  // Google Maps real + deck.gl carregados via import() dinâmico — só quando
  // o mapa de fato monta, mesmo padrão do mapa principal desktop.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [rgm, dgm, dgl] = await Promise.all([
        import("@vis.gl/react-google-maps"),
        import("@deck.gl/google-maps"),
        import("@deck.gl/layers"),
      ]);
      if (alive) {
        setMod({
          APIProvider: rgm.APIProvider,
          Map: rgm.Map,
          useMap: rgm.useMap,
          GoogleMapsOverlay: dgm.GoogleMapsOverlay,
          IconLayer: dgl.IconLayer,
          ScatterplotLayer: dgl.ScatterplotLayer,
          TextLayer: dgl.TextLayer,
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Container do mapa fica escondido (display) quando outra aba está ativa
  // — Google Maps precisa de um "resize" explícito quando volta a ficar
  // visível, senão os tiles ficam cortados/incompletos até o próximo pan.
  useEffect(() => {
    if (activeTab !== "mapa") return;
    const m = googleMapRef.current;
    if (!m || typeof google === "undefined") return;
    requestAnimationFrame(() => google.maps.event.trigger(m, "resize"));
  }, [activeTab]);

  const zoomBy = (delta: number) => {
    const m = googleMapRef.current;
    if (!m) return;
    m.setZoom((m.getZoom() ?? 12) + delta);
  };

  // useCallback — passado como onPick/onSelectVehicle pra VehiclesList e
  // InicioPanel (ambos memoizados).
  const handleVehiclePick = useCallback(
    (id: string) => {
      setSelectedId(id);
      goToTab("mapa");
    },
    [goToTab],
  );

  // Callbacks abaixo só existem pra manter identidade estável entre renders
  // pros painéis memoizados (InicioPanel/YouPanel) — pedido 2026-09-08 "app
  // lento": sem useCallback, um simples `() => setShowX(true)` inline no JSX
  // seria uma função NOVA a cada render do MobileApp, o que anula o
  // React.memo desses painéis (props "diferentes" a cada comparação).
  const openVehicleDetailsFromInicio = useCallback((id: string) => {
    setSelectedId(id);
    setShowDetails(true);
  }, []);
  const openEventosTab = useCallback(() => goToTab("eventos"), [goToTab]);
  const openPerfilTab = useCallback(() => goToTab("voce"), [goToTab]);
  const openMeuPerfil = useCallback(() => setShowMeuPerfil(true), []);
  const openRegistrarAssistencia = useCallback(() => setShowRegistrarAssistencia(true), []);
  const openMinhasAssistencias = useCallback(() => setShowMinhasAssistencias(true), []);
  const openApagarHistorico = useCallback(() => setShowApagarHistorico(true), []);
  const openCriarUsuario = useCallback(() => setShowCriarUsuario(true), []);
  const openIndicacoes = useCallback(() => setShowIndicacoes(true), []);
  const openVistoria = useCallback(() => setShowVistoria(true), []);
  const openManutencao = useCallback(() => setShowManutencao(true), []);
  const openFaturas = useCallback(() => setShowFaturas(true), []);

  const showMap = activeTab === "mapa";
  const shortName = (v: Vehicle) => `${v.name.split(" ")[0].toUpperCase()} - ${v.plate}`;

  return (
    <div
      className={cn(
        "relative flex h-[100dvh] w-full flex-col overflow-hidden",
        darkMode ? "dark bg-slate-950 text-slate-100" : "bg-white text-slate-900",
      )}
    >
      {/* Fundo cinza (cor de "terreno" do Google) em vez de branco, e o
          carrinho até a 1ª tela de mapa terminar de desenhar — queixa
          2026-10-05: "no mapa fica tudo branco até carregar tudo". */}
      <div className="absolute inset-0 z-0 bg-[#e5e3df] dark:bg-slate-900">
        {showMap && !mapReady && (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
            <GpsInlineLoader size={110} />
          </div>
        )}
        {Mod && (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) && (
          <Mod.APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string}>
            <Mod.Map
              mapId={resolveGoogleMapId()}
              defaultCenter={{ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] }}
              defaultZoom={12}
              // Mesmo motivo do mapa desktop (routes/index.tsx) — sem isso
              // dava pra tirar o zoom até o nível "mundo" (tiles repetem na
              // projeção Mercator).
              minZoom={3}
              mapTypeId={layer === "streets" ? "roadmap" : layer}
              disableDefaultUI
              zoomControl={false}
              clickableIcons={false}
              gestureHandling="greedy"
              style={{ height: "100%", width: "100%" }}
              onTilesLoaded={() => setMapReady(true)}
            >
              <SatelliteZoomGuard Mod={Mod} mapTypeId={layer === "streets" ? "roadmap" : layer} />
              <MapTileRecovery Mod={Mod} />
              <MobileMapController
                Mod={Mod}
                vehicles={mapVehicles}
                selectedId={selectedId}
                followNonce={followNonce}
                hasCenteredRef={hasCenteredRef}
                googleMapRef={googleMapRef}
                onDeselect={() => {
                  setSelectedId(null);
                  setShowLayers(false);
                  setShowVisibilityMenu(false);
                }}
                onBoundsChange={(bounds, zoom) => {
                  setMapBounds(bounds);
                  setMapZoom(zoom);
                }}
              />
              {/* MobileVehicleLayer agora fica SEMPRE montada (não mais
                  gated em `showMap`) — pedido 2026-09-09 "quando clico na
                  aba do mapa aparece nenhum veículo, passa uns 2s e aparece":
                  desmontar/remontar a cada troca de aba (fix 2026-09-08
                  abaixo) recriava do zero o overlay/contexto WebGL do
                  deck.gl (GoogleMapsOverlay) — compilar shaders + subir a
                  atlas de ícones pra uma GPU/WebView de celular é
                  perceptível (a origem provável do atraso de ~2s), e
                  acontecia TODA vez que a aba Mapa era aberta, não só na
                  primeira. Criar o overlay uma única vez (quando `map`/`Mod`
                  ficam prontos) elimina esse custo recorrente. O componente
                  agora recebe `visible={showMap}` e congela a lista de
                  veículos internamente enquanto escondido (ver comentário em
                  MobileVehicleLayer) — preserva o motivo original do fix
                  2026-09-08 (não recomputar o índice Supercluster pra frota
                  inteira em segundo plano, achado real com a conta de teste
                  Administrador/5000 dispositivos, ver
                  gpsdrive_pwa_slowness_admin_fleet_2026-09-08) sem pagar de
                  novo o custo de (re)inicializar o WebGL a cada visita. */}
              <MobileVehicleLayer
                Mod={Mod}
                vehicles={mapVehicles}
                bounds={clusteringActive ? mapBounds : null}
                zoom={mapZoom}
                focusId={selectedId}
                onSelect={handleMapVehicleSelect}
                visible={showMap}
                interleaved={layer === "streets"}
              />
              {/* Personagem de localização do usuário — camada/overlay
                  própria, totalmente independente de MobileVehicleLayer
                  acima (nunca lê/mexe em posição de veículo). Só desenha
                  quando a aba Mapa está aberta E já existe um fix de GPS.
                  Desativada por enquanto — ver USER_LOCATION_FEATURE_ENABLED. */}
              {USER_LOCATION_FEATURE_ENABLED && (
                <MobileUserLocationLayer
                  Mod={Mod}
                  position={userLocationWithHeading}
                  visible={showMap}
                />
              )}
              <MobileTrailsLayer Mod={Mod} trails={trails} />
              <WazeAlertsLayer
                Mod={Mod}
                bounds={mapBounds}
                visible={showWaze && showMap}
                interleaved={layer === "streets"}
              />
              {showMap && (
                <>
                  <MobileGeofencesLayer Mod={Mod} geofences={geofences} visible={showGeofences} />
                  {/* Âncora do veículo aberto — mesma camada (é uma geocerca de
                      verdade), instância própria pra não depender do toggle
                      "Mostrar geocercas" (ver comentário no mapa desktop). Cor
                      própria (pedido 2026-09-04: verde fraco/borda vermelha). */}
                  <MobileGeofencesLayer
                    Mod={Mod}
                    geofences={anchorGeofences}
                    visible={!!selectedAnchor}
                    strokeColor="#ef4444"
                    fillColor="#86efac"
                  />
                </>
              )}
            </Mod.Map>
          </Mod.APIProvider>
        )}
      </div>

      {
        // z-10 (abaixo da nav, que agora é z-40) e cobrindo até o fim da tela
        // (sem "bottom" recortando o fundo) — antes o fundo parava 68px+
        // safe-area antes do fim pra "abrir espaço" pra nav aparecer por
        // baixo dele, mas esse número é um chute que não bate exatamente com
        // a altura real da nav renderizada, deixando uma fresta onde o mapa
        // ao vivo (z-0) aparecia por trás (bug relatado 2026-08-31 "ainda"
        // acontecendo em Faturas/Veículos/Eventos/Perfil no PWA do iPhone).
        // Preenchimento por baixo (padding, não corte) garante que a última
        // linha da lista não fique escondida atrás da nav ao rolar.
        //
        // O painel inteiro (e cada aba dentro dele) fica SEMPRE montado,
        // só escondido via `hidden` (display:none) quando não é a ativa —
        // mesmo truque que o mapa já usa (ver `showMap`/resize acima).
        // Antes cada troca de aba desmontava o painel anterior e montava o
        // de novo do zero, o que reexecutava TODAS as queries dele (resumo/
        // paradas/viagens/imagens/geocode do Início, lista de Veículos,
        // feed de Eventos) mesmo quando o cache já tinha acabado de buscar
        // os mesmos dados segundos antes — daí a demora perceptível pra
        // trocar de aba (pedido 2026-09-03: "quando eu to clicando em
        // inicio, veiculos eventos perfil demora muito para trocar"). Ficar
        // sempre montado também corrige de raiz o `useMemo(() => ..., [])`
        // da janela "hoje" do Início (recalculava com `new Date()` novo a
        // cada montagem, gerando uma queryKey diferente e forçando refetch
        // completo toda vez que voltava pra essa aba).
        <div
          className={cn(
            "absolute inset-0 z-10 overflow-y-auto bg-slate-50 dark:bg-slate-950",
            showMap && "hidden",
          )}
          style={{ paddingBottom: "calc(58px + env(safe-area-inset-bottom))" }}
        >
          {!suspended && (
            <div className={activeTab === "veiculos" ? undefined : "hidden"}>
              <VehiclesList
                vehicles={vehicles}
                onPick={handleVehiclePick}
                onEdit={setEditVehicle}
              />
            </div>
          )}
          {!suspended && (
            <div className={activeTab === "voce" ? undefined : "hidden"}>
              <YouPanel
                onOpenMeuPerfil={openMeuPerfil}
                onOpenRegistrarAssistencia={openRegistrarAssistencia}
                onOpenMinhasAssistencias={openMinhasAssistencias}
                onOpenApagarHistorico={openApagarHistorico}
                onOpenCriarUsuario={openCriarUsuario}
                onOpenIndicacoes={openIndicacoes}
                onOpenVistoria={openVistoria}
                onOpenManutencao={openManutencao}
                darkMode={darkMode}
                onChangeDarkMode={setDarkMode}
              />
            </div>
          )}
          <div className={activeTab === "inicio" ? undefined : "hidden"}>
            {suspended ? (
              <FinanceiroPanel />
            ) : (
              menuAccess.isMotorista ? (
              // Motorista (pedido 2026-10-08): tela inicial própria — ver
              // mobile-driver-home.tsx.
              <DriverHomePanel
                vehicles={vehicles}
                vehiclesLoading={vehiclesLoading}
                greeting={greetingFor(undefined)}
                statusColor={statusColorFor}
                statusLabel={statusText}
                iconUrl={iconUrlFor}
                onOpenVehicleDetails={openVehicleDetailsFromInicio}
                onShowOnMap={handleVehiclePick}
                onOpenEventos={openEventosTab}
                onOpenReports={setReportsVehicleId}
                onOpenVistoria={openVistoria}
              />
              ) : (
              <InicioPanel
                vehicles={vehicles}
                vehiclesLoading={vehiclesLoading}
                traccarUserId={user?.id}
                onSelectVehicle={handleVehiclePick}
                onOpenVehicleDetails={openVehicleDetailsFromInicio}
                onOpenEventos={openEventosTab}
                onOpenAssistencia={openRegistrarAssistencia}
                onOpenMinhasAssistencias={openMinhasAssistencias}
                onOpenFaturas={openFaturas}
                onOpenReports={setReportsVehicleId}
                onOpenPerfilTab={openPerfilTab}
                darkMode={darkMode}
              />
              )
            )}
          </div>
          {!suspended && (
            <div className={activeTab === "eventos" ? undefined : "hidden"}>
              <EventosPanel vehicles={vehicles} active={activeTab === "eventos"} />
            </div>
          )}
        </div>
      }

      {aiAccess.data?.allowed && aiVisited && !suspended && (
        <div
          className={cn("absolute inset-x-0 top-0 z-10", activeTab !== "assistente" && "hidden")}
          // Teclado aberto: a barra de abas some e o chat desce até o fim
          // (o campo sobe pelo keyboardInset quando a WebView não encolhe).
          style={{ bottom: aiKeyboard.open ? 0 : "calc(4.5rem + env(safe-area-inset-bottom))" }}
        >
          <MobileAiAssistantPanel me={aiAccess.data} keyboardInset={aiKeyboard.inset} />
        </div>
      )}

      {/* Top pill — paddingTop soma a safe-area do topo, senão fica atrás
          da barra de status/entalhe no iPhone/Android (pedido 2026-10-16). */}
      {showMap && selected && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center"
          style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
        >
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-sky-500 bg-white/95 px-3 py-1.5 shadow-md backdrop-blur dark:bg-slate-900/95">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: statusColorFor(selected) }}
            />
            <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
              {shortName(selected)}
            </span>
            <Zap className="h-3.5 w-3.5 text-orange-500" strokeWidth={2.5} />
            <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
              {selected.speed} km/h
            </span>
          </div>
        </div>
      )}

      {/* Right controls */}
      {showMap && (
        <div className="absolute right-3 top-20 z-10 flex flex-col gap-2.5">
          {/* iOS 13+ só libera a bússola do personagem depois de um toque
              explícito (ver use-device-heading.ts) — some sozinho assim que
              conceder. Não aparece no Android (liga sozinho, sem esse
              passo). */}
          {USER_LOCATION_FEATURE_ENABLED &&
            (deviceHeadingStatus === "idle" || deviceHeadingStatus === "denied") && (
              <button
                type="button"
                onClick={() => requestHeadingPermission()}
                className="grid h-11 w-11 place-items-center rounded-full bg-slate-800 text-white shadow-lg"
                aria-label="Ativar bússola"
                title="Ativar bússola"
              >
                <Compass className="h-5 w-5" strokeWidth={2.5} />
              </button>
            )}
          {/* Compartilhar localização (pedido 2026-09-23) — ver
              location-share-sheet.tsx. Já abre com o veículo selecionado. */}
          {menuAccess.compartilhar && (
          <button
            type="button"
            onClick={() => {
              setShowShare(true);
              setShowLayers(false);
              setShowVisibilityMenu(false);
            }}
            className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-sky-500 text-white shadow-lg shadow-teal-500/40"
            aria-label="Compartilhar localização"
            title="Compartilhar localização"
          >
            <Share2 className="h-5 w-5" strokeWidth={2.5} />
          </button>
          )}
          <button
            type="button"
            onClick={() => {
              setShowLayers((s) => !s);
              setShowVisibilityMenu(false);
            }}
            className="grid h-11 w-11 place-items-center rounded-full bg-slate-800 text-white shadow-lg"
            aria-label="Camadas"
          >
            <Layers className="h-5 w-5" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={() => {
              setShowVisibilityMenu((s) => !s);
              setShowLayers(false);
            }}
            className={cn(
              "grid h-11 w-11 place-items-center rounded-full text-white shadow-lg",
              showGeofences ? "bg-sky-600" : "bg-slate-800",
            )}
            aria-label="Visibilidade do mapa"
          >
            <Eye className="h-5 w-5" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={() => zoomBy(1)}
            className="grid h-11 w-11 place-items-center rounded-full bg-slate-800 text-white shadow-lg"
            aria-label="Aproximar"
          >
            <Plus className="h-5 w-5" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => zoomBy(-1)}
            className="grid h-11 w-11 place-items-center rounded-full bg-slate-800 text-white shadow-lg"
            aria-label="Afastar"
          >
            <Minus className="h-5 w-5" strokeWidth={3} />
          </button>
        </div>
      )}

      {/* Layers popover */}
      {showMap && showLayers && (
        <div className="absolute right-16 top-20 z-20 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
          {(Object.keys(LAYERS) as LayerKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setLayer(k);
                setShowLayers(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm",
                layer === k
                  ? "bg-sky-50 font-semibold text-sky-700 dark:bg-sky-950/40 dark:text-sky-300"
                  : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800",
              )}
            >
              <span className="text-base">{LAYERS[k].emoji}</span>
              <span>{LAYERS[k].label}</span>
              {layer === k && <span className="ml-auto text-sky-600 dark:text-sky-400">✓</span>}
            </button>
          ))}
        </div>
      )}

      {/* Visibilidade (olho) — geocercas + agrupar veículos, mesma camada
          do botão de olho do mapa principal desktop. */}
      {showMap && showVisibilityMenu && (
        <div className="absolute right-16 top-[136px] z-20 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center gap-2 px-2.5 py-2">
            <LandPlot className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">Geocercas</span>
            <Switch
              checked={showGeofences}
              onCheckedChange={toggleShowGeofences}
              aria-label="Mostrar geocercas"
            />
          </div>
          <div className="flex items-center gap-2 px-2.5 py-2">
            <Combine className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <div className="flex-1">
              <div className="text-sm text-slate-700 dark:text-slate-200">Cluster</div>
              {clusterMandatory && (
                <div className="text-[11px] text-slate-400 dark:text-slate-500">
                  Obrigatório nessa área
                </div>
              )}
            </div>
            <Switch
              checked={clusteringActive}
              onCheckedChange={toggleShowClusters}
              aria-label="Agrupar veículos próximos (cluster)"
            />
          </div>
          <div className="flex items-center gap-2 px-2.5 py-2">
            <Tag className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">Tags</span>
            <Switch checked={showTags} onCheckedChange={toggleShowTags} aria-label="Mostrar tags no mapa" />
          </div>
          <div className="flex items-center gap-2 px-2.5 py-2">
            <RouteIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <div className="flex-1">
              <div className="text-sm text-slate-700 dark:text-slate-200">Rastro</div>
              {!selectedId && (
                <div className="text-[11px] text-slate-400 dark:text-slate-500">
                  Selecione um veículo
                </div>
              )}
            </div>
            <Switch
              checked={trailActive}
              onCheckedChange={toggleTrail}
              aria-label="Mostrar rastro do veículo selecionado"
            />
          </div>
          <div className="mx-2 my-1 h-px bg-slate-200 dark:bg-slate-700" />
          <div className="flex items-center gap-2 px-2.5 py-2">
            <WazeIcon className="h-4 w-4" />
            <span className="flex-1 text-sm text-slate-700 dark:text-slate-200">Alertas do Waze</span>
            <Switch
              checked={showWaze}
              onCheckedChange={setShowWaze}
              aria-label="Mostrar alertas do Waze no mapa"
            />
          </div>
        </div>
      )}

      <VehicleEditDialog vehicle={editVehicle} onOpenChange={(o) => !o && setEditVehicle(null)} />

      {/* Bottom sheet — tap to open details. Botões redondos
          Bloquear/Desbloquear/Âncora logo acima do cartão (pedido
          2026-10-14), no mesmo contêiner pra acompanharem a altura dele. */}
      {showMap && selected && (
        <div
          className="pointer-events-none absolute inset-x-3 z-10 flex flex-col items-end gap-2"
          // Soma o safe-area-inset-bottom igual à nav (ver <nav> acima) — com
          // bottom fixo em 88px, no iPhone (home indicator ~34px) a nav subia
          // por cima do card e cortava a linha do endereço (bug 2026-09-23).
          style={{ bottom: "calc(88px + env(safe-area-inset-bottom))" }}
        >
          <MapQuickActions key={selected.id} v={selected} />
          <BottomSheetCard
            selected={selected}
            onOpen={() => setShowDetails(true)}
            distanceToUserMeters={distanceToSelectedMeters}
          />
        </div>
      )}

      {showShare && (
        <LocationShareSheet
          vehicles={vehicles}
          initialVehicleId={selected?.id ?? null}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Full details modal */}
      {showDetails && selected && (
        <VehicleDetails v={selected} vehicles={vehicles} onClose={() => setShowDetails(false)} />
      )}

      {showMeuPerfil && <MeuPerfilScreen onClose={() => setShowMeuPerfil(false)} />}
      {showIndicacoes && <ReferralsCenterScreen onClose={() => setShowIndicacoes(false)} />}
      {showVistoria && (
        <VehicleInspectionScreen vehicles={vehicles} onClose={() => setShowVistoria(false)} />
      )}
      {showManutencao && <ClientMaintenanceScreen vehicles={vehicles} onClose={() => setShowManutencao(false)} />}
      {showRegistrarAssistencia && (
        <RegistrarAssistenciaScreen onClose={() => setShowRegistrarAssistencia(false)} />
      )}
      {showMinhasAssistencias && (
        <MinhasAssistenciasScreen onClose={() => setShowMinhasAssistencias(false)} />
      )}
      <VehicleHistoryDeleteDialog
        vehicles={vehicles}
        open={showApagarHistorico}
        onOpenChange={setShowApagarHistorico}
      />
      <SubUsersManagerDialog
        vehicles={vehicles}
        open={showCriarUsuario}
        onOpenChange={setShowCriarUsuario}
      />
      {showFaturas && <FaturasScreen onClose={() => setShowFaturas(false)} />}
      {reportsVehicleId && (
        <RouteHistoryCenter
          vehicleId={reportsVehicleId}
          vehicles={vehicles}
          onClose={() => setReportsVehicleId(null)}
          compact
        />
      )}

      {/* Bottom tab bar — offset extra pelo safe-area-inset-bottom (faixa de
          gesto/home indicator no iOS) senão a barra fica parcialmente sob o
          system UI e o mapa (z-0) aparece por trás dela (bug relatado
          2026-08-31: "fica cortado a barra do menu e fica aparecendo o mapa
          no fundo"). Exige viewport-fit=cover no meta viewport (__root.tsx),
          senão env(safe-area-inset-bottom) sempre resolve pra 0. Continua
          z-20 — só precisa ficar acima do painel das abas não-mapa (agora
          z-10, ver acima) pra flutuar sobre um fundo sólido que cobre a tela
          inteira, sem depender de um recorte milimetricamente calculado no
          painel por baixo. NÃO subir mais que isso: os modais full-screen
          (Detalhes do veículo, Meu perfil etc.) usam z-40/z-50/z-[60] e
          precisam continuar acima da nav quando abertos. */}
      <nav
        className={cn(
          "absolute inset-x-3 z-20 flex items-center justify-around rounded-2xl border border-slate-800 bg-slate-900/95 px-1 py-1 shadow-2xl backdrop-blur",
          activeTab === "assistente" && aiKeyboard.open && "hidden",
        )}
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <TabItem
          emoji="🏠"
          label="Início"
          active={activeTab === "inicio"}
          onClick={() => goToTab("inicio")}
        />
        {menuAccess.mapa && (
          <TabItem
            emoji="🗺️"
            label="Mapa"
            active={activeTab === "mapa"}
            onClick={() => goToTab("mapa")}
            disabled={suspended}
          />
        )}
        {menuAccess.veiculos && (
          <TabItem
            emoji="🚗"
            label="Veículos"
            active={activeTab === "veiculos"}
            onClick={() => goToTab("veiculos")}
            disabled={suspended}
          />
        )}
        {menuAccess.eventos && (
          <TabItem
            emoji="🔔"
            label="Eventos"
            active={activeTab === "eventos"}
            onClick={() => goToTab("eventos")}
            disabled={suspended}
          />
        )}
        {aiAccess.data?.allowed && (
          <TabItem
            emoji="🤖"
            label="Assistente"
            active={activeTab === "assistente"}
            onClick={() => goToTab("assistente")}
            disabled={suspended}
          />
        )}
        {canSeeAgenda && (
          <TabItem
            emoji="📅"
            label="Agenda"
            active={false}
            onClick={() => {
              rememberAppTheme();
              void navigateToRoute({ to: "/agenda" });
            }}
            disabled={suspended}
          />
        )}
        <TabItem
          emoji="🙂"
          icon={<ProfileTabIcon traccarUserId={user?.id} />}
          label="Perfil"
          active={activeTab === "voce"}
          onClick={() => goToTab("voce")}
          disabled={suspended}
        />
      </nav>
    </div>
  );
}

// Card fixo no rodapé do mapa (prévia antes de abrir o drawer completo) —
// endereço vem do último lat/lng conhecido do veículo (bug relatado
// 2026-08-31: mostrava o nome do motorista no lugar do endereço). Continua
// mostrando o último endereço normalmente mesmo com o veículo offline, já
// que `selected.lat`/`selected.lng` guardam a última posição reportada, não
// somem quando o veículo sai do ar.
//
// Foto real do veículo (pedido 2026-09-11: "aparece apenas o ícone
// genérico do carro... quero que apareça a FOTO cadastrada") — antes este
// card só usava iconUrlFor(selected) (o ícone 3D genérico), nunca a foto
// cadastrada em vehicle_metadata, mesmo já existindo useVehicleImages
// (mesmo hook/endpoint em lote que o carrossel do Início e a lista de
// Veículos já usam, ver InicioPanel#vehicleImagesMap/carouselVehicles
// acima) — só não estava conectado aqui. Um só id (não a frota inteira),
// então é uma query pequena, e normalmente já vem quente do cache das
// outras abas que também chamam useVehicleImages. Sem foto cadastrada →
// cai no MESMO ícone genérico de antes, comportamento idêntico.
function BottomSheetCard({
  selected,
  onOpen,
  distanceToUserMeters,
}: {
  selected: Vehicle;
  onOpen: () => void;
  // Distância até a localização atual do usuário (Haversine local) — null
  // enquanto não há fix de GPS ainda (permissão não concedida ou aguardando
  // o primeiro watchPosition). Ver use-user-location.ts/geo-distance.ts.
  distanceToUserMeters: number | null;
}) {
  const address = useReverseGeocode(selected.lat, selected.lng, { stableKey: selected.id, priority: true });
  const { data: vehicleImagesMap } = useVehicleImages([selected.id]);
  const imgs = vehicleImagesMap?.[selected.id];
  const photoUrl = imgs ? resolveGpsDriveUrl(imgs["128"] ?? imgs.original) : undefined;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="pointer-events-auto flex w-full items-start gap-3 rounded-2xl bg-slate-900 px-4 pt-4 pb-3 text-left text-white shadow-2xl"
    >
      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-slate-700">
        <img
          src={photoUrl ?? iconUrlFor(selected)}
          alt=""
          className={cn(
            "absolute inset-0 h-full w-full",
            photoUrl ? "object-cover" : "object-contain p-1",
          )}
        />
        <span
          className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-slate-900"
          style={{ background: statusColorFor(selected) }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="truncate text-[15px] font-bold">
            {selected.name} <LicensePlate plate={selected.plate} size="xs" className="ml-1" />
          </div>
          <Zap className="h-4 w-4 text-orange-400" strokeWidth={2.5} />
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[13px]">
          <span className="font-semibold" style={{ color: statusColorFor(selected) }}>
            {statusText(selected)}
          </span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-300">{selected.speed} km/h</span>
        </div>
        <div className="mt-1 flex items-start gap-1 text-[12.5px] text-slate-200">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-400" />
          <span className="truncate">{address ?? "Buscando localização..."}</span>
        </div>
        {distanceToUserMeters != null && (
          <div className="mt-0.5 text-[12.5px] text-slate-400">
            {formatDistance(distanceToUserMeters)} de você
          </div>
        )}
      </div>
      <ChevronRight className="mt-2 h-5 w-5 shrink-0 text-slate-400" />
    </button>
  );
}

// Atalhos redondos acima do cartão do Mapa (pedido 2026-10-14, imagem de
// referência): vermelho = Bloquear, verde = Desbloquear, azul = Âncora.
// Cada um abre o MESMO popup da tela de detalhes — o de bloqueio já vem só
// com a ação do botão tocado. Sem permissão o botão nem aparece.
function MapQuickActions({ v }: { v: Vehicle }) {
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const canAnchor = usePermission("devices", "anchor");
  const { data: metadata } = useVehicleMetadata(v.id);
  const smsFallback = useBlockCommandSmsFallback(metadata?.protocol, metadata?.trackerModel);
  const block = useVehicleBlockAction(v, smsFallback);
  const { anchor } = useVehicleAnchor(Number(v.id));
  // Ação fica guardada depois de fechar, senão o popup some vazio na animação.
  const [blockAction, setBlockAction] = useState<"block" | "unblock">("block");
  const [blockOpen, setBlockOpen] = useState(false);
  const openBlock = (action: "block" | "unblock") => {
    setBlockAction(action);
    setBlockOpen(true);
  };
  const [anchorOpen, setAnchorOpen] = useState(false);

  if (!canLock && !canUnlock && !canAnchor) return null;
  const btn =
    "pointer-events-auto grid h-12 w-12 place-items-center rounded-full text-white shadow-lg ring-2 ring-white/20 transition active:scale-95 disabled:opacity-60";

  return (
    <>
      <div className="flex items-center gap-3">
        {canLock && (
          <button
            type="button"
            onClick={() => openBlock("block")}
            disabled={block.sending}
            className={cn(btn, "bg-red-500 shadow-red-500/40")}
            aria-label="Bloquear veículo"
            title="Bloquear veículo"
          >
            <Lock className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
        {canUnlock && (
          <button
            type="button"
            onClick={() => openBlock("unblock")}
            disabled={block.sending}
            className={cn(btn, "bg-green-600 shadow-green-600/40")}
            aria-label="Desbloquear veículo"
            title="Desbloquear veículo"
          >
            <Unlock className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
        {canAnchor && (
          <button
            type="button"
            onClick={() => setAnchorOpen(true)}
            className={cn(btn, "bg-blue-600 shadow-blue-600/40", anchor && "ring-4 ring-sky-300")}
            aria-label={anchor ? "Âncora ativa" : "Ativar âncora"}
            title={anchor ? "Âncora ativa" : "Ativar âncora"}
          >
            <Anchor className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
      </div>

      <VehicleBlockConfirmDialog
        open={blockOpen}
        onOpenChange={setBlockOpen}
        vehicleName={v.name}
        vehiclePlate={v.plate}
        canLock={canLock && blockAction === "block"}
        canUnlock={canUnlock && blockAction === "unblock"}
        sending={block.sending}
        onConfirm={(action) => void block.send(action)}
      />

      {anchorOpen && (
        <VehicleAnchorDialog
          vehicle={{ ...v, offline: v.status === "offline" }}
          open={anchorOpen}
          onClose={() => setAnchorOpen(false)}
        />
      )}
    </>
  );
}

// Envio real de bloquear/desbloquear (GPRS com fallback SMS, recusa se
// offline, toasts e log) — compartilhado entre a tela de detalhes do
// veículo e o atalho "Bloqueio veicular" da Início (pedido 2026-09-22:
// "quando clicar em bloquear no botão rápido tem que abrir o popup de
// bloquear e não ir para a tela de detalhes").
export function useVehicleBlockAction(
  v: Vehicle | null,
  smsFallback: ReturnType<typeof useBlockCommandSmsFallback>,
  onSent?: (action: "block" | "unblock") => void,
) {
  const [sending, setSending] = useState(false);
  const send = async (action: "block" | "unblock") => {
    if (!v) return;
    const next = action;
    // Pedido 2026-09-04 (mesmo tratamento do painel desktop,
    // routes/index.tsx#sendBlockCommand): veículo offline não tenta o
    // comando por GPRS — Traccar aceitaria e só enfileiraria, sem avisar
    // ninguém que nada aconteceu de verdade ainda. Desde 2026-09-06 esse
    // caso vai direto pro fallback SMS (se configurado) em vez de só erro.
    const offline = v.status === "offline";
    setSending(true);
    try {
      const outcome = await sendBlockCommandWithSmsFallback({
        vehicleId: v.id,
        action: next,
        offline,
        fallback: smsFallback,
      });
      if (outcome.channel === "none") {
        if (outcome.reason === "offline") {
          toast.error(
            next === "block" ? "Não foi possível bloquear" : "Não foi possível desbloquear",
            {
              description: `${v.name} · ${v.plate} — veículo offline (sem comunicação com o rastreador agora).`,
            },
          );
          logActivity({
            type: "ERROR",
            message: `Comando "${next}" recusado — ${v.name} (${v.plate}) está offline`,
            metadata: { vehicleId: v.id, commandType: next, reason: "device_offline" },
          });
        } else {
          toast.error(
            next === "block" ? "Falha ao bloquear o veículo" : "Falha ao desbloquear o veículo",
            { description: outcome.error instanceof Error ? outcome.error.message : undefined },
          );
        }
        return;
      }
      onSent?.(next);
      if (outcome.channel === "sms") {
        toast.success(
          next === "block"
            ? "Comando de bloqueio enviado por SMS"
            : "Comando de desbloqueio enviado por SMS",
          {
            description: smsFallback.smsOnly
              ? `${v.name} · ${v.plate} — aguardando o rastreador confirmar`
              : `${v.name} · ${v.plate} — enviado via SMS (GPRS indisponível)`,
          },
        );
      } else {
        toast.success(
          next === "block" ? "Comando de bloqueio enviado" : "Comando de desbloqueio enviado",
          { description: `${v.name} · ${v.plate} — aguardando confirmação do rastreador` },
        );
      }
    } catch (err) {
      toast.error(
        next === "block" ? "Falha ao bloquear o veículo" : "Falha ao desbloquear o veículo",
        { description: err instanceof Error ? err.message : undefined },
      );
    } finally {
      setSending(false);
    }
  };

  return { sending, send };
}

function VehicleDetails({
  v,
  vehicles,
  onClose,
}: {
  v: Vehicle;
  vehicles: Vehicle[];
  onClose: () => void;
}) {
  // Mesma fonte de verdade do painel desktop (src/routes/index.tsx) e da aba
  // Sensores — nunca heurística por status, só o atributo real do rastreador
  // (ver src/lib/vehicle-state.ts).
  const ignition = ignitionState(v);
  // "Última comunicação há X min" (pedido 2026-10-19) — relógio de 30 s
  // pra o contador subir sozinho com a tela aberta.
  const nowTick = useNowTick();
  const blocked = isVehicleBlocked(v);
  const powered = isVehiclePowered(v);
  // Endereço real via geocodificação reversa (Nominatim), mesmo hook/cache
  // usado no Centro de Histórico — nunca texto fixo.
  const address = useReverseGeocode(v.lat, v.lng, { stableKey: v.id, priority: true });

  // Hero card virou um Street View de verdade (arrastável, como o
  // bonequinho amarelo do Google Maps) — pedido 2026-08-31: "quero aquele
  // mapa aonde da para ver a rua mexer na rua estilo quando tu coloca o
  // boneguinho na rua". Componente compartilhado com o painel desktop (ver
  // src/components/street-view-panorama.tsx) — ele decide sozinho a fonte
  // (só coleção oficial Google, nunca foto de estabelecimento) e cuida da
  // chave/carregamento da API.

  // Bloquear/desbloquear — mesmo comando real do drawer desktop (ver
  // routes/index.tsx#sendBlockCommand), só que com AlertDialog em vez de
  // window.confirm() (mesma migração já feita em routes/users.tsx — bug
  // relatado 2026-08-28: "tem que abrir um popup tem certeza que deseja
  // bloquear o veículo: nome do veículo"). pendingCommand nunca decide
  // sozinho o estado "bloqueado" — só reflete que um comando foi enviado e
  // ainda não confirmado pelo rastreador; quem manda é sempre v.blocked
  // (Traccar), por isso o efeito abaixo limpa pendingCommand assim que
  // v.blocked bater com o que foi pedido.
  // Gap real encontrado 2026-09-04: ao contrário do painel desktop
  // (routes/index.tsx#VehicleDetailSidebar), bloquear/desbloquear aqui no
  // PWA nunca checava devices.lock/devices.unlock — qualquer usuário logado
  // conseguia imobilizar o motor mesmo sem a permissão ativa. Corrigido
  // junto por ser a mesma área de código tocada pela Âncora abaixo.
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const canAnchor = usePermission("devices", "anchor");
  // Mesma permissão do botão "Enviar comando" do painel desktop
  // (routes/index.tsx) — faltava aqui no PWA (pedido 2026-09-07: "o botão
  // de enviar comando pop up").
  const canSendCommand = usePermission("commands", "send_command");
  const menuAccess = useAppMenuAccess();
  // Fallback SMS automático (pedido 2026-09-06) — ver
  // sendBlockCommandWithSmsFallback / routes/index.tsx#VehicleDetailSidebar.
  // Protocolo vem do cadastro GPS Drive (vehicle_metadata.protocol, pedido
  // 2026-09-07), nunca de v.protocol (auto-detectado pelo Traccar).
  const { data: vehicleMetadataForFallback } = useVehicleMetadata(v.id);
  const smsFallback = useBlockCommandSmsFallback(vehicleMetadataForFallback?.protocol, vehicleMetadataForFallback?.trackerModel);
  const { anchor } = useVehicleAnchor(Number(v.id));
  const [anchorDialogOpen, setAnchorDialogOpen] = useState(false);
  const [sendCommandOpen, setSendCommandOpen] = useState(false);
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showDeviceInfo, setShowDeviceInfo] = useState(false);
  // "Contatar motorista" nunca teve telefone real por trás (motorista é só
  // texto livre em v.driver, sem campo de contato) — trocado por
  // Informações do dispositivo (IMEI, SIM, modelo, instalação), visível só
  // pra quem tem a meta-permissão Administrador (pedido 2026-08-31: "isso
  // so administrator admin pode ver").
  const isAdmin = usePermission("administrador");

  // "Aguardando confirmação" compartilhado com o painel desktop e o popup
  // de comandos (lib/pending-block-commands.ts) — some quando v.blocked
  // bate com o pedido; equipamento só-SMS (J4) espera até confirmar.
  const pending = usePendingBlockCommand(v.id, blocked, (p) => {
    toast.message("Sem confirmação do rastreador ainda", {
      description:
        p.channel === "sms"
          ? "O SMS foi enviado, mas o rastreador ainda não confirmou."
          : "O comando foi enviado, mas o rastreador pode estar offline.",
    });
  });
  const pendingCommand = pending?.action ?? null;

  const { sending: sendingCommand, send: sendBlock } = useVehicleBlockAction(v, smsFallback);
  const handleConfirmBlock = (action: "block" | "unblock") => {
    setConfirmBlockOpen(false);
    void sendBlock(action);
  };

  // Link "perfeito" pro Google Maps (pedido 2026-09-07) — mesmo formato já
  // usado no painel desktop (routes/index.tsx#TabGeral), abre exatamente
  // naquele ponto (query=lat,lng), nunca um deep link antigo/heurístico.
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`;
  const handleCopyMapLink = async () => {
    try {
      await navigator.clipboard.writeText(mapUrl);
      toast.success("Link do Google Maps copiado", { description: `${v.name} · ${v.plate}` });
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  // Pedido 2026-09-16: o sheet de confirmação agora mostra os dois comandos
  // (Bloquear e Desbloquear) juntos, igual à referência visual — o rótulo da
  // linha que abre o sheet não pode mais assumir qual dos dois o usuário vai
  // escolher.
  const blockActionLabel = pendingCommand
    ? pending?.channel === "sms"
      ? "Aguardando confirmação (SMS)…"
      : "Aguardando confirmação…"
    : sendingCommand
      ? "Enviando…"
      : "Bloqueio do veículo";

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header — paddingTop soma a safe-area do topo, ver InicioPanel acima */}
      <div
        className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold text-slate-900 dark:text-slate-100">
            {v.name} <LicensePlate plate={v.plate} size="xs" className="ml-1" />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: statusColorFor(v) }} />
              <span className="font-semibold" style={{ color: statusColorFor(v) }}>
                {statusText(v)}
              </span>
            </span>
            {/* Pedido 2026-09-04: "o status tem que mudar âncora ativada" —
                mesmo raciocínio do painel desktop (routes/index.tsx): não
                substitui o status acima, é ortogonal (dá pra estar ancorado
                E offline ao mesmo tempo). */}
            {anchor && (
              <span className="flex items-center gap-1 font-semibold text-sky-600 dark:text-sky-400">
                <span aria-hidden>⚓</span> Âncora ativada
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Hero card — Street View de verdade (arrastável), não mais a foto
            do veículo (ver comentário acima). Overlay (nome do motorista)
            usa pointer-events-none pra não atrapalhar o arrastar dentro do
            painorama. */}
        <div className="relative h-48 w-full overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow-lg">
          <StreetViewPanorama
            lat={v.lat}
            lng={v.lng}
            heading={v.bearing}
            className="h-full w-full"
            fallback={
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-900 to-slate-700">
                <img
                  src={iconUrlFor(v)}
                  alt=""
                  className="h-20 w-20 object-contain opacity-90 drop-shadow-lg"
                />
                <span className="text-xs font-medium text-slate-300">
                  Sem imagem de rua disponível aqui
                </span>
              </div>
            }
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-transparent to-transparent p-3 pt-8">
            <div className="flex items-center gap-1 text-[12px] text-slate-100">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-rose-300" />
              <span className="truncate">{v.driver}</span>
            </div>
          </div>
        </div>

        {/* Endereço atual — geocodificação reversa real (Nominatim), sem texto fixo */}
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-white px-3 py-3 shadow-sm dark:bg-slate-900">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Endereço
            </div>
            <div className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-100">
              {address ?? "Buscando endereço..."}
            </div>
          </div>
          {/* Pedido 2026-09-07: "icone de mapa pra poder copiar a localização,
              link do google maps perfeitamente" — copia o link exato (não abre
              direto, pra poder colar em WhatsApp/etc.), mesmo formato do
              painel desktop. */}
          <button
            type="button"
            onClick={handleCopyMapLink}
            title="Copiar link do Google Maps"
            aria-label="Copiar link do Google Maps"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400"
          >
            <MapIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Stats grid — Velocidade mora aqui agora, saiu do hero (que virou
            só a foto do veículo). Mesmo par ícone/label do painel desktop
            (routes/index.tsx#visaoGeral): Gauge = Velocidade, Activity =
            Odômetro. */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <StatCard
            icon={<Gauge className="h-4 w-4" />}
            label="Velocidade"
            value={`${Math.round(v.speed ?? 0)} km/h`}
          />
          <StatCard
            icon={<Activity className="h-4 w-4" />}
            label="Odômetro"
            value={v.odometer != null ? `${v.odometer.toLocaleString("pt-BR")} km` : "—"}
          />
          <StatCard
            icon={
              <Zap
                className={cn(
                  "h-4 w-4",
                  ignition === "on"
                    ? "text-orange-500"
                    : ignition === "off"
                      ? "text-slate-400"
                      : "text-slate-300",
                )}
              />
            }
            label="Ignição"
            value={ignitionLabel(ignition)}
          />
          <StatCard
            icon={
              blocked ? (
                <Lock className="h-4 w-4 text-rose-500" />
              ) : (
                <Unlock className="h-4 w-4 text-emerald-500" />
              )
            }
            label="Bloqueio"
            value={blocked ? "Bloqueado" : "Liberado"}
          />
          <StatCard
            icon={<Battery className="h-4 w-4" />}
            label="Bateria (rastreador)"
            value={
              v.batteryLevel != null
                ? `${Math.round(v.batteryLevel)}%`
                : v.batteryLow
                  ? "Baixa"
                  : "—"
            }
          />
          {/* Voltímetro — tensão real do veículo (fio de alimentação do
              rastreador), NÃO a bateria interna do rastreador acima. Bug
              relatado 2026-09-09: essa tela só mostrava "Bateria" (do
              rastreador), sem nenhum jeito de ver a tensão real do veículo
              — já existia no painel desktop ("Voltímetro", ver
              routes/index.tsx), só faltava aqui. v.powerVoltage vem de
              attributes.power (nunca attributes.battery, que nenhum
              protocolo em uso aqui envia — mesma fonte já usada no card
              "Bateria" do resumo do Início, ver mais abaixo neste arquivo). */}
          <StatCard
            icon={<BatteryLow className="h-4 w-4" />}
            label="Voltímetro (veículo)"
            value={v.powerVoltage != null ? `${v.powerVoltage.toFixed(1)} V` : "—"}
          />
          <StatCard
            icon={
              <Radio className={cn("h-4 w-4", powered ? "text-emerald-500" : "text-rose-500")} />
            }
            label="Alimentação"
            value={powered ? "Ligada" : "Desligada"}
          />
          <StatCard
            icon={
              <Signal className={cn("h-4 w-4", v.status === "offline" ? "text-rose-500" : "text-emerald-500")} />
            }
            label="Última comunicação"
            value={timeWithAgo(v.lastConnection, nowTick)}
          />
          <StatCard
            icon={<MapPin className="h-4 w-4 text-sky-500" />}
            label="Última posição"
            value={timeWithAgo(v.positionTimestamp ?? v.lastConnection, nowTick)}
          />
        </div>

        {/* Actions — Bloquear/Desbloquear primeiro de propósito (pedido
            2026-08-28: "tem que ser a primeira opção") */}
        {/* Pedido 2026-09-08: sem permissão a linha some, não fica só
            desabilitada — mesmo ajuste feito no painel desktop
            (routes/index.tsx#VehicleDetailSidebar). */}
        <div className="mt-4 space-y-2">
          {(canLock || canUnlock) && (
            <ActionRow
              emoji={blocked ? "🔓" : "🔒"}
              label={blockActionLabel}
              danger={!blocked}
              disabled={sendingCommand || (!!pending && pending.channel === "gprs")}
              onClick={() => setConfirmBlockOpen(true)}
            />
          )}
          {canAnchor && (
            <ActionRow
              emoji="⚓"
              label={anchor ? "Âncora ativa" : "Ativar âncora"}
              onClick={() => setAnchorDialogOpen(true)}
            />
          )}
          {canSendCommand && (
            <ActionRow
              emoji="📡"
              icon={<Send className="h-4 w-4" />}
              label="Enviar comando"
              onClick={() => setSendCommandOpen(true)}
            />
          )}
          {menuAccess.mapa && <ActionRow emoji="📍" label="Ver no mapa" onClick={onClose} />}
          {menuAccess.historico && (
            <ActionRow
              emoji="🛣️"
              label="Histórico de rotas"
              icon={<RouteIcon className="h-4 w-4" />}
              onClick={() => setShowHistory(true)}
            />
          )}
          <ActionRow
            emoji="🔔"
            label="Notificações"
            icon={<Bell className="h-4 w-4" />}
            onClick={() => setShowNotifications(true)}
          />
          {isAdmin && (
            <ActionRow
              emoji="🔧"
              label="Informações do dispositivo"
              icon={<Cpu className="h-4 w-4" />}
              onClick={() => setShowDeviceInfo(true)}
            />
          )}
        </div>
      </div>

      {/* Reskin pedido 2026-09-16 ("quero igual a esse visual") — extraído
          pra vehicle-block-confirm-dialog.tsx pra ser reaproveitado também
          no painel desktop e no atalho do popup "Enviar comando" (mesmo
          pedido, "no web tem que aparecer o popup também"). */}
      <VehicleBlockConfirmDialog
        open={confirmBlockOpen}
        onOpenChange={setConfirmBlockOpen}
        vehicleName={v.name}
        vehiclePlate={v.plate}
        canLock={canLock}
        canUnlock={canUnlock}
        sending={sendingCommand}
        onConfirm={handleConfirmBlock}
      />

      {anchorDialogOpen && (
        <VehicleAnchorDialog
          vehicle={{ ...v, offline: v.status === "offline" }}
          open={anchorDialogOpen}
          onClose={() => setAnchorDialogOpen(false)}
        />
      )}

      <VehicleSendCommandDialog
        vehicle={sendCommandOpen ? v : null}
        open={sendCommandOpen}
        onOpenChange={setSendCommandOpen}
      />

      {showHistory && (
        <RouteHistoryCenter
          vehicleId={v.id}
          vehicles={vehicles}
          onClose={() => setShowHistory(false)}
          compact
        />
      )}

      {showNotifications && (
        <MobileNotificationsPanel vehicle={v} onClose={() => setShowNotifications(false)} />
      )}

      {showDeviceInfo && (
        <VehicleDeviceInfoPanel vehicle={v} onClose={() => setShowDeviceInfo(false)} />
      )}
    </div>
  );
}

// Mesma lógica real do desktop (routes/index.tsx#TabAlertas), só com o
// visual do app (pedido 2026-08-28: "notificações é para ativar as
// notificações de cada coisa igual no web"). Dois sistemas por baixo,
// escolhidos por item do ALERT_CATALOG (nunca os dois juntos no mesmo
// item, ver alert-types.ts):
//   - item.alertRuleType (5 tipos): AlertRule real (Fase 3, push+som) via
//     .../rules/quick-toggle — pedido 2026-09-09 "cliente ativa a
//     notificação, já deve aparecer o alerta configurado automaticamente"
//     (reflete regra já criada pelo admin, e o toggle daqui cria uma
//     regra de verdade, não só liga um sino no Traccar).
//   - item.traccarTypes sem alertRuleType (só "perimeter-alert" hoje):
//     sistema nativo do Traccar de antes, sem push/som customizado — fica
//     assim porque geofence exige escolher UMA cerca específica, que este
//     toggle genérico não pede.
function MobileNotificationsPanel({ vehicle, onClose }: { vehicle: Vehicle; onClose: () => void }) {
  const deviceId = Number(vehicle.id);
  const notifQuery = useDeviceNotifications(deviceId);
  const toggleMutation = useToggleDeviceNotification(deviceId);
  const ruleQuery = useQuickToggleRules(deviceId);
  const ruleToggleMutation = useQuickToggleRule(deviceId);
  const [pendingType, setPendingType] = useState<string | null>(null);

  const enabledTypes = useMemo(
    () => new Set((notifQuery.data ?? []).map((n) => n.type)),
    [notifQuery.data],
  );
  const enabledRuleTypes = useMemo(
    () => new Set((ruleQuery.data ?? []).filter((r) => r.enabled).map((r) => r.type)),
    [ruleQuery.data],
  );

  const ruleByType = useMemo(
    () =>
      new Map((ruleQuery.data ?? []).filter((r) => r.geofenceId === null).map((r) => [r.type, r])),
    [ruleQuery.data],
  );

  const handleToggle = (type: string, next: boolean) => {
    setPendingType(type);
    toggleMutation.mutate(
      { type, enabled: next },
      {
        onSuccess: () =>
          toast.success(next ? "Alerta ativado" : "Alerta desativado", {
            description: ALERT_TYPE_META[type]?.label ?? type,
          }),
        onError: (err) =>
          toast.error("Não foi possível salvar o alerta", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  const handleLimitsSave = (
    item: (typeof ALERT_CATALOG)[number],
    config: AlertRuleConfig,
    actionKind?: "notify" | "block",
  ) => {
    setPendingType(item.key);
    ruleToggleMutation.mutate(
      { type: item.alertRuleType!, enabled: true, config, ...(actionKind ? { actionKind } : {}) },
      {
        onSuccess: () => toast.success("Limites salvos", { description: item.label }),
        onError: (err) =>
          toast.error("Não foi possível salvar os limites", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  const handleRuleToggle = (item: (typeof ALERT_CATALOG)[number], next: boolean) => {
    setPendingType(item.key);
    ruleToggleMutation.mutate(
      { type: item.alertRuleType!, enabled: next },
      {
        onSuccess: () =>
          toast.success(next ? "Alerta ativado" : "Alerta desativado", {
            description: item.label,
          }),
        onError: (err) =>
          toast.error("Não foi possível salvar o alerta", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  const loading = notifQuery.isLoading || ruleQuery.isLoading;
  const failed = notifQuery.isError || ruleQuery.isError;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* paddingTop soma a safe-area do topo, ver InicioPanel acima */}
      <div
        className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100">
            Notificações
          </div>
          <div className="truncate text-[12px] text-slate-500 dark:text-slate-400">
            {vehicle.name} <LicensePlate plate={vehicle.plate} size="xs" className="ml-1" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-14 w-full animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : failed ? (
          <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            Não foi possível carregar os alertas deste veículo. Tente novamente em instantes.
          </div>
        ) : (
          (["meus-alertas", "avancados"] as const).map((groupKey) => (
            <div key={groupKey} className="mt-4 first:mt-0">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {groupKey === "meus-alertas" ? "Meus alertas" : "Alertas Avançados"}
              </h2>
              <ul className="mt-2 space-y-2">
                {ALERT_CATALOG.filter((item) => item.group === groupKey).map((item) => {
                  const togglable = !!item.alertRuleType || !!item.traccarTypes;
                  const on = item.alertRuleType
                    ? enabledRuleTypes.has(item.alertRuleType)
                    : (item.traccarTypes?.every((t) => enabledTypes.has(t)) ?? false);
                  const saving = item.alertRuleType
                    ? pendingType === item.key && ruleToggleMutation.isPending
                    : !!item.traccarTypes?.some((t) => pendingType === t) &&
                      toggleMutation.isPending;
                  return (
                    <li
                      key={item.key}
                      className="flex flex-wrap items-center gap-3 rounded-xl bg-white px-3 py-2.5 shadow-sm dark:bg-slate-900"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-base dark:border-slate-700 dark:bg-slate-800">
                        {item.emoji}
                      </span>
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                          {item.label}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-slate-500 dark:text-slate-400">
                          {togglable
                            ? on
                              ? [
                                  "Ativado",
                                  limitSummary(
                                    item.alertRuleType,
                                    ruleByType.get(item.alertRuleType!)?.config,
                                  ),
                                ]
                                  .filter(Boolean)
                                  .join(" · ")
                              : "Desativado"
                            : item.description}
                        </span>
                      </div>
                      {item.alertRuleType ? (
                        <Switch
                          checked={on}
                          disabled={saving}
                          onCheckedChange={(checked) => handleRuleToggle(item, checked)}
                          aria-label={item.label}
                        />
                      ) : item.traccarTypes ? (
                        <Switch
                          checked={on}
                          disabled={saving}
                          onCheckedChange={(checked) =>
                            item.traccarTypes!.forEach((t) => handleToggle(t, checked))
                          }
                          aria-label={item.label}
                        />
                      ) : (
                        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                          Em breve
                        </span>
                      )}
                      {on && isLimitEditable(item.alertRuleType) && (
                        <AlertLimitsEditor
                          key={JSON.stringify(ruleByType.get(item.alertRuleType!)?.config ?? null)}
                          type={item.alertRuleType!}
                          rule={ruleByType.get(item.alertRuleType!)}
                          saving={saving}
                          onSave={(config, actionKind) =>
                            handleLimitsSave(item, config, actionKind)
                          }
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Ficha técnica do dispositivo (IMEI + cadastro real em vehicle_metadata —
// mesmos campos das abas Operadora/Modelo do Rastreador/Local da Instalação
// do painel desktop, ver TabHardware em routes/index.tsx). Só entra na
// árvore quando isAdmin já é true (ver VehicleDetails), mas checa de novo
// aqui como segunda barreira — nunca confia só no botão estar escondido.
function VehicleDeviceInfoPanel({ vehicle, onClose }: { vehicle: Vehicle; onClose: () => void }) {
  const isAdmin = usePermission("administrador");
  const { data: metadata, isLoading } = useVehicleMetadata(isAdmin ? vehicle.id : undefined);
  // Plano (Administração > Planos) e usuário(s) vinculado(s) — pedido
  // 2026-08-31: "tem que aparecer qual plano é desse veículo, o nome do
  // usuário que está vinculado". Mesmas fontes reais já usadas na aba
  // Principal do formulário desktop (routes/index.tsx#TabPrincipal):
  // metadata.planId (cadastro manual, nunca deduzido de uma Recorrência
  // Asaas — ver comentário em UpdateVehicleMetadataInput) e useVehicleUsers
  // (vínculo real via GPS Drive Backend → Traccar). Sem risco de 403 aqui:
  // este painel já só existe pra quem é isAdmin (bypassa a permissão de
  // Planos no backend também).
  const { data: plans } = usePlans(isAdmin);
  const { data: linkedUsers } = useVehicleUsers(isAdmin ? Number(vehicle.id) : undefined);
  const planName = metadata?.planId
    ? plans?.find((p) => p.id === metadata.planId)?.name
    : undefined;
  const linkedUserNames =
    linkedUsers && linkedUsers.length > 0 ? linkedUsers.map((u) => u.name).join(", ") : undefined;

  const rows: { label: string; value: string | null | undefined }[] = [
    { label: "Plano", value: planName },
    { label: "Usuário vinculado", value: linkedUserNames },
    { label: "IMEI", value: vehicle.imei },
    { label: "Fabricante do rastreador", value: metadata?.trackerBrand },
    { label: "Modelo do rastreador", value: metadata?.trackerModel },
    { label: "Fornecedor", value: metadata?.trackerSupplier },
    { label: "Operadora", value: metadata?.simCarrier },
    { label: "Número SIM", value: metadata?.simNumber },
    { label: "ICCID", value: metadata?.simIccid },
    { label: "APN", value: metadata?.simApn },
    { label: "Empresa da instalação", value: metadata?.installCompany },
    { label: "Local da instalação", value: metadata?.installLocation },
    { label: "Data de instalação", value: metadata?.installedAt },
    { label: "Instalador", value: metadata?.installTechnician },
    { label: "Garantia", value: metadata?.installWarranty },
    { label: "Observações", value: metadata?.installNotes },
  ];

  if (!isAdmin) return null;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* paddingTop soma a safe-area do topo, ver InicioPanel acima */}
      <div
        className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100">
            Informações do dispositivo
          </div>
          <div className="truncate text-[12px] text-slate-500 dark:text-slate-400">
            {vehicle.name} <LicensePlate plate={vehicle.plate} size="xs" className="ml-1" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <p className="mb-3 text-[11px] text-slate-500 dark:text-slate-400">
          Toque em qualquer linha para copiar o valor.
        </p>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-14 w-full animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <DeviceInfoRow key={row.label} label={row.label} value={row.value} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DeviceInfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  const hasValue = !!value;
  const handleCopy = async () => {
    if (!hasValue) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copiado", { description: label });
    } catch {
      toast.error("Não foi possível copiar");
    }
  };
  return (
    <li>
      <button
        type="button"
        onClick={handleCopy}
        disabled={!hasValue}
        className="flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-left shadow-sm disabled:opacity-60 dark:bg-slate-900"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </div>
          <div className="mt-0.5 truncate font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
            {value || "—"}
          </div>
        </div>
        {hasValue && <Copy className="h-4 w-4 shrink-0 text-slate-400" />}
      </button>
    </li>
  );
}


// Limite máximo (px) do logotipo do topo — mesmo valor do slider em
// Administração > Identidade Visual (admin.identity.tsx, APP_LOGO_MAX).
// Só clampa um valor salvo fora do range atual (ex.: era 96 antes de virar
// 180) — a barra em si não depende mais disso, ver InicioTab.
const APP_LOGO_MAX = 180;

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-slate-900">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-sm font-bold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function ActionRow({
  emoji,
  label,
  onClick,
  danger,
  disabled,
  title,
}: {
  emoji: string;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-left text-sm font-medium shadow-sm disabled:opacity-60 dark:bg-slate-900",
        danger ? "text-rose-600 dark:text-rose-400" : "text-slate-800 dark:text-slate-100",
      )}
    >
      <span className="text-lg leading-none">{emoji}</span>
      <span className="flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </button>
  );
}

// Duração curta (ex.: "2h 15min") pro "Resumo do seu veículo" do Início —
// mesma fórmula de RouteHistoryCenter#formatDuration, mas essa vive num
// componente diferente e não vale a pena compartilhar através de um import
// só por causa disso.
export function formatShortDuration(ms: number): string {
  if (!ms || ms < 0) return "0min";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${m > 0 ? ` ${m}min` : ""}`;
}

function ProfileAvatarCircle({ traccarUserId }: { traccarUserId: number | undefined }) {
  const { data: avatarData } = useUserAvatar(traccarUserId);
  const avatarUrl = avatarData?.imageUrls
    ? resolveGpsDriveUrl(avatarData.imageUrls["128"] ?? avatarData.imageUrls.original)
    : undefined;
  return (
    <span className="grid h-14 w-14 place-items-center overflow-hidden rounded-full border-2 border-primary bg-slate-100 text-xl dark:bg-slate-800">
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span>🙂</span>
      )}
    </span>
  );
}

// Fundo do bloco = cor da empresa no modo claro (pedido 2026-09-01: "coloca
// nas cores da empresa"; modo escuro deixado como estava — "no fundo escuro
// deixa assim por enquanto"). Ícone branco em cima da cor cheia (pedido: "os
// ícones coloca branco") — o círculo vira um chip translúcido branco em vez
// do bg-primary/10 de antes (que ficaria com a mesma cor do bloco e
// sumiria); no escuro o chip volta a ser bg-primary/10 + ícone na cor da
// empresa, como já estava.
// Pré-visualização do tema do clima — `null` = clima real (produção).
// Pra testar um visual, troque por "sol" | "nuvem" | "chuva" |
// "tempestade" | "noite": força o tema pra TODOS os usuários da Início.
const WEATHER_PREVIEW: WeatherBucket | null = null;

// Céu atrás do topo da Início, conforme o clima (referências do usuário
// 2026-09-22; "deixa mais bonito o fundo"). Só CSS/SVG inline — nada pra
// baixar: nuvens fofas em SVG com volume (degradê + sombra), brilho/raios
// de sol, lua com halo e estrelas piscando à noite. Animações leves em
// styles.css (.sky-*), desligadas com prefers-reduced-motion. Fica atrás de
// todo o conteúdo (-z-10 dentro do `isolate` do container da Início) e não
// recebe toque.
const SKY_STARS: ReadonlyArray<readonly [number, number, number]> = [
  [6, 18, 1.4], [14, 60, 1], [21, 28, 1.8], [29, 96, 1], [34, 12, 1.2], [42, 70, 1.6],
  [49, 34, 1], [55, 110, 1.2], [61, 20, 1], [68, 82, 1.8], [75, 44, 1], [81, 128, 1],
  [87, 16, 1.4], [93, 62, 1], [9, 140, 1], [24, 170, 1.2], [38, 150, 1], [52, 190, 1],
  [64, 160, 1.4], [78, 200, 1], [90, 176, 1.2], [3, 230, 1], [31, 250, 1], [58, 240, 1.2],
  [72, 270, 1], [96, 250, 1], [17, 320, 1], [45, 340, 1.2], [84, 330, 1], [66, 380, 1],
  [11, 420, 1.2], [37, 460, 1], [89, 440, 1], [53, 520, 1], [22, 560, 1.2], [76, 600, 1],
];

// Nuvem "fofa": união de círculos + base achatada, preenchida com degradê
// vertical (topo claro → base sombreada) e borda levemente desfocada.
function SkyCloud({
  top,
  bottom,
  opacity = 1,
  blur = 1.2,
  className,
  style,
}: {
  top: string;
  bottom: string;
  opacity?: number;
  blur?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      viewBox="0 0 220 90"
      className={cn("absolute", className)}
      style={{ opacity, ...style }}
      aria-hidden
    >
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={top} />
          <stop offset="100%" stopColor={bottom} />
        </linearGradient>
        <filter id={`f${id}`} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation={blur} />
        </filter>
      </defs>
      <g fill={`url(#g${id})`} filter={`url(#f${id})`}>
        <circle cx="48" cy="58" r="24" />
        <circle cx="82" cy="42" r="31" />
        <circle cx="122" cy="34" r="34" />
        <circle cx="160" cy="48" r="27" />
        <circle cx="188" cy="62" r="18" />
        <rect x="30" y="56" width="172" height="26" rx="13" />
      </g>
    </svg>
  );
}

// Chuva (pedido 2026-09-22 "coloca as chuvas mais bonitas" e depois
// "pingos reais" — ver rainTile acima;
// a 1ª, com 70 gotas animadas uma a uma, foi retirada por suspeita de
// travar a Início). Continua leve: 3 camadas de profundidade (longe/meio/
// perto), cada uma UM elemento com textura SVG de gotas repetida deslizando
// via transform (compositor, sem repaint); gotas com rastro em degradê.
// Por cima: névoa passando, alguns respingos no "chão" do céu e, em
// trovoada, um relâmpago de vez em quando — poucos elementos no total.
// Pingos com forma de gota de verdade (pedido 2026-09-22: "coloca a chuva
// com pingos reais"): cabeça redonda, ponta fina voltada pra cima/direita
// (contra o sentido da queda), preenchimento em degradê, reflexo branco e
// contorno azulado bem leve (refração) pra aparecer também sobre o céu claro.
function rainTile(
  size: number,
  count: number,
  w: number,
  len: number,
  seed: number,
  fill: string,
  edge: string,
) {
  let r = seed;
  const rnd = () => {
    r = (r * 9301 + 49297) % 233280;
    return r / 233280;
  };
  const drops: string[] = [];
  for (let i = 0; i < count; i++) {
    const s = 0.75 + rnd() * 0.5;
    const dw = w * s;
    const dl = len * s;
    const x = dw + rnd() * (size - dw * 2);
    const y = rnd() * size;
    const h = dw / 2;
    const path = `M0,${-dl} C${(dw * 0.12).toFixed(2)},${(-dl * 0.45).toFixed(2)} ${h},${(-h * 0.9).toFixed(2)} ${h},0 A${h},${h} 0 0 1 ${-h},0 C${-h},${(-h * 0.9).toFixed(2)} ${(-dw * 0.12).toFixed(2)},${(-dl * 0.45).toFixed(2)} 0,${-dl} Z`;
    const drop = `<path d='${path}' fill='url(#f)' stroke='${edge}' stroke-width='0.5'/><ellipse cx='${(-h * 0.35).toFixed(2)}' cy='${(-h * 0.1).toFixed(2)}' rx='${(h * 0.28).toFixed(2)}' ry='${(h * 0.5).toFixed(2)}' fill='white' fill-opacity='0.9'/>`;
    // Cópia no topo quando a gota passa da borda de baixo — textura emenda.
    for (const dy of y + h > size ? [0, -size] : y - dl < 0 ? [0, size] : [0]) {
      drops.push(`<g transform='translate(${x.toFixed(1)},${(y + dy).toFixed(1)}) rotate(12)'>${drop}</g>`);
    }
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'><defs><linearGradient id='f' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='${fill}' stop-opacity='0.05'/><stop offset='0.6' stop-color='${fill}' stop-opacity='0.55'/><stop offset='1' stop-color='${fill}' stop-opacity='0.95'/></linearGradient></defs>${drops.join("")}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

const RAIN_SPLASHES = [
  [8, 0.0], [21, 0.55], [34, 0.2], [47, 0.8], [60, 0.35], [73, 0.65], [86, 0.1], [94, 0.45],
] as const;

function RainLayer({
  color,
  storm,
  heavy = false,
  splashTop,
}: {
  color: string;
  storm: boolean;
  // Tempestade: mais pingos por camada.
  heavy?: boolean;
  splashTop?: number;
}) {
  const tiles = useMemo(() => {
    const edge = "rgba(40,80,130,0.35)";
    const k = heavy ? 1.7 : 1;
    return {
      far: rainTile(70, Math.round(10 * k), 1.8, 6, 7, color, edge),
      mid: rainTile(110, Math.round(8 * k), 2.8, 10, 31, color, edge),
      near: rainTile(170, Math.round(6 * k), 4.4, 16, 97, color, edge),
    };
  }, [color, heavy]);
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div
        className="sky-rain-far absolute -left-8 -right-8 -top-[70px] bottom-0 opacity-60"
        style={{ backgroundImage: tiles.far, backgroundSize: "70px 70px" }}
      />
      <div
        className="sky-rain-mid absolute -left-8 -right-8 -top-[110px] bottom-0 opacity-80"
        style={{ backgroundImage: tiles.mid, backgroundSize: "110px 110px" }}
      />
      <div
        className="sky-rain-near absolute -left-10 -right-10 -top-[170px] bottom-0"
        style={{ backgroundImage: tiles.near, backgroundSize: "170px 170px" }}
      />
      {/* Névoa atravessando devagar */}
      <span
        className="sky-cross absolute top-[150px] h-16 w-[70%] rounded-full bg-white/25 blur-2xl"
        style={{ "--sky-dur": "38s", "--sky-delay": "-12s" } as React.CSSProperties}
      />
      <span
        className="sky-cross absolute top-[60px] h-12 w-[55%] rounded-full bg-white/15 blur-2xl"
        style={{ "--sky-dur": "52s", "--sky-delay": "-35s" } as React.CSSProperties}
      />
      {/* Respingos (só onde existe um "chão" visível — céu do dia) */}
      {splashTop !== undefined &&
        RAIN_SPLASHES.map(([x, delay], i) => (
          <span
            key={i}
            className="sky-splash absolute h-[5px] w-4 rounded-[50%] border border-white/80"
            style={{
              left: `${x}%`,
              top: splashTop + ((i * 23) % 36),
              animationDelay: `${delay}s`,
            }}
          />
        ))}
      {/* Relâmpago: clarão duplo rápido + raio visível. Dois raios em
          ciclos diferentes (9s e 6,5s) pra não ficar um padrão repetitivo. */}
      {storm && (
        <>
          <div className="sky-lightning absolute inset-0 bg-[#dfe8ff]" />
          <div
            className="sky-lightning absolute inset-0 bg-[#dfe8ff]"
            style={{ animationDuration: "6.5s", animationDelay: "-2.7s" }}
          />
          <SkyBolt className="sky-bolt left-[14%] top-0 h-56 w-24" />
          <SkyBolt
            className="sky-bolt right-[8%] top-6 h-44 w-20 -scale-x-100"
            style={{ animationDuration: "6.5s", animationDelay: "-2.7s" }}
          />
        </>
      )}
    </div>
  );
}

// Raio em zigue-zague com ramificações; brilho via drop-shadow. Aparece
// só no instante do clarão (.sky-bolt usa o mesmo ciclo de .sky-lightning).
function SkyBolt({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      viewBox="0 0 100 240"
      preserveAspectRatio="none"
      className={cn("absolute", className)}
      style={style}
      aria-hidden
    >
      <g
        fill="none"
        stroke="#ffffff"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: "drop-shadow(0 0 4px #b9ccff) drop-shadow(0 0 10px #7f9cff)" }}
      >
        <path
          d="M62 0 L50 38 L60 44 L38 92 L52 98 L30 150 L42 156 L22 206 L28 240"
          strokeWidth="3"
        />
        <path d="M50 38 L32 62 L36 70 L24 92" strokeWidth="1.6" />
        <path d="M38 92 L62 118 L58 128 L74 150" strokeWidth="1.4" />
        <path d="M30 150 L12 170 L16 178 L4 196" strokeWidth="1.2" />
      </g>
    </svg>
  );
}

export function InicioSky({
  bucket,
  raining,
  storm,
}: {
  bucket: WeatherBucket;
  raining: boolean;
  storm: boolean;
}) {
  if (bucket === "tempestade") {
    return (
      <div
        aria-hidden
        // Só a faixa do topo (como chuva/nublado), esmaecendo pro branco; no
        // modo escuro do PWA fica escondida, igual aos outros céus claros.
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[330px] overflow-hidden [container-type:inline-size] dark:hidden"
        style={{
          background:
            "linear-gradient(180deg, #151b26 0%, #273142 15%, #4a5567 28%, #8591a2 40%, #bcc4ce 50%, #e4e8ed 62%, #f7f8fa 78%, #ffffff 100%)",
        }}
      >
        {/* Nuvens pesadas em camadas — topo levemente iluminado */}
        <SkyCloud
          top="#4a5566"
          bottom="#1c2330"
          blur={3}
          className="sky-cross -top-4 w-[26rem]"
          style={{ "--sky-dur": "60s", "--sky-delay": "-15s" } as React.CSSProperties}
        />
        <SkyCloud
          top="#566276"
          bottom="#232b39"
          blur={3}
          className="sky-cross top-[20px] w-80"
          style={{ "--sky-dur": "48s", "--sky-delay": "-38s" } as React.CSSProperties}
        />
        <SkyCloud
          top="#3f4958"
          bottom="#1a212c"
          opacity={0.95}
          blur={2.5}
          className="sky-cross top-[90px] w-96"
          style={{ "--sky-dur": "42s", "--sky-delay": "-5s" } as React.CSSProperties}
        />
        <SkyCloud
          top="#5b6778"
          bottom="#262e3b"
          opacity={0.85}
          blur={3}
          className="sky-cross top-[150px] w-72"
          style={{ "--sky-dur": "55s", "--sky-delay": "-28s" } as React.CSSProperties}
        />
        <RainLayer color="#cfdcf5" storm heavy splashTop={236} />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
      </div>
    );
  }

  if (bucket === "noite") {
    return (
      <div
        aria-hidden
        // Só a faixa do topo esmaecendo pro branco, igual tempestade (pedido
        // 2026-10-21: "quero só o banner lá de cima com o céu escuro e o
        // fundo fica branco como padrão" — antes a tela inteira virava
        // azul-marinho e sobrava faixa vazia em celular de tela alta).
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[330px] overflow-hidden [container-type:inline-size] dark:hidden"
        style={{
          background:
            "linear-gradient(180deg, #040b22 0%, #0a1a40 15%, #132a5c 28%, #3f5486 40%, #9aa9cc 50%, #dfe4f0 62%, #f6f7fb 78%, #ffffff 100%)",
        }}
      >
        {/* Faixa de "via láctea" bem sutil na diagonal */}
        <div
          className="absolute -left-1/4 top-6 h-40 w-[150%] -rotate-12 opacity-40 blur-2xl"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(147,170,255,0.25) 35%, rgba(200,180,255,0.18) 60%, transparent)",
          }}
        />
        {/* Halo da lua atrás do card de clima (canto superior direito) */}
        <div
          className="absolute -right-16 top-10 h-64 w-64 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,244,214,0.28) 0%, rgba(160,180,255,0.12) 38%, transparent 70%)",
          }}
        />
        {SKY_STARS.filter(([, y]) => y < 160).map(([x, y, r], i) => (
          <span
            key={i}
            className={cn("absolute rounded-full bg-white", i % 3 === 0 && "sky-twinkle")}
            style={
              {
                left: `${x}%`,
                top: y,
                width: r * 2,
                height: r * 2,
                opacity: 0.45 + (i % 4) * 0.15,
                boxShadow: r > 1.3 ? "0 0 6px 1px rgba(255,255,255,0.6)" : undefined,
                "--sky-o": 0.45 + (i % 4) * 0.15,
                "--sky-dur": `${2.5 + (i % 5)}s`,
                animationDelay: `${(i % 7) * 0.6}s`,
              } as React.CSSProperties
            }
          />
        ))}
        {/* Nuvens noturnas translúcidas, iluminadas de cima pela lua */}
        <SkyCloud
          top="rgba(120,140,190,0.45)"
          bottom="rgba(30,45,90,0.2)"
          blur={3}
          className="sky-cross top-[120px] w-64"
          style={{ "--sky-dur": "70s", "--sky-delay": "-10s" } as React.CSSProperties}
        />
        <SkyCloud
          top="rgba(140,160,210,0.4)"
          bottom="rgba(30,45,90,0.15)"
          blur={3}
          className="sky-cross top-[170px] w-72"
          style={{ "--sky-dur": "95s", "--sky-delay": "-55s" } as React.CSSProperties}
        />
        {raining && <RainLayer color="#d6e2ff" storm={storm} />}
      </div>
    );
  }

  const sunny = bucket === "sol";
  const rainy = bucket === "chuva";
  // Nublado e chuva usam o mesmo arranjo de nuvens em camadas; na chuva as
  // cores são mais carregadas/escuras.
  const c = rainy
    ? {
        back1: ["#7d8b9c", "#5d6b7d"],
        back2: ["#8593a3", "#627083"],
        mid: ["#b7c1cc", "#8391a2"],
        front: ["#cdd5dd", "#95a2b1"],
        low: ["#dfe5ea", "#b0bac5"],
      }
    : {
        back1: ["#b4c1d0", "#8e9fb4"],
        back2: ["#bfcad7", "#95a6ba"],
        mid: ["#f4f7fa", "#c7d2de"],
        front: ["#ffffff", "#d3dce6"],
        low: ["#ffffff", "#e4eaf0"],
      };
  return (
    <div
      aria-hidden
      // Só no tema claro — sobre o fundo escuro do modo noturno do PWA um
      // céu claro ficaria deslocado; lá o card de clima já basta.
      className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[360px] overflow-hidden [container-type:inline-size] dark:hidden"
      style={{
        background: sunny
          ? "linear-gradient(180deg, #3d9be9 0%, #6cbcf3 22%, #a6d8fa 48%, #d9effd 72%, #f5fbff 88%, #ffffff 100%)"
          : rainy
          ? "linear-gradient(180deg, #56657a 0%, #75859a 22%, #a2afbe 48%, #d3dae2 72%, #f3f5f8 88%, #ffffff 100%)"
          : "linear-gradient(180deg, #7d8fa6 0%, #9eaec1 24%, #c3cfdc 50%, #e3e9f0 74%, #f7f9fb 90%, #ffffff 100%)",
      }}
    >
      {sunny ? (
        <>
          {/* Sol: brilho quente + raios girando devagar (canto sup. direito,
              atrás do card de clima) */}
          <div
            className="absolute -right-24 -top-24 h-80 w-80 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(255,247,200,0.95) 0%, rgba(255,226,140,0.55) 18%, rgba(255,210,120,0.18) 40%, transparent 68%)",
            }}
          />
          <div
            className="sky-rays absolute -right-40 -top-40 h-[28rem] w-[28rem] opacity-40"
            style={{
              background:
                "repeating-conic-gradient(from 0deg, rgba(255,255,255,0.35) 0deg 6deg, transparent 6deg 22deg)",
              maskImage: "radial-gradient(circle, black 10%, transparent 62%)",
              WebkitMaskImage: "radial-gradient(circle, black 10%, transparent 62%)",
            }}
          />
          <SkyCloud
            top="#ffffff"
            bottom="#dbeaf7"
            className="sky-cross top-[70px] w-48"
            style={{ "--sky-dur": "55s", "--sky-delay": "-8s" } as React.CSSProperties}
          />
          <SkyCloud
            top="#ffffff"
            bottom="#e2eef9"
            opacity={0.85}
            blur={1.8}
            className="sky-cross top-[26px] w-28"
            style={{ "--sky-dur": "80s", "--sky-delay": "-50s" } as React.CSSProperties}
          />
          <SkyCloud
            top="#ffffff"
            bottom="#d6e7f6"
            opacity={0.9}
            className="sky-cross top-[150px] w-56"
            style={{ "--sky-dur": "65s", "--sky-delay": "-30s" } as React.CSSProperties}
          />
          <SkyCloud
            top="#ffffff"
            bottom="#eaf4fc"
            opacity={0.7}
            blur={2.5}
            className="sky-cross top-[200px] w-40"
            style={{ "--sky-dur": "90s", "--sky-delay": "-70s" } as React.CSSProperties}
          />
        </>
      ) : (
        <>
          {/* Nublado: camadas de nuvens — as de trás mais escuras/desfocadas,
              as da frente mais claras, dando profundidade */}
          <SkyCloud
            top={c.back1[0]}
            bottom={c.back1[1]}
            opacity={0.9}
            blur={3}
            className="sky-cross -top-6 w-80"
            style={{ "--sky-dur": "110s", "--sky-delay": "-20s" } as React.CSSProperties}
          />
          <SkyCloud
            top={c.back2[0]}
            bottom={c.back2[1]}
            opacity={0.85}
            blur={3}
            className="sky-cross top-[10px] w-80"
            style={{ "--sky-dur": "100s", "--sky-delay": "-75s" } as React.CSSProperties}
          />
          <SkyCloud
            top={c.mid[0]}
            bottom={c.mid[1]}
            className="sky-cross top-[80px] w-60"
            style={{ "--sky-dur": "70s", "--sky-delay": "-40s" } as React.CSSProperties}
          />
          <SkyCloud
            top={c.front[0]}
            bottom={c.front[1]}
            opacity={0.95}
            className="sky-cross top-[120px] w-64"
            style={{ "--sky-dur": "62s", "--sky-delay": "-5s" } as React.CSSProperties}
          />
          <SkyCloud
            top={c.low[0]}
            bottom={c.low[1]}
            opacity={0.75}
            blur={2.5}
            className="sky-cross top-[190px] w-52"
            style={{ "--sky-dur": "85s", "--sky-delay": "-60s" } as React.CSSProperties}
          />
        </>
      )}
      {raining && <RainLayer color="#ffffff" storm={storm} splashTop={236} />}
      {/* Esmaecimento final pro branco da página, sem linha de corte */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
    </div>
  );
}

function QuickAccessButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    // h-16 fixo + label com line-clamp-2 (pedido 2026-09-01: "os retângulos
    // que fica os ícones nos acessos rápidos tem que ficar no mesmo
    // tamanho") — sem altura fixa, um rótulo de 2 linhas (ex.: "Solicitar
    // assistência") deixava aquele botão mais alto que os de 1 linha (ex.:
    // "Meus relatórios"), mesmo estando lado a lado no grid.
    <button
      type="button"
      onClick={onClick}
      className="flex h-16 flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-100 px-0.5 py-1.5 text-center active:bg-slate-200 dark:border-transparent dark:bg-white dark:active:bg-slate-100"
    >
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="line-clamp-2 text-[8.5px] font-medium leading-tight text-slate-600">
        {label}
      </span>
    </button>
  );
}

function SummaryStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div>
      <div className="mx-auto grid h-5 w-5 place-items-center text-primary">{icon}</div>
      <div className="mt-0.5 text-[11.5px] font-bold text-slate-900">{value}</div>
      <div className="text-[9px] text-slate-500">{label}</div>
    </div>
  );
}

// Tela Início (pedido 2026-09-01: "vamos colocar esse visual aqui no
// sistema porem com o fundo branco" — visual copiado de uma referência
// externa, dados sempre reais). Substitui a antiga aba Faturas na barra
// (ver MobileApp#TabItem); sempre clara (sem dark:) de propósito — só essa
// tela ganhou o visual novo, o resto do app continua respeitando o toggle
// claro/escuro existente.
//
// "Veículo em destaque" é escolhido pelo carrossel de avatares (clique troca
// qual veículo aparece no card/resumo abaixo), começando no primeiro da
// frota. Nada aqui é inventado: Situação/Última atualização vêm de
// status/lastConnection reais da frota, o resumo do dia vem dos MESMOS
// relatórios oficiais do Traccar usados no Centro de Histórico
// (useVehicleSummary/useVehicleStops). O card de Emergência
// saiu daqui (pedido 2026-09-22) e foi pra aba Perfil (YouPanel).
// memo — fica sempre montado (mesmo painel-nunca-desmonta explicado acima),
// então sem isso QUALQUER clique em outro lugar do app (abrir um dialog no
// Perfil, digitar na busca de Veículos etc.) forçava esta tela inteira a
// re-renderizar de novo, refazendo os `vehicles.filter`/`reduce` sobre a
// frota (5000+ veículos, ver comentário logo abaixo de VehiclesList) —
// pedido 2026-09-08 "app lento, demora pra abrir qualquer coisa". Callbacks
// vêm de useCallback em MobileApp pra manter a identidade estável.
// Placeholder do Início enquanto vehiclesLoading (ver InicioPanel) — mesmo
// layout geral (barra/carrossel/card) em blocos animate-pulse, pra não
// mostrar a tela "apagada"/vazia por ~2s no 1º fetch real sem snapshot.
function InicioSkeleton() {
  return (
    <div className="bg-white px-3 pb-3 pt-2 dark:bg-slate-950">
      <div className="flex h-14 items-center justify-center">
        <div className="h-8 w-28 animate-pulse rounded-md bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="mt-2 flex gap-2.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-14 w-14 shrink-0 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>
      <div className="mt-4 h-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      <div className="mt-3 h-24 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

const InicioPanel = memo(function InicioPanel({
  vehicles,
  vehiclesLoading,
  traccarUserId,
  onSelectVehicle,
  onOpenVehicleDetails,
  onOpenEventos,
  onOpenAssistencia,
  onOpenMinhasAssistencias,
  onOpenFaturas,
  onOpenReports,
  onOpenPerfilTab,
  darkMode,
}: {
  darkMode: boolean;
  vehicles: Vehicle[];
  // true só na 1ª abertura sem nenhum snapshot salvo ainda (instalação
  // nova/dado local limpo) — com snapshot (ver useVehicles#initialData),
  // vehicles já chega preenchido e isto nasce false. Sem essa distinção a
  // tela renderizava carrossel/card vazios por ~2s até o 1º fetch real
  // responder (pedido 2026-09-09: "aparece sem nenhum dado, fica tudo
  // apagado sem veículo").
  vehiclesLoading: boolean;
  traccarUserId: number | undefined;
  onSelectVehicle: (id: string) => void;
  onOpenVehicleDetails: (id: string) => void;
  onOpenEventos: () => void;
  onOpenAssistencia: () => void;
  onOpenFaturas: () => void;
  onOpenReports: (id: string) => void;
  onOpenPerfilTab: () => void;
  onOpenMinhasAssistencias: () => void;
}) {
  const { user, gpsDriveUser } = useAuth();
  const { data: branding } = useAdminBranding();
  // Bug relatado 2026-09-09: o atalho "Bloqueio veicular" aparecia pra
  // qualquer usuário, mesmo sem devices.lock/devices.unlock — a checagem
  // certa já existia na tela de detalhe do veículo (ver `canLock`/
  // `canUnlock` mais abaixo neste arquivo, na função que renderiza o botão
  // de fato), mas este atalho aqui em cima nunca checava nada, só navegava
  // pra lá. Mostra o atalho só se o usuário tem pelo menos uma das duas
  // permissões — mesmo critério usado pro botão de bloquear/desbloquear em
  // si (routes/index.tsx#VehicleDetailSidebar já usa `canLock || canUnlock`
  // pra decidir se mostra a seção inteira).
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const menuAccess = useAppMenuAccess();
  // "Solicitar assistência" (pedido 2026-09-11): funcionário/admin
  // (administracao.assistencia) continua registrando PARA um cliente
  // ("Registrar assistência", abre RegistrarAssistenciaScreen com busca de
  // cliente); o cliente logado nunca deve ver essa tela de busca — pra ele
  // o atalho vira "Minhas assistências" (histórico próprio), e só aparece
  // se algum dos veículos DELE tiver um Plano com assistência vinculado
  // (Plan.serviceGroupId, ver hasAssistancePlan em vehicle-metadata.
  // service.ts). Frota do cliente é sempre pequena (só os veículos dele),
  // diferente da frota de um Administrator (pode passar de 5000, ver
  // incidente 2026-09-08) — por isso o `enabled` abaixo só liga a busca em
  // lote pra conta tipo "usuario", nunca pra funcionário/admin.
  //
  // Atualização 2026-09-22 (pedido: "quem é cliente não pode ver o botão de
  // minhas assistências"): o atalho "Minhas assistências" saiu da Início
  // de vez (e do Perfil pro cliente) — aqui só fica "Registrar
  // assistência" pra quem tem a permissão.
  const canRegisterAssistance = usePermission("administracao", "assistencia");
  // Carrossel (pedido 2026-10-01): TODOS os veículos (o limite antigo de
  // 20 era da frota de teste de 5000, já apagada) e sem tags. Fotos com
  // loading="lazy" — só baixam as que entram na tela ao rolar.
  //
  // Atualização 2026-10-05 ("F5 fica carregando demais"): desenhar os
  // 2.800+ avatares de uma vez criava ~11 mil elementos e travava a
  // montagem da tela por vários segundos (pior no celular). Continua dando
  // pra chegar em TODOS, mas eles entram em lotes de CAROUSEL_BATCH conforme
  // a pessoa rola pro lado — e as fotos só são buscadas pros já desenhados.
  //
  // Atualização 2026-10-05 (cliente com veículo + tag: "a tag não aparece
  // no início"): o "sem tags" era pra frota grande de admin/funcionário.
  // Conta de cliente (accountType "usuario") vê as tags DELE, depois dos
  // veículos — o destaque continua começando pelo 1º veículo.
  const isClientAccount = isRestrictedAccountType(gpsDriveUser?.accountType);
  // Pedido 2026-10-12: online primeiro no carrossel — em movimento, depois
  // parado/conectado, por último offline (ordem original mantida em cada
  // grupo; tags do cliente continuam no fim).
  const carouselVehicles = useMemo(() => {
    const byOnline = (list: typeof vehicles) =>
      [...list].sort((a, b) => CAROUSEL_STATUS_RANK[a.status] - CAROUSEL_STATUS_RANK[b.status]);
    return isClientAccount
      ? [...byOnline(vehicles.filter((v) => !v.isTag)), ...byOnline(vehicles.filter((v) => v.isTag))]
      : byOnline(vehicles.filter((v) => !v.isTag));
  }, [vehicles, isClientAccount]);
  const [carouselLimit, setCarouselLimit] = useState(CAROUSEL_BATCH);
  const carouselShown = useMemo(
    () => carouselVehicles.slice(0, carouselLimit),
    [carouselVehicles, carouselLimit],
  );
  const { data: vehicleImagesMap } = useVehicleImages(carouselShown.map((v) => v.id));
  const onCarouselScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (carouselLimit < carouselVehicles.length && el.scrollLeft + el.clientWidth > el.scrollWidth - 300) {
      setCarouselLimit((n) => n + CAROUSEL_BATCH);
    }
  };
  const [featuredId, setFeaturedId] = useState<string | null>(null);
  const featured = carouselVehicles.find((v) => v.id === featuredId) ?? carouselVehicles[0] ?? null;
  // Atalho "Bloqueio veicular" abre direto o popup de bloquear/desbloquear
  // do veículo em destaque (pedido 2026-09-22), mesmo envio da tela de
  // detalhes (useVehicleBlockAction). Permissões canLock/canUnlock abaixo.
  const { data: featuredMetadata } = useVehicleMetadata(featured?.id);
  const featuredSmsFallback = useBlockCommandSmsFallback(featuredMetadata?.protocol, featuredMetadata?.trackerModel);
  const featuredBlock = useVehicleBlockAction(featured, featuredSmsFallback);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const address = useReverseGeocode(featured?.lat, featured?.lng, { stableKey: featured?.id });
  const greeting = useMemo(() => greetingFor(user?.name?.split(" ")[0]), [user?.name]);
  const logoUrl =
    darkMode && branding?.logoDarkUrl
      ? resolveGpsDriveUrl(branding.logoDarkUrl)
      : branding?.logoUrl
        ? resolveGpsDriveUrl(branding.logoUrl)
        : logoAsset.url;

  // Clima do tempo — ver use-weather.ts e o bloco de render logo abaixo do
  // carrossel. `useWeatherCoords` aqui e o de dentro de `useWeather()`
  // compartilham a mesma queryKey ("weather-geolocation"), então isso não
  // dispara uma 2ª leitura de GPS — é só a mesma posição já buscada,
  // reaproveitada aqui pra resolver o nome da cidade (useReverseGeocode, o
  // mesmo hook/proxy já usado pro endereço do veículo em destaque acima).
  // Sem GPS do aparelho (ex.: app da Play Store sem permissão de
  // localização), usa a posição do veículo em destaque — ver
  // useWeatherCoords em use-weather.ts.
  const weatherFallback = useMemo(
    () =>
      featured && Number.isFinite(featured.lat) && Number.isFinite(featured.lng) && (featured.lat !== 0 || featured.lng !== 0)
        ? { lat: featured.lat, lon: featured.lng }
        : null,
    [featured?.lat, featured?.lng],
  );
  const weather = useWeather(weatherFallback);
  const weatherCoords = useWeatherCoords(weatherFallback);
  const weatherCityAddress = useReverseGeocode(weatherCoords?.lat, weatherCoords?.lon);
  // O backend devolve "rua, num · bairro · cidade" (ver geocoding.service.ts)
  // — o último trecho é a cidade; sem estrutura pronta, o último segmento é
  // a aproximação mais simples sem precisar mudar o backend agora (pedido
  // 2026-09-22: "só no visual do pwa pra testarmos").
  const weatherCity = weatherCityAddress?.split(" · ").pop()?.trim();
  const realWeatherMeta = weather.data
    ? describeWeatherCode(weather.data.weatherCode, weather.data.isDay)
    : null;
  const realSkyBucket = weather.data
    ? weatherBucket(weather.data.weatherCode, weather.data.isDay)
    : null;
  // Pré-visualização forçada (ver WEATHER_PREVIEW) — só muda o visual; a
  // temperatura e a cidade continuam as reais.
  const skyBucket = WEATHER_PREVIEW && realSkyBucket ? WEATHER_PREVIEW : realSkyBucket;
  const weatherMeta =
    WEATHER_PREVIEW === "chuva" && realWeatherMeta
      ? { icon: CloudRain, label: "Chuva" }
      : WEATHER_PREVIEW === "tempestade" && realWeatherMeta
        ? { icon: CloudLightning, label: "Tempestade" }
        : realWeatherMeta;
  const raining =
    WEATHER_PREVIEW === "chuva" ||
    WEATHER_PREVIEW === "tempestade" ||
    (!WEATHER_PREVIEW && !!weather.data && isRainCode(weather.data.weatherCode));
  // Relâmpago/raios só com trovoada de verdade (WMO 95–99) — de dia é o
  // tema "tempestade"; à noite entra por cima do céu noturno.
  const storm =
    WEATHER_PREVIEW === "tempestade" ||
    (!WEATHER_PREVIEW && !!weather.data && weather.data.weatherCode >= 95);
  const isNightSky = skyBucket === "noite";
  // Tempestade escurece só o TOPO (pedido 2026-09-22: "quero o fundo branco
  // ainda, só lá em cima") — pílula e rótulos dos avatares, que ficam sobre
  // essa faixa escura, usam as cores claras.
  const darkTop = isNightSky || skyBucket === "tempestade";

  const offlineCount = vehicles.filter((v) => v.status === "offline").length;
  const lastUpdateIso = vehicles.reduce<string | null>((latest, v) => {
    const ts = v.positionTimestamp ?? v.lastConnection;
    if (!ts) return latest;
    return !latest || ts > latest ? ts : latest;
  }, null);

  // Janela "hoje" (00h até agora), mesmo cálculo do período "Hoje" do
  // Centro de Histórico — recalculado só na montagem, não a cada render.
  // "Hoje" até AGORA, renovado a cada 5min — antes era calculado uma vez só
  // na montagem, e como a Início nunca desmonta, o resumo ficava congelado
  // na hora em que o app abriu (e virava a noite sem trocar de dia). Janela
  // arredondada em 5min pra não gerar query key nova a cada render.
  // nowMinute: relógio de 1min (contador "Parado há" sobe sozinho);
  // nowBucket: janela de 5min usada nas queries.
  const [nowMinute, setNowMinute] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMinute(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const nowBucket = Math.floor(nowMinute / 300_000);
  const today = useMemo(() => {
    const now = new Date();
    return { from: startOfDay(now).toISOString(), to: now.toISOString() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowBucket]);
  const summaryQuery = useVehicleSummary(featured?.id, today.from, today.to, !!featured);
  const stopsQuery = useVehicleStops(featured?.id, today.from, today.to, !!featured);
  const summary = summaryQuery.data?.[0];
  // Mesmo filtro do Centro de Histórico (isRealStop) — contagem de
  // "Paradas" e tempo "Parado" batem com a web.
  const validStops = (stopsQuery.data ?? []).filter(
    (s) => isValidCoordinate(s.lat, s.lng) && s.durationMs > 0,
  );
  // "Parado há X" (pedido 2026-09-22: "o correto era marcar da hora que
  // parou até agora") = agora − fim da última viagem. Versões anteriores
  // somavam o tempo parado do dia (8h05 pro BMW que parou às 14:35 — o
  // usuário queria "há quanto tempo está parado"). Viagens dos últimos 7
  // dias, pra funcionar com carro parado desde ontem ou antes. Andando
  // agora (velocidade atual > 3 km/h) mostra "Andando".
  const lastTripsRange = useMemo(() => {
    const now = new Date();
    return {
      from: new Date(now.getTime() - 7 * 24 * 3600_000).toISOString(),
      to: now.toISOString(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowBucket]);
  const lastTripsQuery = useVehicleTrips(
    featured?.id,
    lastTripsRange.from,
    lastTripsRange.to,
    !!featured,
  );
  const isMovingNow = (featured?.speed ?? 0) > 3;
  const parkedSinceMs = useMemo(() => {
    const trips = lastTripsQuery.data;
    if (!trips) return null;
    let last = 0;
    for (const t of trips) {
      const end = t.endTime ? new Date(t.endTime).getTime() : NaN;
      if (Number.isFinite(end) && end > last) last = end;
    }
    return last > 0 ? last : null;
  }, [lastTripsQuery.data]);
  const parkedLabel = isMovingNow
    ? "Andando"
    : !lastTripsQuery.data
      ? "—"
      : parkedSinceMs == null
        ? "+7 dias"
        : formatShortDuration(Math.max(0, nowMinute - parkedSinceMs));
  const featuredImageUrls = featured ? vehicleImagesMap?.[featured.id] : undefined;
  const featuredPhotoUrl = featuredImageUrls
    ? resolveGpsDriveUrl(featuredImageUrls["128"] ?? featuredImageUrls.original)
    : undefined;

  // Trocar a foto do veículo direto pelo card de destaque (pedido
  // 2026-09-11: "quero que o cliente possa clicar/tocar diretamente na
  // FOTO do veículo para alterá-la"). Mesmo endpoint/campo que toda outra
  // troca de foto de veículo no sistema já usa (vehicleMetadataService.
  // uploadImage → POST /vehicle-metadata/:id/image → vehicle_metadata.
  // image_key, ver StorageService no gpsdrive-backend) — mesmo padrão de
  // interação já usado no card do veículo no painel desktop (routes/
  // index.tsx#VehicleListItem: input file escondido + botão que o
  // aciona). compressImageFile roda no navegador ANTES do upload (reduz
  // uma foto de câmera de alguns MB pra uma fração disso, sem perder
  // qualidade perceptível no tamanho exibido) — é o que faz o upload ser
  // rápido mesmo em rede móvel; o servidor recodifica pra WebP e gera as
  // miniaturas de qualquer forma, então comprimir aqui só evita mandar
  // bytes à toa pela rede.
  const queryClient = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const handlePickFeaturedPhoto = async (file?: File) => {
    if (!file || !featured) return;
    setPhotoUploading(true);
    try {
      const optimized = await compressImageFile(file);
      await vehicleMetadataService.uploadImage(featured.id, optimized);
      // Invalida em TODA a tela (não só este card) — o mesmo hook
      // (useVehicleImages) alimenta o carrossel logo abaixo, a lista de
      // Veículos e o card inferior do Mapa (BottomSheetCard), então uma
      // única invalidação por prefixo já atualiza a foto em todo lugar
      // que a mostra, sem precisar recarregar a página (mesmo padrão do
      // painel desktop, ver onPickImage/handlePickPhoto em routes/
      // index.tsx). A foto antiga só é apagada do storage DEPOIS que o
      // upload novo confirma — se o upload falhar (catch abaixo), nada
      // muda: a foto anterior continua sendo a que os componentes já
      // têm em cache, nunca fica quebrada.
      await queryClient.invalidateQueries({ queryKey: ["vehicle-images"] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicleMetadata(featured.id) });
      toast.success("Foto do veículo atualizada");
    } catch (err) {
      toast.error("Não foi possível trocar a foto do veículo", {
        description: describeImageUploadError(err),
      });
    } finally {
      setPhotoUploading(false);
    }
  };

  if (vehiclesLoading && vehicles.length === 0) {
    return <InicioSkeleton />;
  }

  return (
    <div
      // Céu por condição do clima (referências do usuário 2026-09-22): sol/
      // nublado pintam só o topo (camada abaixo, esmaecendo pro branco);
      // à noite a tela inteira vira azul-marinho estrelado e o Início entra
      // no mesmo modo escuro já existente (classe `dark` — os cards viram
      // brancos sobre fundo escuro, exatamente como nas referências).
      className={cn(
        "relative isolate min-h-full px-3 pb-3 bg-white dark:bg-slate-950",
      )}
      // paddingTop soma env(safe-area-inset-top) ao respiro de sempre
      // (0.5rem) em vez de substituí-lo — em WebView/PWA sem status bar
      // sobrepondo o conteúdo o env() resolve pra 0px e nada muda; em
      // Android com edge-to-edge forçado pela plataforma (obrigatório desde
      // API 36/Android 16, ver targetSdkVersion no android-capacitor — sem
      // opt-out possível nessa versão) o WebView desenha por baixo da
      // status bar/notch, cortando a logo e ficando inacessível o resto da
      // barra (bug relatado 2026-09-11: "a logo do GPS Drive fica
      // parcialmente escondida no topo" em alguns aparelhos, mas não
      // outros — a diferença é só a versão do Android forçar ou não esse
      // modo, não o tamanho de tela). Mesmo padrão aplicado a toda barra/
      // cabeçalho que hoje encosta no topo da tela neste arquivo (comentário
      // completo só aqui pra não repetir em cada ocorrência).
      style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
    >
      {/* Barra superior — logo centralizado no meio da barra (pedido
          2026-09-01: "a logotipo ta muito pequena e ela tem que ficar bem no
          meio"), sino de notificação flutuando à direita via posição
          absoluta pra não empurrar a centralização do logo. Tamanho é
          configurável em Administração > Identidade Visual > Logotipos >
          "Logotipo do app (PWA)" (branding.appIconSize, pedido: "coloca
          para poder corrigir a logotipo no pwa ai consegue ajustar o
          tamanho que fica bom"); 48px é o valor usado antes de virar
          ajustável.

          Altura da barra TRAVADA em h-14 (56px, independente do tamanho do
          logotipo) — a 1ª tentativa travava a altura em APP_LOGO_MAX, mas
          isso deixava a barra permanentemente alta demais mesmo com o
          logotipo pequeno (o "espaçamento" que sobrava em volta parecia um
          fundo/caixa em volta da imagem, bug relatado 2026-09-01: "fica
          criando aquele espaçamento aparecendo que a imagem tem fundo").
          Agora o <img> é `absolute` (fora do fluxo, só o sino ficava assim
          antes) — ele pode crescer até 180px sem empurrar NADA, porque não
          participa mais da altura da barra; como a barra inteira é o mesmo
          bg-white contínuo do resto da tela, um logotipo grande simplesmente
          "vaza" pra cima/baixo da faixa sem costura visível, sem caixa e sem
          mover o carrossel de avatares abaixo. */}
      {skyBucket && <InicioSky bucket={skyBucket} raining={raining} storm={storm} />}
      {/* Logo à esquerda + clima à direita (pedido 2026-09-22:
          "logotipo na esquerda e o clima do lado da logo no canto direito").
          A logo continua `absolute` (não empurra a altura da barra, ver
          comentário acima), agora ancorada à esquerda e com largura limitada
          pra nunca passar por baixo do card de clima. */}
      <div className="relative flex min-h-14 items-center justify-end gap-1.5">
        <img
          src={logoUrl}
          alt={branding?.appName ?? "GPS Drive"}
          style={{ height: Math.min(branding?.appIconSize ?? 48, APP_LOGO_MAX) }}
          className="absolute left-0 top-1/2 w-auto max-w-[40%] -translate-y-1/2 object-contain object-left"
        />
        {/* Widget de clima (pedido 2026-09-22; visual refeito seguindo as
            3 referências enviadas — sol/nublado/noite): card de vidro no
            canto direito da barra superior, ao lado da logo (pedido:
            "logotipo na esquerda e o clima do lado da logo no canto
            direito") com ícone, temperatura, condição e cidade, tudo numa
            linha só; sino de notificação removido daqui a pedido (Eventos
            continua acessível pela barra inferior).
            Geolocalização de quem está usando (ver use-weather.ts), API
            Open-Meteo, cidade via o mesmo proxy de geocodificação reversa do
            veículo em destaque. Enquanto a localização não é concedida/
            carrega ou a API falha, não renderiza nada — é um extra. */}
        {weather.data && weatherMeta && skyBucket && (
          <div
            className={cn(
              "flex max-w-[58%] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border py-1.5 pl-2 pr-3 shadow-sm backdrop-blur-md",
              darkTop
                ? "border-white/15 bg-white/10 text-white"
                : "border-white/80 bg-white/65 text-slate-900 dark:border-white/10 dark:bg-slate-900/60 dark:text-white",
            )}
          >
            <weatherMeta.icon
              className={cn(
                "h-6 w-6 shrink-0 drop-shadow-sm",
                skyBucket === "sol" && "wx-spin fill-amber-300 text-amber-400",
                skyBucket === "nuvem" && "wx-float fill-slate-300 text-slate-400",
                skyBucket === "chuva" && "wx-float fill-slate-400 text-slate-500",
                skyBucket === "tempestade" && "wx-glow fill-slate-400 text-white",
                skyBucket === "noite" && "wx-glow fill-white text-white",
              )}
              strokeWidth={1.5}
              aria-hidden
            />
            <span className="shrink-0 text-[14px] font-bold leading-none">
              {weather.data.temperatureC}°C
            </span>
            <span
              className={cn(
                "shrink-0 text-[11px] font-medium",
                darkTop ? "text-white/85" : "text-slate-600 dark:text-slate-300",
              )}
            >
              {weatherMeta.label}
            </span>
            <span
              className={cn(
                "flex min-w-0 items-center gap-0.5 text-[11px]",
                darkTop ? "text-white/70" : "text-slate-500 dark:text-slate-400",
              )}
            >
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{weatherCity ?? "Sua localização"}</span>
            </span>
          </div>
        )}
      </div>

      {/* Carrossel de avatares — Meu perfil + frota, clicar num veículo troca o destaque abaixo */}
      <div className="mt-2 flex gap-2.5 overflow-x-auto pb-1" onScroll={onCarouselScroll}>
        <button
          type="button"
          onClick={onOpenPerfilTab}
          className="flex shrink-0 flex-col items-center gap-1"
        >
          <ProfileAvatarCircle traccarUserId={traccarUserId} />
          <span
            className={cn(
              "text-[10px] font-medium",
              skyBucket === "tempestade"
                ? "text-white drop-shadow dark:text-slate-300"
                : "text-slate-600 dark:text-slate-300",
            )}
          >
            Meu perfil
          </span>
        </button>
        {carouselShown.map((v) => {
          const imgs = vehicleImagesMap?.[v.id];
          const photoUrl = imgs ? resolveGpsDriveUrl(imgs["128"] ?? imgs.original) : undefined;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setFeaturedId(v.id)}
              className="flex shrink-0 flex-col items-center gap-1"
            >
              <span
                className={cn(
                  "grid h-14 w-14 place-items-center overflow-hidden rounded-full border-2 bg-slate-100 dark:bg-slate-800",
                  featured?.id === v.id
                    ? "border-primary"
                    : "border-slate-200 dark:border-slate-700",
                )}
              >
                <img
                  src={photoUrl ?? iconUrlFor(v)}
                  alt=""
                  loading="lazy"
                  className={photoUrl ? "h-full w-full object-cover" : "h-8 w-8 object-contain"}
                />
              </span>
              <span
                className={cn(
                  "max-w-[60px] truncate text-[10px] font-medium",
                  skyBucket === "tempestade"
                    ? "text-white drop-shadow dark:text-slate-300"
                    : "text-slate-600 dark:text-slate-300",
                )}
              >
                {v.name.split(" ")[0]}
              </span>
            </button>
          );
        })}
      </div>


      {/* Saudação + status real da frota — nome/pill sempre na mesma linha
          (nome truncado se precisar), subtítulo + última atualização juntos
          numa segunda linha só, nunca quebrando em 3+ linhas (pedido
          2026-09-01: "tem que ficar na mesma linha ou dividir em 2 linha");
          fonte reduzida (pedido: "o bom dia gps drive esta muito grande"). */}
      <div className="mt-2">
        <div className="flex items-center justify-between gap-2">
          <h1 className="truncate text-[13.5px] font-bold text-slate-900 dark:text-slate-100">
            {greeting.text} <span aria-hidden>{greeting.emoji}</span>
          </h1>
          {vehicles.length > 0 && (
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                offlineCount === 0
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
              )}
            >
              <CheckCircle2 className="h-2.5 w-2.5" />
              {offlineCount === 0 ? "Tudo certo" : `${offlineCount} offline`}
            </span>
          )}
        </div>
        <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
          Bem-vindo de volta!
          {lastUpdateIso && (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}
              · atualizado{" "}
              {formatDistanceToNow(new Date(lastUpdateIso), { addSuffix: true, locale: ptBR })}
            </span>
          )}
        </p>
      </div>

      {/* Card do veículo em destaque — compactado (pedido: "diminui um pouco
          o botão acessar rastreamento aonde aparece veiculo posição etc") */}
      {featured ? (
        <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-100 p-2.5 dark:border-transparent dark:bg-white">
          <div className="flex gap-2.5">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void handlePickFeaturedPhoto(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              aria-label="Trocar foto do veículo"
              title="Trocar foto do veículo"
              disabled={photoUploading}
              onClick={() => photoInputRef.current?.click()}
              className="relative h-11 w-14 shrink-0 overflow-hidden rounded-lg bg-white dark:bg-slate-100"
            >
              <img
                src={featuredPhotoUrl ?? iconUrlFor(featured)}
                alt=""
                className={
                  featuredPhotoUrl
                    ? "h-full w-full object-cover"
                    : "h-full w-full object-contain p-1"
                }
              />
              {/* Bolinha de status em vez do texto "Em movimento"/"Parado"
                  (pedido 2026-09-01: "a etiqueta... ficou ruim assim,
                  coloca em algum lugar dentro do retângulo, pode colocar
                  pequeno") — o retângulo da foto tem só 56px de largura,
                  "Em movimento" por extenso não cabe nisso; a cor já
                  carrega o significado (ver legenda igual usada no mapa),
                  title dá o texto completo no hover/leitor de tela. */}
              <span
                className="absolute bottom-1 left-1 h-2.5 w-2.5 rounded-full border border-white shadow-sm"
                style={{ background: statusColorFor(featured) }}
                title={statusText(featured)}
              />
              {/* Selo de câmera sempre visível (pedido 2026-09-11: "mostrar
                  claramente que a imagem é clicável") — sem :hover no
                  toque (celular), então o selo fica sempre ali, não só
                  aparece ao interagir como no painel desktop (mouse). Vira
                  o spinner durante o upload, cobrindo a foto inteira pra
                  deixar claro que uma ação está em andamento. */}
              <span
                aria-hidden
                className={cn(
                  "absolute inset-0 flex items-center justify-center",
                  photoUploading ? "bg-black/55" : "bg-black/0",
                )}
              >
                {photoUploading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <span className="absolute bottom-0.5 right-0.5 grid h-4 w-4 place-items-center rounded-full bg-slate-900/70 text-white">
                    <Camera className="h-2.5 w-2.5" strokeWidth={2.5} />
                  </span>
                )}
              </span>
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-[12.5px] font-bold text-slate-900">{featured.name}</p>
                <LicensePlate plate={featured.plate} size="xs" />
              </div>
              <div className="mt-1 grid grid-cols-3 gap-1.5 text-center text-[10px]">
                <div>
                  <div className="text-slate-500">Velocidade</div>
                  <div className="font-semibold text-slate-900">
                    {Math.round(featured.speed ?? 0)} km/h
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Bateria</div>
                  <div className="font-semibold text-slate-900">
                    {featured.powerVoltage != null
                      ? `${featured.powerVoltage.toFixed(1)}V`
                      : featured.batteryLevel != null
                        ? `${Math.round(featured.batteryLevel)}%`
                        : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Posição</div>
                  <div className="truncate font-semibold text-slate-900">
                    {address ?? "Buscando..."}
                  </div>
                  <div className="truncate text-[9.5px] text-slate-500">
                    {timeWithAgo(featured.positionTimestamp ?? featured.lastConnection)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSelectVehicle(featured.id)}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-[12.5px] font-bold text-white active:opacity-90"
          >
            <MapPin className="h-3.5 w-3.5" /> Acessar rastreamento{" "}
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="mt-2 text-center text-sm text-slate-500 dark:text-slate-400">
          Nenhum veículo cadastrado.
        </p>
      )}

      {/* Acesso rápido — número de colunas acompanha quantos atalhos
          realmente aparecem (ver canLock/canUnlock acima) pra nunca deixar
          uma célula vazia no grid quando "Bloqueio veicular" some. */}
      {featured &&
        (() => {
          const tiles = [
            // Motorista não tem faturas próprias (pedido 2026-10-08).
            !menuAccess.isMotorista && (
              <QuickAccessButton
                key="faturas"
                icon={<Receipt className="h-4 w-4" />}
                label="Minhas faturas"
                onClick={onOpenFaturas}
              />
            ),
            (canLock || canUnlock) && (
              <QuickAccessButton
                key="bloqueio"
                icon={<Lock className="h-4 w-4" />}
                label="Bloqueio veicular"
                onClick={() => setBlockDialogOpen(true)}
              />
            ),
            menuAccess.eventos && (
              <QuickAccessButton
                key="alertas"
                icon={<Bell className="h-4 w-4" />}
                label="Alertas e avisos"
                onClick={onOpenEventos}
              />
            ),
            canRegisterAssistance && (
              <QuickAccessButton
                key="assistencia"
                icon={<Wrench className="h-4 w-4" />}
                label="Registrar assistência"
                onClick={onOpenAssistencia}
              />
            ),
            menuAccess.historico && (
              <QuickAccessButton
                key="relatorios"
                icon={<FileText className="h-4 w-4" />}
                label="Meus relatórios"
                onClick={() => onOpenReports(featured.id)}
              />
            ),
          ].filter(Boolean);
          return (
            <div className="mt-2">
              <div
                className="grid gap-1"
                style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
              >
                {tiles}
              </div>
            </div>
          );
        })()}

      {/* Aviso da empresa — pedido 2026-09-01, mas sem nenhum sistema real de
          comunicados/anúncios por trás (a referência tinha um carrossel com
          texto fixo, não dado real). Único texto real e já configurável pela
          própria empresa que existe hoje é "Descrição do servidor"
          (Administração > Identidade Visual > Dados & Contato,
          branding.serverDescription) — reaproveitado aqui em vez de inventar
          um card com texto fixo. Some sozinho se a empresa não preencheu. */}
      {branding?.serverDescription && (
        <div className="mt-2 flex items-start gap-2 rounded-2xl border border-slate-200 bg-slate-100 p-2.5 dark:border-transparent dark:bg-white">
          <Megaphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="text-[12px] font-bold text-slate-900">Aviso da empresa</div>
            <p className="mt-0.5 text-[11px] leading-snug text-slate-600">
              {branding.serverDescription}
            </p>
          </div>
        </div>
      )}

      {/* Resumo do dia — mesmos relatórios oficiais do Centro de Histórico, período "Hoje" */}
      {featured && (
        <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-100 p-2.5 dark:border-transparent dark:bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-[12px] font-bold text-slate-900">Resumo do seu veículo</h2>
            <span className="text-[10px] font-semibold text-primary">Hoje</span>
          </div>
          <div className="mt-1.5 grid grid-cols-4 gap-1.5 text-center">
            <SummaryStat
              icon={<Gauge className="h-3.5 w-3.5" />}
              label="Distância"
              value={`${(summary?.distanceKm ?? 0).toFixed(0)} km`}
            />
            <SummaryStat
              icon={<RouteIcon className="h-3.5 w-3.5" />}
              label="Paradas"
              value={String(validStops.length)}
            />
            <SummaryStat
              icon={<Zap className="h-3.5 w-3.5" />}
              label="Vel. máx"
              value={`${summary?.maxSpeed ?? 0} km/h`}
            />
            <SummaryStat
              icon={<CirclePause className="h-3.5 w-3.5" />}
              label={isMovingNow ? "Agora" : "Parado há"}
              value={parkedLabel}
            />
          </div>
        </div>
      )}

      {featured && (canLock || canUnlock) && (
        <VehicleBlockConfirmDialog
          open={blockDialogOpen}
          onOpenChange={setBlockDialogOpen}
          vehicleName={featured.name}
          vehiclePlate={featured.plate}
          canLock={canLock}
          canUnlock={canUnlock}
          sending={featuredBlock.sending}
          onConfirm={(action) => void featuredBlock.send(action)}
        />
      )}

      {/* Street View do veículo em destaque — pedido 2026-09-01: "vamos
          colocar o street view de cada veículo, conforme selecionar o
          veículo muda o street view". Reaproveita o mesmo StreetViewPanorama
          real (arrastável, fonte GOOGLE) já usado no hero de VehicleDetails
          — troca sozinho porque `featured` já muda ao clicar num avatar do
          carrossel acima. */}
      {featured && (
        <div className="relative mt-2 h-40 w-full overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white shadow-sm">
          <StreetViewPanorama
            lat={featured.lat}
            lng={featured.lng}
            heading={featured.bearing}
            className="h-full w-full"
            fallback={
              <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-slate-900 to-slate-700">
                <img
                  src={iconUrlFor(featured)}
                  alt=""
                  className="h-12 w-12 object-contain opacity-90 drop-shadow-lg"
                />
                <span className="text-[10px] font-medium text-slate-300">
                  Sem imagem de rua disponível aqui
                </span>
              </div>
            }
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-transparent to-transparent p-2 pt-6">
            <div className="text-[10px] font-semibold text-slate-100">Street View</div>
            <div className="truncate text-[9px] text-slate-300">{featured.name}</div>
          </div>
        </div>
      )}

      {/* Histórico do veículo — pedido 2026-10-21: "embaixo do street view
          coloca o histórico do veículo". Últimas viagens reais dos últimos 7
          dias (mesma query de "Parado há", sem chamada nova); tocar abre o
          Centro de Histórico completo ("Meus relatórios"). */}
      {featured && menuAccess.historico && (
        <InicioTripHistory
          trips={lastTripsQuery.data}
          loading={lastTripsQuery.isLoading}
          onOpen={() => onOpenReports(featured.id)}
        />
      )}
    </div>
  );
});

function tripDayLabel(d: Date): string {
  const today = startOfDay(new Date()).getTime();
  const day = startOfDay(d).getTime();
  if (day === today) return "Hoje";
  if (day === today - 86_400_000) return "Ontem";
  return d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
}

function hhmm(d: Date): string {
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function InicioTripHistory({
  trips,
  loading,
  onOpen,
}: {
  trips: Trip[] | undefined;
  loading: boolean;
  onOpen: () => void;
}) {
  const recent = useMemo(
    () =>
      [...(trips ?? [])]
        .filter((t) => t.startTime)
        .sort((a, b) => b.startTime.localeCompare(a.startTime))
        .slice(0, 5),
    [trips],
  );
  return (
    <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-100 p-2.5 dark:border-transparent dark:bg-white">
      <button type="button" onClick={onOpen} className="flex w-full items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-[12px] font-bold text-slate-900">
          <HistoryIcon className="h-3.5 w-3.5 text-primary" />
          Histórico do veículo
        </h2>
        <span className="flex items-center text-[10px] font-semibold text-primary">
          Ver tudo <ChevronRight className="h-3 w-3" />
        </span>
      </button>
      {loading ? (
        <div className="mt-2 space-y-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-11 animate-pulse rounded-xl bg-slate-200" />
          ))}
        </div>
      ) : recent.length === 0 ? (
        <p className="mt-2 py-2 text-center text-[11px] text-slate-500">
          Nenhuma viagem nos últimos 7 dias.
        </p>
      ) : (
        <ul className="mt-1.5 space-y-1.5">
          {recent.map((t) => {
            const start = new Date(t.startTime);
            const end = t.endTime ? new Date(t.endTime) : null;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={onOpen}
                  className="flex w-full items-center gap-2 rounded-xl bg-white p-2 text-left shadow-sm dark:bg-slate-50"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <RouteIcon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-900">
                      {tripDayLabel(start)} · {hhmm(start)}
                      {end && <> → {hhmm(end)}</>}
                    </span>
                    {(t.startAddress || t.endAddress) && (
                      <span className="block truncate text-[10px] text-slate-500">
                        {t.startAddress ?? "—"} → {t.endAddress ?? "—"}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-[11px] font-bold text-slate-900">
                      {t.distanceKm.toFixed(1)} km
                    </span>
                    <span className="flex items-center justify-end gap-0.5 text-[10px] text-slate-500">
                      <ClockIcon className="h-2.5 w-2.5" />
                      {formatShortDuration(t.durationMs)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Frota grande de verdade em produção (5000+ veículos, sobra de carga de
// teste — ver memória "Nginx gzip_proxied fix": "5000 leftover load-test
// devices still in prod DB on purpose"). Antes esta lista renderizava TODOS
// de uma vez, sem filtro nem paginação — 5000 <li> com foto cada um deixava
// a troca pra essa aba visivelmente travada (pedido 2026-09-03: "esta
// lostando todos tem que ter a quebra de linha também lista 10 e quebra a
// linha 1,2,3 etc"), e como o painel agora fica sempre montado (ver
// comentário grande acima do container das abas), a lista inteira também
// reconciliava a cada poll de 15s de useVehicles() mesmo em outra aba —
// paginar em 10 por página resolve as duas coisas de uma vez.
const VEHICLES_PAGE_SIZE = 10;

function matchesVehicleSearch(v: Vehicle, query: string): boolean {
  return (
    v.name.toLowerCase().includes(query) ||
    v.plate.toLowerCase().includes(query) ||
    (v.imei ?? "").toLowerCase().includes(query) ||
    (v.phone ?? "").toLowerCase().includes(query) ||
    (v.driver ?? "").toLowerCase().includes(query)
  );
}

// memo — mesmo motivo de InicioPanel acima: fica sempre montado, e sem isso
// reprocessava busca+paginação+âncoras sobre a frota inteira a cada clique
// em qualquer outro lugar do app. onPick/onEdit vêm de useCallback/setState
// estável em MobileApp.
const VehiclesList = memo(function VehiclesList({
  vehicles,
  onPick,
  onEdit,
}: {
  vehicles: Vehicle[];
  onPick: (id: string) => void;
  onEdit: (vehicle: Vehicle) => void;
}) {
  // Lápis de editar — só pra quem é Administrador (mesma meta-permissão
  // usada em "Informações do dispositivo", ver VehicleDetails).
  // Pedido 2026-09-24: cliente com devices:edit também vê o lápis — o
  // VehicleEditDialog abre só o campo Nome pra ele (ver VehicleRenamePanel).
  const isAdmin = usePermission("administrador");
  const canEditDevices = usePermission("devices", "edit");
  const canEdit = isAdmin || canEditDevices;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Ícone de âncora por linha (pedido 2026-09-04) — mesma busca em lote do
  // mapa desktop (routes/index.tsx#MapPage), nunca useVehicleAnchor por
  // linha (N+1 numa frota de milhares de veículos).
  const { data: geofenceAlertRules } = useGeofenceAlertRules();
  const anchoredDeviceIds = useMemo(() => {
    const ids = new Set<number>();
    for (const r of geofenceAlertRules ?? []) {
      if (r.name?.startsWith(ANCHOR_NAME_PREFIX)) ids.add(r.traccarDeviceId);
    }
    return ids;
  }, [geofenceAlertRules]);

  // Online → Parado → Offline antes de paginar (mesma ordem da lista do
  // mapa no computador, lib/vehicle-status-sort.ts — pedido 2026-10-03).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortVehiclesByStatus(q ? vehicles.filter((v) => matchesVehicleSearch(v, q)) : vehicles);
  }, [vehicles, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / VEHICLES_PAGE_SIZE));
  // Corrige sozinho se a busca reduzir o total de páginas abaixo da página
  // atual (ex.: estava na página 8, digitou algo que só dá 2 páginas) — sem
  // isso a lista ficava em branco até o usuário mexer na paginação de novo.
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => filtered.slice((currentPage - 1) * VEHICLES_PAGE_SIZE, currentPage * VEHICLES_PAGE_SIZE),
    [filtered, currentPage],
  );

  // Foto real cadastrada (mesma fonte/prioridade do card desktop, ver
  // VehicleRow em routes/index.tsx) — só pros 10 da página atual, nunca a
  // frota inteira de uma vez (era o payload que mais pesava nessa tela).
  const { data: vehicleImagesMap } = useVehicleImages(pageItems.map((v) => v.id));

  const handleSearchChange = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  return (
    <div className="flex flex-col">
      {/* paddingTop soma a safe-area do topo, ver InicioPanel acima */}
      <div
        className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Veículos</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {vehicles.length} veículos na frota
        </p>
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 dark:border-slate-700 dark:bg-slate-950">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            type="text"
            inputMode="search"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por placa, nome, IMEI, linha ou motorista"
            className="h-10 flex-1 min-w-0 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100"
          />
          {search && (
            <button
              type="button"
              onClick={() => handleSearchChange("")}
              aria-label="Limpar busca"
              className="shrink-0 text-slate-400"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {filtered.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
          {vehicles.length === 0
            ? "Nenhum veículo cadastrado."
            : "Nenhum veículo encontrado para essa busca."}
        </p>
      )}
      <ul className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-900">
        {pageItems.map((v) => (
          <VehicleListRow
            key={v.id}
            v={v}
            imageUrls={vehicleImagesMap?.[v.id]}
            hasAnchor={anchoredDeviceIds.has(Number(v.id))}
            onPick={() => onPick(v.id)}
            onEdit={canEdit ? () => onEdit(v) : undefined}
          />
        ))}
      </ul>
      {totalPages > 1 && (
        <VehiclesPager page={currentPage} totalPages={totalPages} onChange={setPage} />
      )}
    </div>
  );
});

// Paginador numerado (pedido: "quebra a linha 1,2,3 etc") — janela de no
// máximo 5 números ao redor da página atual, sempre com a 1ª/última visíveis
// (com "…" no meio quando a frota é grande), pra nunca ter que desenhar
// centenas de botões numa frota de milhares de veículos.
function VehiclesPager({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  const windowSize = 5;
  let start = Math.max(1, page - Math.floor(windowSize / 2));
  const end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  const nums = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const pageButtonClass = (active: boolean) =>
    cn(
      "grid h-8 w-8 shrink-0 place-items-center rounded-lg text-sm font-semibold",
      active
        ? "bg-sky-600 text-white"
        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    );

  return (
    <div className="flex items-center justify-center gap-1.5 border-t border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="Página anterior"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600 disabled:opacity-40 dark:bg-slate-800 dark:text-slate-300"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {start > 1 && (
        <>
          <button type="button" onClick={() => onChange(1)} className={pageButtonClass(false)}>
            1
          </button>
          {start > 2 && <span className="px-0.5 text-slate-400">…</span>}
        </>
      )}
      {nums.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={pageButtonClass(n === page)}
        >
          {n}
        </button>
      ))}
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="px-0.5 text-slate-400">…</span>}
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            className={pageButtonClass(false)}
          >
            {totalPages}
          </button>
        </>
      )}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label="Próxima página"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600 disabled:opacity-40 dark:bg-slate-800 dark:text-slate-300"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function VehicleListRow({
  v,
  imageUrls,
  hasAnchor,
  onPick,
  onEdit,
}: {
  v: Vehicle;
  imageUrls: VehicleImageUrls | undefined;
  hasAnchor: boolean;
  onPick: () => void;
  onEdit?: () => void;
}) {
  const photoUrl = imageUrls
    ? resolveGpsDriveUrl(imageUrls["128"] ?? imageUrls.original)
    : undefined;
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setPhotoFailed(false), [photoUrl]);

  return (
    <li className="flex items-center gap-1 pr-2">
      <button
        type="button"
        onClick={onPick}
        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 dark:hover:bg-slate-800 dark:active:bg-slate-800"
      >
        <div className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800">
          {photoUrl && !photoFailed ? (
            <img
              src={photoUrl}
              alt={v.name}
              className="h-full w-full object-cover"
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <img src={iconUrlFor(v)} alt="" className="h-9 w-9 object-contain" />
          )}
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-slate-900"
            style={{ background: statusColorFor(v) }}
          />
          {/* Pedido 2026-09-04: ícone da âncora na lista da frota, pra ver
              se está ativa ou desativada sem abrir o veículo. Mesmo par
              ativo (verde)/inativo (cinza) do mapa desktop. */}
          <span
            className={cn(
              "absolute -top-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full border-2 border-white text-[8px] leading-none dark:border-slate-900",
              hasAnchor ? "bg-emerald-500" : "bg-slate-400 dark:bg-slate-600",
            )}
            title={hasAnchor ? "Âncora ativada" : "Sem âncora"}
            aria-label={hasAnchor ? "Âncora ativada" : "Sem âncora"}
          >
            ⚓
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {v.name}
            </p>
            <LicensePlate plate={v.plate} size="xs" />
          </div>
          <p className="text-xs" style={{ color: statusColorFor(v) }}>
            <span className="font-semibold">{statusText(v)}</span>
            <span className="text-slate-500 dark:text-slate-400">
              {" "}
              · {v.speed} km/h · {v.driver}
            </span>
          </p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Editar ${v.name}`}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-600 active:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:active:bg-slate-700"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

// Ícone da aba "Perfil" na barra inferior — mostra a foto real do usuário em
// vez do emoji genérico 🙂 (pedido 2026-08-31: "no icone do perfil que esta
// emoji tem que ficar a imagem icone do perfil do usuario do cliente").
// Mesmo fallback (emoji) do AvatarEditor enquanto não existe foto nenhuma.
function ProfileTabIcon({ traccarUserId }: { traccarUserId: number | undefined }) {
  const { data: avatarData } = useUserAvatar(traccarUserId);
  const avatarUrl = avatarData?.imageUrls
    ? resolveGpsDriveUrl(avatarData.imageUrls["128"] ?? avatarData.imageUrls.original)
    : undefined;
  if (!avatarUrl) return <span aria-hidden>🙂</span>;
  return (
    <img
      src={avatarUrl}
      alt=""
      className="h-6 w-6 rounded-full object-cover ring-1 ring-white/40"
    />
  );
}

// memo — mesmo motivo dos painéis acima (fica sempre montado). Callbacks
// vêm de useCallback em MobileApp.
const YouPanel = memo(function YouPanel({
  onOpenMeuPerfil,
  onOpenRegistrarAssistencia,
  onOpenMinhasAssistencias,
  onOpenApagarHistorico,
  onOpenCriarUsuario,
  onOpenIndicacoes,
  onOpenVistoria,
  onOpenManutencao,
  darkMode,
  onChangeDarkMode,
}: {
  onOpenMeuPerfil: () => void;
  onOpenRegistrarAssistencia: () => void;
  onOpenMinhasAssistencias: () => void;
  onOpenApagarHistorico: () => void;
  onOpenCriarUsuario: () => void;
  onOpenIndicacoes: () => void;
  onOpenVistoria: () => void;
  onOpenManutencao: () => void;
  darkMode: boolean;
  onChangeDarkMode: (v: boolean) => void;
}) {
  const { user, gpsDriveUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  // "Registrar Assistência"/"Minhas Assistências" só pra quem tem a
  // permissão granular administracao.assistencia (funcionário/admin da
  // empresa de rastreamento). Cliente (accountType "usuario") NUNCA vê
  // "Minhas Assistências" — pedido 2026-09-22 ("quem é cliente não pode
  // ver o botão de minhas assistências"); antes aparecia pro cliente com
  // plano com assistência.
  const canRegisterAssistance = usePermission("administracao", "assistencia");
  const isClientAccount = gpsDriveUser?.accountType === "usuario";
  // Motorista (pedido 2026-10-08): Vistoria do veículo no Perfil, liberada
  // pela permissão vistoria.create. Não apaga histórico (o veículo é da
  // empresa/cliente, não dele) e não vê Assistências.
  const isMotorista = gpsDriveUser?.accountType === "motorista";
  const canInspect = usePermission("vistoria", "create");
  // Sub-usuário criado pelo cliente só vê "Apagar histórico" se o titular
  // liberou (pedido 2026-10-15); titular/admin/funcionário seguem vendo.
  const canDeleteHistory = usePermission("reports", "delete_history");
  const showDeleteHistory = !isMotorista && (!gpsDriveUser?.createdByUserId || canDeleteHistory);
  // Saudação por horário real do aparelho (pedido: "Bom dia, Igor Feijó 👋")
  // — recalculada a cada montagem da aba, não fixa.
  const greeting = useMemo(() => greetingFor(user?.name), [user?.name]);
  const { data: branding } = useAdminBranding();

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate({ to: "/login", replace: true });
    }
  };

  const items: [string, string, (() => void) | undefined][] = [
    ["👤", "Meu perfil", onOpenMeuPerfil],
    ...(canInspect
      ? ([["📋", "Vistoria do veículo", onOpenVistoria]] as [string, string, (() => void) | undefined][])
      : []),
    // Manutenção do veículo (pedido 2026-10-14) — cliente cuida da
    // manutenção e do km dos próprios veículos.
    ...(isClientAccount
      ? ([["🛠️", "Manutenção do veículo", onOpenManutencao]] as [string, string, (() => void) | undefined][])
      : []),
    ...(canRegisterAssistance
      ? ([["🔧", "Registrar Assistência", onOpenRegistrarAssistencia]] as [
          string,
          string,
          (() => void) | undefined,
        ][])
      : []),
    ...(canRegisterAssistance && !isClientAccount && !isMotorista
      ? ([["🛎", "Minhas Assistências", onOpenMinhasAssistencias]] as [
          string,
          string,
          (() => void) | undefined,
        ][])
      : []),
    // Central de Indicações (pedido 2026-09-27) — só cliente; o backend
    // também recusa admin/funcionário (403).
    ...(isClientAccount
      ? ([["🎁", "Indicações", onOpenIndicacoes]] as [string, string, (() => void) | undefined][])
      : []),
    ...(showDeleteHistory
      ? ([["🗑", "Apagar histórico", onOpenApagarHistorico]] as [string, string, (() => void) | undefined][])
      : []),
    // "Criar usuário" (pedido 2026-09-10) — só o TITULAR de uma conta
    // Cliente (accountType "usuario" E createdByUserId nulo) pode ter
    // sub-usuários; o backend já faz este enforcement de verdade (ver
    // SubUsersService#create), esta checagem aqui é só pra não oferecer o
    // botão a quem o backend recusaria. Bug real 2026-09-14: faltava o
    // segundo critério aqui — um sub-usuário criado por um Cliente herda
    // accountType "usuario" do titular (ver comentário em
    // SubUsersService#create), então accountType sozinho deixava o botão
    // visível pro sub-usuário também, mesmo o backend recusando a chamada.
    // iOS: escondido em 2026-10-05 (Apple 5.1.1(v) exige "Excluir conta"
    // quando o app cria conta); liberado de novo a pedido em 2026-10-14
    // ("Criar usuário não aparece pros clientes") — usuário escolheu
    // liberar SEM "Excluir conta", ciente do risco na próxima revisão.
    ...(gpsDriveUser?.accountType === "usuario" && !gpsDriveUser.createdByUserId
      ? ([["➕", "Criar usuário", onOpenCriarUsuario]] as [
          string,
          string,
          (() => void) | undefined,
        ][])
      : []),
    ["🚪", "Sair", handleLogout],
  ];

  return (
    <div
      className="relative flex flex-col items-center px-6 pb-8"
      // paddingTop soma a safe-area do topo, ver InicioPanel acima
      style={{ paddingTop: "calc(2.5rem + env(safe-area-inset-top))" }}
    >
      {/* Ícone de tema claro/escuro — antes só existia dentro da tela
          "Preferências" (removida a pedido 2026-08-31 junto com
          "Notificações"); precisava de um lugar visível pra continuar
          acessível ("cria um icone em algum lugar pra trocar a cor do fundo
          pra preto ou branco"). Posição soma a safe-area do topo pelo mesmo
          motivo do container acima — senão fica sob a status bar junto com
          o resto. */}
      <button
        type="button"
        onClick={() => onChangeDarkMode(!darkMode)}
        className="absolute right-4 grid h-10 w-10 place-items-center rounded-full bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200"
        style={{ top: "calc(1rem + env(safe-area-inset-top))" }}
        aria-label={darkMode ? "Mudar para tema claro" : "Mudar para tema escuro"}
      >
        {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>
      <AvatarEditor traccarUserId={user?.id} />
      <h2 className="mt-4 text-lg font-bold text-slate-900 dark:text-slate-100">
        {greeting.text} <span aria-hidden>{greeting.emoji}</span>
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400">{user?.email ?? ""}</p>
      {/* Emergência (movido da tela Início a pedido 2026-09-22) — só aparece
          quando a empresa cadastrou um telefone de verdade em Administração >
          Identidade Visual (branding.emergencyPhone); sem número, sem card
          (nunca um 0800 fixo/fake). */}
      {branding?.emergencyPhone && (
        <a
          href={`tel:${branding.emergencyPhone.replace(/\D/g, "")}`}
          className="mt-6 flex w-full items-center justify-between gap-3 rounded-xl bg-rose-600 px-4 py-3 text-white shadow-sm active:bg-rose-700"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <div>
              <div className="text-sm font-bold">Emergência</div>
              <div className="text-[11px] text-rose-100">Toque para acionar</div>
            </div>
          </div>
          <div className="text-right text-sm font-semibold">{branding.emergencyPhone}</div>
        </a>
      )}
      <div className={cn("w-full space-y-2", branding?.emergencyPhone ? "mt-2" : "mt-6")}>
        {items.map(([e, label, onClick]) => (
          <button
            key={label}
            type="button"
            onClick={onClick}
            disabled={label === "Sair" && loggingOut}
            className="flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-sm font-medium text-slate-800 shadow-sm disabled:opacity-60 dark:bg-slate-900 dark:text-slate-100"
          >
            <span className="text-lg">{e}</span>
            <span className="flex-1 text-left">
              {label === "Sair" && loggingOut ? "Saindo..." : label}
            </span>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        ))}
      </div>
    </div>
  );
});

const financeCurrency = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

// Tela Financeiro/Faturas do app do cliente (pedido 2026-08-28) — dois
// estados possíveis, decididos INTEIRAMENTE por useMyBillingStatus (nunca
// um estado local/otimista): "suspended" mostra o aviso de acesso
// suspenso (+ a fatura vencida real, quando existe uma por trás — ver
// FinanceiroSuspenso); caso contrário mostra "Tudo em dia" + o histórico
// real de pagamentos (GET /users/:id/invoices, mesmo endpoint da aba
// FATURAMENTO administrativa). `suspended` sozinho (sem overdueInvoice) é
// o caso de suspensão MANUAL (coluna Situação, tela Usuários) — não
// depende de nenhuma fatura vencida existir. gpsDriveUser?.id nunca é
// undefined aqui na prática (a tela só existe pra quem já está logado),
// mas useInvoices já lida com undefined (enabled:false) por segurança.
function FinanceiroPanel() {
  const { gpsDriveUser } = useAuth();
  const { data: status, isLoading: loadingStatus, refetch } = useMyBillingStatus();
  const { data: invoicesPage, isLoading: loadingInvoices } = useInvoices(gpsDriveUser?.id, 0);

  // Espera as duas queries: se só a de status resolver primeiro, a lista de
  // faturas ainda undefined vira "[]" e a tela mostra "Tudo em dia" por um
  // instante antes de piscar para "Fatura em aberto" quando a segunda query
  // chega (bug relatado 2026-08-31).
  if (loadingStatus || loadingInvoices) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center text-sm text-slate-500 dark:text-slate-400">
        Carregando…
      </div>
    );
  }

  if (status?.suspended) {
    return (
      <FinanceiroSuspenso
        userId={gpsDriveUser?.id}
        invoice={status.overdueInvoice}
        onCheckPayment={() => refetch()}
      />
    );
  }

  const invoices = invoicesPage?.invoices ?? [];
  const paidInvoices = invoices
    .filter((i) => i.status === "paga")
    .sort((a, b) => (b.paymentDate ?? b.dueDate).localeCompare(a.paymentDate ?? a.dueDate));
  // "Em aberto" cobre pendente E vencida — mesmo que a fatura vencida ainda
  // não tenha alcançado o prazo de suspensão (senão o cliente só descobre
  // que deve quando o app já bloqueou tudo, bug relatado 2026-08-28: "tem
  // que aparecer as faturas em aberto também").
  const openInvoices = invoices
    .filter((i) => i.status === "pendente" || i.status === "vencida")
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  return (
    <FinanceiroEmDia
      userId={gpsDriveUser?.id}
      openInvoices={openInvoices}
      paidInvoices={paidInvoices}
      loading={loadingInvoices}
    />
  );
}

function FinanceiroEmDia({
  userId,
  openInvoices,
  paidInvoices,
  loading,
}: {
  userId: string | undefined;
  openInvoices: Invoice[];
  paidInvoices: Invoice[];
  loading: boolean;
}) {
  // Pedido 2026-08-29: clicar numa fatura em aberto tem que abrir uma tela
  // própria com as informações da fatura + PIX (QR/copia-e-cola), não só um
  // dialog pequeno em cima da lista — ver InvoiceDetailScreen abaixo.
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  return (
    <div
      className="px-4 pb-8"
      // paddingTop soma a safe-area do topo, ver InicioPanel acima
      style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}
    >
      <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Financeiro</h1>

      <div className="mt-4 overflow-hidden rounded-2xl bg-slate-900 p-5 text-white shadow-lg">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
          Situação
        </div>
        {openInvoices.length === 0 ? (
          <>
            <div className="mt-1 text-2xl font-bold">Tudo em dia</div>
            <p className="mt-1.5 text-sm text-slate-300">
              Você não possui faturas em aberto no momento.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5" /> Sem pendências
            </div>
          </>
        ) : (
          <>
            <div className="mt-1 text-2xl font-bold">Fatura em aberto</div>
            <p className="mt-1.5 text-sm text-slate-300">
              Você tem {openInvoices.length} {openInvoices.length === 1 ? "fatura" : "faturas"}{" "}
              aguardando pagamento.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" /> Pagamento pendente
            </div>
          </>
        )}
        <div className="mt-4 flex items-center gap-1.5 border-t border-white/10 pt-3 text-[11px] text-slate-400">
          <Lock className="h-3 w-3" /> Cobrança processada com segurança pela Asaas.
        </div>
      </div>

      {openInvoices.length > 0 && (
        <>
          <h2 className="mt-6 mb-2 text-sm font-bold text-slate-900 dark:text-slate-100">
            Faturas em aberto
          </h2>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-sm dark:divide-slate-800 dark:bg-slate-900">
            {openInvoices.map((inv) => {
              const overdue = inv.status === "vencida";
              return (
                <li key={inv.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedInvoice(inv)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-slate-50 dark:active:bg-slate-800"
                  >
                    <span
                      className={cn(
                        "grid h-8 w-8 shrink-0 place-items-center rounded-full",
                        overdue ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600",
                      )}
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium capitalize text-slate-800 dark:text-slate-100">
                        {formatMonthYear(inv.dueDate)}
                      </span>
                      <span
                        className={cn(
                          "block text-xs",
                          overdue
                            ? "text-rose-600 dark:text-rose-400"
                            : "text-slate-500 dark:text-slate-400",
                        )}
                      >
                        {overdue ? "Vencida em" : "Vence em"} {formatDueDate(inv.dueDate)}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-bold text-slate-900 dark:text-slate-100">
                      {financeCurrency.format(inv.value)}
                    </span>
                    {inv.billingType === "PIX" ? (
                      <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">
                        <QrCode className="h-3 w-3" /> PIX
                      </span>
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <h2 className="mt-6 mb-2 text-sm font-bold text-slate-900 dark:text-slate-100">
        Histórico de pagamentos
      </h2>
      {loading ? (
        <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
          Carregando…
        </div>
      ) : paidInvoices.length === 0 ? (
        <div className="rounded-xl bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
          Nenhum pagamento registrado ainda.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-sm dark:divide-slate-800 dark:bg-slate-900">
          {paidInvoices.map((inv) => (
            <li key={inv.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-slate-50 dark:active:bg-slate-800"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium capitalize text-slate-800 dark:text-slate-100">
                  {formatMonthYear(inv.paymentDate ?? inv.dueDate)}
                </span>
                <span className="shrink-0 text-sm font-bold text-slate-900 dark:text-slate-100">
                  {financeCurrency.format(inv.value)}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedInvoice && userId && (
        <InvoiceDetailScreen
          userId={userId}
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  );
}

function FinanceiroSuspenso({
  userId,
  invoice,
  onCheckPayment,
}: {
  userId: string | undefined;
  // null = suspensão MANUAL (coluna Situação, tela Usuários), sem
  // nenhuma fatura vencida real por trás — mostra só o aviso genérico,
  // sem o card de fatura/PIX abaixo (ver FinanceiroPanel).
  invoice: Invoice | null;
  onCheckPayment: () => Promise<{ data?: BillingStatus }>;
}) {
  const [checking, setChecking] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const handleCheckPayment = async () => {
    setChecking(true);
    try {
      const result = await onCheckPayment();
      // O botão só INFORMA que o app deve checar de novo agora — quem
      // decide se liberou é sempre o status real devolvido pelo backend
      // (ver FinanceiroPanel/useMyBillingStatus), nunca o clique em si.
      const stillSuspended = result.data?.suspended ?? true;
      if (stillSuspended) {
        toast.info("Pagamento ainda não confirmado", {
          description: "Pode levar alguns minutos após o pagamento para o Asaas confirmar.",
        });
      } else {
        toast.success("Pagamento confirmado — acesso liberado!");
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="px-4 pb-8"
      // paddingTop soma a safe-area do topo, ver InicioPanel acima
      style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}
    >
      <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Financeiro</h1>

      <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
        <div className="flex items-center gap-2 text-sm font-bold text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4" /> Acesso suspenso
        </div>
        <p className="mt-1.5 text-[13px] leading-snug text-amber-900 dark:text-amber-200">
          Seu acesso está suspenso por pendências financeiras. Caso já tenha realizado o pagamento,
          aguarde a confirmação ou reative abaixo.
        </p>
        <button
          type="button"
          onClick={handleCheckPayment}
          disabled={checking}
          className="mt-3 w-full rounded-xl bg-amber-600 py-3 text-sm font-bold text-white shadow-sm active:bg-amber-700 disabled:opacity-60"
        >
          {checking ? "Verificando…" : "Já realizei o pagamento"}
        </button>
      </div>

      {invoice && (
        <div className="mt-4 overflow-hidden rounded-2xl bg-slate-900 p-5 text-white shadow-lg">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
            Fatura • {formatMonthYear(invoice.dueDate)}
          </div>
          <div className="mt-1 text-3xl font-bold">{financeCurrency.format(invoice.value)}</div>
          {invoice.description && (
            <p className="mt-1 text-sm text-slate-300">{invoice.description}</p>
          )}
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-3 py-1 text-xs font-semibold text-rose-400">
            <AlertTriangle className="h-3.5 w-3.5" /> Em atraso • venceu em{" "}
            {formatDueDate(invoice.dueDate)}
          </div>

          <button
            type="button"
            onClick={() => setDetailOpen(true)}
            className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-white py-3 text-sm font-bold text-slate-900 shadow-sm active:bg-slate-100"
          >
            {invoice.billingType === "PIX" ? (
              <>
                <QrCode className="h-4 w-4" /> Pagar com PIX
              </>
            ) : (
              "Ver fatura e pagar"
            )}
          </button>

          <div className="mt-4 flex items-center gap-1.5 border-t border-white/10 pt-3 text-[11px] text-slate-400">
            <Lock className="h-3 w-3" /> Pagamento processado com segurança pela Asaas.
          </div>
        </div>
      )}

      {detailOpen && userId && invoice && (
        <InvoiceDetailScreen
          userId={userId}
          invoice={invoice}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

// Botão "Pagar com PIX" (Faturas do app do cliente, pedido 2026-08-28) —
// Tela própria (não mais um dialog pequeno em cima da lista) aberta ao
// clicar numa fatura em aberto — FinanceiroEmDia e FinanceiroSuspenso (aba
// Faturas do app do cliente). Pedido 2026-08-29: "quando o cliente clicar
// em cima da fatura pendente tem que abrir outra tela com o código pix
// copia e cola e qr code, mostrar as informações da fatura" — mostra
// valor/vencimento/descrição sempre, e o QR/copia-e-cola (gerado no Asaas a
// cada abertura, nunca cacheado — ver usePixQrCode) só quando a fatura é
// PIX; para as demais formas de pagamento, mostra o link real do Asaas.
function InvoiceDetailScreen({
  userId,
  invoice,
  onClose,
}: {
  userId: string;
  invoice: Invoice;
  onClose: () => void;
}) {
  const isPix = invoice.billingType === "PIX";
  const overdue = invoice.status === "vencida";
  const paymentUrl = invoice.invoiceUrl ?? invoice.bankSlipUrl;
  const pixQrCode = usePixQrCode();

  useEffect(() => {
    if (isPix) pixQrCode.mutate({ userId, invoiceId: invoice.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id]);

  const handleCopy = async () => {
    const payload = pixQrCode.data?.payload;
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Código PIX copiado");
    } catch {
      toast.error("Não foi possível copiar o código");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* paddingTop soma a safe-area do topo, ver InicioPanel acima */}
      <div
        className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100">Fatura</div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="overflow-hidden rounded-2xl bg-slate-900 p-5 text-white shadow-lg">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
            Fatura • {formatMonthYear(invoice.dueDate)}
          </div>
          <div className="mt-1 text-3xl font-bold">{financeCurrency.format(invoice.value)}</div>
          {invoice.description && (
            <p className="mt-1 text-sm text-slate-300">{invoice.description}</p>
          )}
          <div
            className={cn(
              "mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
              overdue ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400",
            )}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {overdue ? "Vencida em" : "Vence em"} {formatDueDate(invoice.dueDate)}
          </div>
        </div>

        {isPix ? (
          <div className="mt-4 rounded-2xl bg-white p-5 text-center shadow-sm dark:bg-slate-900">
            <h2 className="flex items-center justify-center gap-1.5 text-sm font-bold text-slate-900 dark:text-slate-100">
              <QrCode className="h-4 w-4" /> Pagar com PIX
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Escaneie o QR code com o app do seu banco ou copie o código abaixo.
            </p>

            {pixQrCode.isPending && (
              <div className="py-10 text-sm text-slate-500 dark:text-slate-400">
                Gerando QR code…
              </div>
            )}

            {pixQrCode.isError && (
              <div className="py-6 text-sm text-rose-600 dark:text-rose-400">
                Não foi possível gerar o QR code agora.
                <button
                  type="button"
                  onClick={() => pixQrCode.mutate({ userId, invoiceId: invoice.id })}
                  className="mt-2 block w-full text-xs font-semibold text-slate-900 underline dark:text-slate-100"
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {pixQrCode.data && (
              <div className="mt-3 flex flex-col items-center gap-3">
                <img
                  src={`data:image/png;base64,${pixQrCode.data.encodedImage}`}
                  alt="QR code PIX"
                  className="h-56 w-56 rounded-lg border border-slate-200 dark:border-slate-700"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white active:bg-slate-800"
                >
                  <Copy className="h-3.5 w-3.5" /> Copiar código PIX
                </button>
              </div>
            )}
          </div>
        ) : (
          paymentUrl && (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 flex items-center justify-center rounded-xl bg-slate-900 py-3 text-sm font-bold text-white shadow-sm active:bg-slate-800"
            >
              Abrir e pagar fatura
            </a>
          )
        )}

        <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
          <Lock className="h-3 w-3" /> Pagamento processado com segurança pela Asaas.
        </div>
      </div>
    </div>
  );
}

function formatMonthYear(dateStr: string): string {
  return format(new Date(`${dateStr}T00:00:00`), "MMMM yyyy", { locale: ptBR });
}

function formatDueDate(dateStr: string): string {
  return format(new Date(`${dateStr}T00:00:00`), "dd/MM/yyyy");
}

// Cabeçalho padrão dos overlays de tela cheia do app (X + título) —
// reaproveitado por MeuPerfilScreen, mesmo visual do header de
// VehicleDetails/InvoiceDetailScreen.
function ScreenHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div
      className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 pb-3 dark:border-slate-800 dark:bg-slate-900"
      // paddingTop soma a safe-area do topo, ver InicioPanel acima
      style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
    >
      <button
        type="button"
        onClick={onClose}
        className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        aria-label="Fechar"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100">{title}</div>
    </div>
  );
}

// Tela cheia de Faturas, aberta pelo atalho "Minhas faturas" do Início
// (pedido 2026-09-01: Faturas deixou de ser aba própria). Reaproveita o
// MESMO FinanceiroPanel de sempre (que já decide sozinho, via
// useMyBillingStatus, entre "tudo em dia" e "suspenso") — só sem um
// ScreenHeader próprio porque o painel já tem seu título "Financeiro"
// embutido (ver FinanceiroEmDia/FinanceiroSuspenso), mesmo padrão de botão
// flutuante no canto já usado em YouPanel pro toggle de tema.
function FaturasScreen({ onClose }: { onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-40 flex flex-col overflow-y-auto bg-slate-50 dark:bg-slate-950">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200"
        // top soma a safe-area do topo, ver InicioPanel acima
        style={{ top: "calc(1rem + env(safe-area-inset-top))" }}
        aria-label="Fechar"
      >
        <X className="h-5 w-5" />
      </button>
      <FinanceiroPanel />
    </div>
  );
}

// "Meu perfil" (aba Perfil, pedido 2026-08-30) — foto + troca da própria
// senha de entrada no app. A troca de senha reusa exatamente o padrão do
// dialog admin "Editar Usuário" (useUser/useUpdateUser, PUT com o objeto
// COMPLETO do Traccar — ver routes/users.tsx), só que confirmando a
// identidade primeiro com um login real usando a senha atual (nunca troca a
// senha sem confirmar que quem está pedindo é o próprio dono dela).
export function MeuPerfilScreen({ onClose }: { onClose: () => void }) {
  const { user, gpsDriveUser } = useAuth();
  const { data: fullUser } = useUser(user?.id);
  const updateUser = useUpdateUser();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confNovaSenha, setConfNovaSenha] = useState("");
  const [showAtual, setShowAtual] = useState(false);
  const [showNova, setShowNova] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user) return;
    if (!senhaAtual || !novaSenha) {
      toast.error("Preencha a senha atual e a nova senha");
      return;
    }
    const policyError = passwordPolicyError(gpsDriveUser?.accountType, novaSenha);
    if (policyError) {
      toast.error("Senha fraca", { description: policyError });
      return;
    }
    if (novaSenha !== confNovaSenha) {
      toast.error("As senhas não coincidem", {
        description: "Confira a confirmação da nova senha.",
      });
      return;
    }
    if (!fullUser) {
      toast.error("Não foi possível carregar seus dados agora. Tente novamente.");
      return;
    }
    setSaving(true);
    try {
      // Confirma que quem está pedindo a troca sabe a senha ATUAL — a
      // própria API de login já faz essa verificação real contra o Traccar.
      await authService.login(user.email, senhaAtual);
    } catch {
      toast.error("Senha atual incorreta");
      setSaving(false);
      return;
    }
    try {
      await updateUser.mutateAsync({ id: user.id, data: { ...fullUser, password: novaSenha } });
      toast.success("Senha alterada com sucesso");
      setSenhaAtual("");
      setNovaSenha("");
      setConfNovaSenha("");
    } catch (err) {
      toast.error("Não foi possível trocar a senha", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950">
      <ScreenHeader title="Meu perfil" onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="flex flex-col items-center py-2">
          <AvatarEditor traccarUserId={user?.id} size={96} />
          <p className="mt-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {user?.name}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
        </div>

        <div className="mt-4 rounded-xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            Trocar senha de entrada
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Essa senha é só sua — ninguém mais tem acesso a ela.
          </p>

          <PasswordField
            label="Senha atual"
            value={senhaAtual}
            onChange={setSenhaAtual}
            show={showAtual}
            onToggleShow={() => setShowAtual((s) => !s)}
          />
          <PasswordField
            label="Nova senha"
            value={novaSenha}
            onChange={setNovaSenha}
            show={showNova}
            onToggleShow={() => setShowNova((s) => !s)}
          />
          <PasswordField
            label="Confirmar nova senha"
            value={confNovaSenha}
            onChange={setConfNovaSenha}
            show={showNova}
            onToggleShow={() => setShowNova((s) => !s)}
          />

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="mt-4 w-full rounded-xl bg-sky-600 py-3 text-sm font-bold text-white shadow-sm active:bg-sky-700 disabled:opacity-60"
          >
            {saving ? "Salvando…" : "Salvar nova senha"}
          </button>
        </div>
      </div>
    </div>
  );
}

const BILLING_SUMMARY_META: Record<ClientBillingStatus, { label: string; className: string }> = {
  em_dia: {
    label: "Em dia",
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  atraso: {
    label: "Em atraso",
    className:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300",
  },
  sem_pendencias: {
    label: "Sem pendências",
    className:
      "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300",
  },
  nao_sincronizado: {
    label: "Sem integração de cobrança",
    className:
      "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400",
  },
};

type ClientBillingStatus = "em_dia" | "atraso" | "sem_pendencias" | "nao_sincronizado";

const currencyFormatterBRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

// "Registrar Assistência" (aba Perfil, permissão administracao.assistencia,
// pedido 2026-09-01) — admin/funcionário escolhe o cliente (busca por
// nome), vê a situação financeira dele, escolhe o tipo de serviço e qual
// veículo do cliente solicitou. Bloqueio de verdade (não mais um simples
// aviso, pedido 2026-09-11) se este mesmo serviço, pra este (cliente,
// veículo), já atingiu o limite anual ou não passou o intervalo mínimo
// configurados em Administração → Configurar Assistência — ver useServiceUsage
// abaixo. Data/hora é sempre gerada pelo servidor no POST — não existe
// campo pra escolher, ver AssistanceController#createRequest no backend.
// Mensagem humana pro bloqueio real de um serviço (pedido 2026-09-11:
// "ao tentar solicitar antes do prazo, informar a data em que estará
// disponível novamente") — mesmo texto usado no aviso inline do form e
// no toast de erro se o usuário ainda assim tentar enviar.
function usageBlockedMessage(usage: ServiceUsage): string {
  const nextDate = usage.nextAvailableAt
    ? format(new Date(usage.nextAvailableAt), "dd/MM/yyyy")
    : null;
  if (usage.blockedReason === "annual_limit") {
    return `Limite anual atingido (${usage.usageCount} de ${usage.annualLimit} utilizações). Disponível novamente em ${nextDate}.`;
  }
  return `Intervalo mínimo de ${usage.minIntervalDays} dias ainda não passou. Disponível novamente em ${nextDate}.`;
}

// Igual à web (pedido 2026-10-16): cliente → serviço → veículo com a
// etiqueta de assistência → Ponto B quando é guincho; ao abrir um guincho,
// segue direto pro acionamento (recomendados, WhatsApp, acompanhamento).
function RegistrarAssistenciaScreen({ onClose }: { onClose: () => void }) {
  const [dispatchFor, setDispatchFor] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950">
      <ScreenHeader title="Registrar Assistência" onClose={onClose} />
      <div className="asst-root flex-1 overflow-y-auto bg-background px-4 py-4" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}>
        <AssistanceNewRequestForm
          key={formKey}
          onCreated={(id, isTow) => {
            setFormKey((k) => k + 1);
            if (isTow) setDispatchFor(id);
            else toast.success("Assistência registrada");
          }}
        />
      </div>
      {dispatchFor && <AssistanceDispatchPanel requestId={dispatchFor} onClose={() => setDispatchFor(null)} />}
    </div>
  );
}

// "Minhas Assistências" (aba Perfil, pedido 2026-09-01) — histórico do
// próprio cliente logado, self-scoped (GET /assistance/me/requests, sem
// permissão nenhuma), mesmo formato de lista de EventosPanel.
function MinhasAssistenciasScreen({ onClose }: { onClose: () => void }) {
  const { data: requests = [], isLoading } = useMyAssistanceRequests();
  const sorted = useMemo(
    () => [...requests].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt)),
    [requests],
  );

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950">
      <ScreenHeader title="Minhas Assistências" onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-20 w-full animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="rounded-xl bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
            Nenhuma assistência solicitada ainda.
          </div>
        ) : (
          <ul className="space-y-2">
            {sorted.map((req: AssistanceRequest) => (
              <li key={req.id} className="rounded-xl bg-white p-3 shadow-sm dark:bg-slate-900">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {req.serviceTypeName}
                  </span>
                  <span className="whitespace-nowrap text-[11px] text-slate-400 dark:text-slate-500">
                    {format(new Date(req.requestedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {req.vehicleName}
                </p>
                {req.reason && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">"{req.reason}"</p>
                )}
                <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                  Registrado por {req.requestedByName}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggleShow,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
}) {
  return (
    <label className="mt-3 block">
      <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">{label}</span>
      <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-white px-3 dark:border-slate-700 dark:bg-slate-950">
        <input
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 flex-1 bg-transparent text-sm text-slate-900 outline-none dark:text-slate-100"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="text-slate-400"
          aria-label="Mostrar/ocultar senha"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </label>
  );
}

// Eventos (pedido 2026-08-30) — contadores da frota (Total/Online/Parado/
// Offline, direto de useVehicles) + feed cronológico real de eventos
// (reportsService.allEvents, ver hooks/queries#useRecentEvents). Traccar não
// tem um evento "bloqueado"/"desbloqueado" dedicado (só a confirmação
// genérica commandResult/queuedCommandSent) — por isso esses dois tipos
// aparecem com o rótulo genérico já usado no relatório desktop de Comandos,
// nunca um texto de bloqueio/desbloqueio inventado.
// memo — mesmo motivo dos painéis acima (fica sempre montado).
const EventosPanel = memo(function EventosPanel({
  vehicles,
  active,
}: {
  vehicles: Vehicle[];
  active: boolean;
}) {
  const vehicleIds = useMemo(() => vehicles.map((v) => v.id), [vehicles]);
  const vehicleById = useMemo(() => new Map(vehicles.map((v) => [v.id, v])), [vehicles]);
  // `active` (aba realmente aberta) — pedido 2026-09-08 "app lento": esse
  // painel fica sempre montado (ver comentário acima sobre não desmontar
  // abas), então sem essa flag o poll de 30s da frota inteira rodava pra
  // sempre em segundo plano mesmo com o cliente noutra tela. Ver
  // useRecentEvents em hooks/queries/index.ts.
  const { data: events = [], isLoading, isError } = useRecentEvents(vehicleIds, 72, active);

  const total = vehicles.length;
  const online = vehicles.filter((v) => v.status === "online").length;
  const parado = vehicles.filter((v) => v.status === "idle").length;
  const offline = vehicles.filter((v) => v.status === "offline").length;

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => b.eventTime.localeCompare(a.eventTime)),
    [events],
  );

  return (
    <div
      className="px-4 pb-8"
      // paddingTop soma a safe-area do topo, ver InicioPanel acima
      style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}
    >
      <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Eventos</h1>

      <div className="mt-4 grid grid-cols-4 gap-2">
        <EventCounter
          label="Total"
          value={total}
          icon={<Activity className="h-4 w-4" />}
          tone="slate"
        />
        <EventCounter
          label="Online"
          value={online}
          icon={<Signal className="h-4 w-4" />}
          tone="emerald"
        />
        <EventCounter
          label="Parado"
          value={parado}
          icon={<CircleDot className="h-4 w-4" />}
          tone="amber"
        />
        <EventCounter
          label="Offline"
          value={offline}
          icon={<SignalZero className="h-4 w-4" />}
          tone="rose"
        />
      </div>

      <h2 className="mt-6 mb-2 text-sm font-bold text-slate-900 dark:text-slate-100">
        Eventos recentes
      </h2>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-16 w-full animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Não foi possível carregar os eventos agora. Tente novamente em instantes.
        </div>
      ) : sortedEvents.length === 0 ? (
        <div className="rounded-xl bg-white px-4 py-6 text-center text-sm text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
          Nenhum evento nas últimas 72 horas.
        </div>
      ) : (
        <ul className="space-y-2">
          {sortedEvents.map((ev) => {
            const meta = ALERT_TYPE_META[ev.type] ?? {
              icon: Bell,
              label: ev.type,
              tone: "slate" as const,
            };
            const vehicle = vehicleById.get(ev.vehicleId);
            const speed = ev.attributes?.speed;
            const speedLimit = ev.attributes?.speedLimit;
            const showSpeed =
              ev.type === "deviceOverspeed" &&
              typeof speed === "number" &&
              typeof speedLimit === "number";
            return (
              <li
                key={ev.id}
                className="flex items-start gap-3 rounded-xl bg-white px-3 py-2.5 shadow-sm dark:bg-slate-900"
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
                    ALERT_TONE_CLASSES[meta.tone],
                  )}
                >
                  <meta.icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {meta.label}
                    </span>
                    {vehicle && (
                      <>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {vehicle.name}
                        </span>
                        <LicensePlate plate={vehicle.plate} size="xs" />
                      </>
                    )}
                  </div>
                  {showSpeed && (
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      Registrado a {Math.round(speed as number)} km/h — limite{" "}
                      {Math.round(speedLimit as number)} km/h
                    </p>
                  )}
                  <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                    {format(new Date(ev.eventTime), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

function EventCounter({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "amber" | "rose";
}) {
  const toneClasses: Record<typeof tone, string> = {
    slate: "text-slate-600 dark:text-slate-300",
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    rose: "text-rose-600 dark:text-rose-400",
  };
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl bg-white py-3 shadow-sm dark:bg-slate-900">
      <span className={toneClasses[tone]}>{icon}</span>
      <span className="text-lg font-bold text-slate-900 dark:text-slate-100">{value}</span>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </div>
  );
}

function TabItem({
  emoji,
  icon,
  label,
  active,
  onClick,
  dot,
  disabled,
}: {
  emoji: string;
  // Substitui o emoji por um nó custom (usado pela aba Perfil pra mostrar a
  // foto real do usuário em vez de um emoji genérico — pedido 2026-08-31).
  icon?: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  dot?: boolean;
  // Acesso suspenso (ver goToTab em MobileApp) — nunca impede o clique de
  // verdade (o próprio goToTab já redireciona pra Faturas com aviso), só
  // sinaliza visualmente que a aba está bloqueada antes mesmo de tocar.
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-disabled={disabled}
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-0.5",
        disabled ? "text-slate-600" : active ? "text-white" : "text-slate-400",
      )}
    >
      <div
        className={cn(
          "relative grid h-7 w-9 place-items-center rounded-md text-base",
          active && !disabled ? "bg-sky-500/20" : "bg-transparent",
          disabled && "opacity-40",
        )}
      >
        {icon ?? <span aria-hidden>{emoji}</span>}
        {dot && <span className="absolute -top-0.5 right-1 h-2 w-2 rounded-full bg-rose-500" />}
      </div>
      <span className="text-[9.5px] font-medium">{label}</span>
    </button>
  );
}
